import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Location } from '@angular/common';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalService } from '@purespectrum1/ui/modal';

import {
  EMPTY,
  forkJoin,
  from,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  pairwise,
  share,
  shareReplay,
  skip,
  switchMap,
  take,
  tap,
  toArray,
} from 'rxjs/operators';
import { cloneDeep } from 'lodash';

import {
  APPLICABLE_QUALIFICATION_FOR_PURE_PRICE,
  Constants,
  GENDER_QUALIFICATION_CODE,
  PURE_PRICE_COUNTRIES,
} from '../shared/constants/create-survey.constants';

import { CreateSurveyService, MultiCountryData } from './create-survey.service';
import { EditSurveyService } from './edit-survey.service';

import { DraftSurveyConfirmationModalComponent } from './draft-survey-confirmation-modal/draft-survey-confirmation-modal.component';
import { QuotaNestingModalComponent } from './quota-nesting/quota-nesting-modal.component';
import { SurveyPayloadService } from './survey-payload.service';
import { QuotaPurePrice } from './pure-price-window/pure-price.interface';
import { notifyMessage } from '../shared/constants/notify-message';
import { QUALIFICATIONS_CATEGORIES } from '../shared/constants/qualification-categories';
import {
  LOCATION_QUALIFICATION_CODES,
  QUALIFICATION_CODES,
} from '../shared/constants/qualification-code';
import { STEPPER_BUTTON_STATUS } from './constants/stepper-button-status';
import { SURVEY_STATUS } from '../shared/constants/survey-status';
import { TRAFFIC_CHANNEL } from './constants/traffic-channels-constants';
import { CURRENCY_CODE } from '../shared/constants/currency-units';
import {
  AdvanceTargetingQualification,
  QualificationPayload,
  CombinedQualification,
  Flexible,
  UpdateAdvanceTargetingCoreQualification,
  Qualification,
  RangeOption,
  QualificationData,
  AttributeQualification,
} from '../shared/interfaces/qualification.interface';
import {
  MetaDataToPurePrice,
  Survey,
} from '../shared/interfaces/black-box.interface';
import {
  CountryInterface,
  CategoriesInterface,
  LanguageInterface,
  Tab,
  CurrencyExchangeRate,
  CurrencyExchangeRateResponse,
  SurveyNavigationState,
  SelectedCountryInterface,
} from '../shared/interfaces/create-surveys.interface';
import {
  Criteria,
  NestedQuota,
  QuotaPayload,
  QuotaResponse,
  SelectedQuota,
} from '../shared/interfaces/quota.interface';
import { UnicornQuotas } from '../shared/interfaces/unicorn.interface';
import {
  CPIData,
  SurveyPayload,
  SurveyTopBarData,
  UpdateSupplierPayloadInterface,
} from '../shared/interfaces/survey-meta-data.interface';
import { SurveyMetaDataService } from '../shared/services/survey-meta-data/survey-meta-data.service';
import { User } from '../shared/interfaces/user.interface';
import { ConfirmationModalComponent } from '../shared/ui/confirmation-modal/confirmation-modal.component';
import { Constants as QuotasConstants } from './constants/quotas';
import { CommonService } from '../shared/services/common-service/common-service';
import { BuyerApiService } from '../shared/services/buyer-api/survey.service';
import {
  BuyerSurveyResponse,
  Datum,
  GetSupplierBlendInterface,
  Locale,
  Quotas,
  SupplierResponseInterface,
  SurveyRequest,
  Qualification as SurveyQualification,
  McSurveyStats,
} from '../shared/interfaces/survey.interface';
import { QualificationsService } from './services/qualification/qualifications.service';
import { AuthService } from '../shared/services/auth/auth.service';
import { FooterComponent } from './layout/footer/footer.component';
import { FeasibilityDataService as FeasibilityComponentService } from './services/feasibility/feasibility-data.service';
import { SharedDataService } from '../shared/services/shared-data-service/shared-data-service';
import {
  IndividualConfig,
  ToasterService,
} from '@purespectrum1/ui/toaster-service';
import { AuthState } from '../shared/interfaces/auth.interface';
import { MarginService } from './services/create-survey-services/margin-service';
import {
  Template,
  TemplateInfo,
  TemplateListResponse,
  TemplateResponse,
  TemplateSupplierAllocation,
} from './layout/template-modal/template-modal.interface';
import { TemplateService } from '../shared/services/template/template.service';
import { deepCopy } from './utils/deep-copy';
import { MarketplaceService } from './marketplace.service';
import { FlexibilityPricesParams } from '../shared/interfaces/supplier-api.interface';
import { TreeDropdownOption } from './shared/interfaces/tree-dropdown-interface';
import { Confirmation } from './layout/header/cpi/cpi-override-confirmation/types/confirmation';
import { LayoutConstants } from './layout/layout-constants';
import { MultiCountryDraftService } from './services/multi-country-draft/multi-country-draft.service';
import { BuyerCpiOverrideSnapShot } from './domain/buyer-cpi-override-snap-shot';
import { SurveyFormData } from './shared/interfaces/survey-form-data';
import { QuotaGroupingModalComponent } from './quota-grouping-modal/quota-grouping-modal.component';
import { Nesting } from './domain/nesting';
import { NestingQuotaFilter } from './domain/nesting-quota-filter';
import { AssignQuota } from './domain/assign-quota';
import { ChurnZeroConsts, ChurnZeroService } from '@purespectrum1/ui/tracking';
import {
  DifficultQuota,
  FeasibilityExclusionData,
} from '../shared/interfaces/feasibility.interface';
import { DeleteTemplateModalComponent } from './layout/template-modal/delete-template-modal/delete-template-modal.component';
import { TEMPLATE_CONSTANTS } from './constants/template.constants';
import { FeasibilityService } from './services/feasibility/feasibility.service';
import { EditSupplierModalService } from './layout/edit-supplier-modal.service';
import {
  FolderPermissions,
  TemplateDelationValidation,
} from '../shared/services/template/domain/template-delete-permissions';
import {
  Child,
  Children,
  QualificationChildGenderAgeMapper,
} from 'purespectrum-lib';
import { FeasibilityDataV2Service } from './services/feasibility/feasibility-data-v2.service';
import { isEqual } from './utils/deep-compare';
import { PurePriceCPI, Supplier } from './shared/interfaces/pricing.interface';
import { FeasibilityError } from './layout/header/feasibility/domain/feasibility-error';
import { BuyerSettingsService } from '../shared/services/buyer-settings-service/buyer-settings-service.service';
import { SurveyRouteParamsComposer } from '../shared/utils/survey-route-params-composer';
import { SurveyStreamSegregator } from '../shared/utils/survey-stream-segregator';
import {
  MultiCountrySpvCreateFormAllDataMapper,
  MultiCountrySpvCreateFormEachDataMapper,
} from './shared/ui/multicountry/domain/multi-country-single-view-data-mapper';
import { QuotaModalService } from './quota-modal/quota-modal.service';
import { RangeInputButtonComponent } from './range-input-button/range-input-button.component';
import { MultiselectInputButtonComponent } from './multiselect-input-button/multiselect-input-button.component';
import {
  ApplyCensusToMcSurveys,
  McQuotaQualificationActions,
} from './shared/ui/multicountry/domain/mc-quota-qualification-data-mapper';
import { McSvpParentChildSegregator } from './../shared/domain/mc-svp-parent-child-segregator';
import { LocaleFullNameMapper } from '../shared/services/locale/locale-full-name-mapper';
import { MultiCountryService } from './shared/ui/multicountry/services/multi-country.service';
import { McSpvSurveyRequest } from './shared/ui/multicountry/interface/multi-country.interface';
import { SimilarProjectsService } from './services/similar-projects/similar-projects.service';
import { SimilarProjectDataMapper } from './services/domain/similar-project-data-mapper';
import { HighCpiConfirmationActivityGenerator } from '../shared/domain/high-cpi-confirmation-activity-generator';
import { ActivityLogsService } from '../shared/services/activity-logs/activity-logs.service';
import { QuotaPayloadService } from './services/quotas/quota-payload.service';
import { SurveyUnicorns } from './domain/survey-unicorns';
import { AidaPayloadService } from '../shared/services/aida/aida-payload/aida-payload.service';
import { ActivityLogDto } from '../shared/types/activity-log.types';
import { AidaSurveyQuotasEditInspector } from '../shared/domain/aida-survey-quotas-edit-inspector';
import { DEFAULT } from '../shared/constants/defaults';
import { QualificationsMerger } from './domain/qualifications-merger';
import { SurveyNestedQuotas } from './domain/survey-nested-quotas';
import { MultiQuotaNestingModalComponent } from './multi-quota-nesting-modal/multi-quota-nesting-modal.component';
import { NestedQuotaBuilder } from './domain/nested-quota-builder';
import { NestedQualificationBuilder } from './domain/nested-qualification-builder';
import { SurveyQualificationUpdater } from './domain/survey-qualification-updater';
import { SurveyClicksCalculator } from './domain/survey-clicks-calculator';
import { QuotaPayloadMapper } from './domain/quota-payload-mapper';
import { PricingService } from './services/pricing/pricing.service';
import { PurePriceOverride } from './services/domain/pure-price-override';
import { MultiQuotaNestingReturn } from './multi-quota-nesting-modal/interfaces/multi-quota-nesting.interface';
import { BuyerConfigSettingsVerifier } from '../shared/utils/buyer-config-settings-verifier';

import { sortDataByFieldName } from '../shared/utils/sort-data';
import { UnNestedQuotaFilter } from './domain/unnested-quota-filter';
@Component({
  selector: 'ps-ui-create-surveys',
  templateUrl: './create-surveys.component.html',
  styleUrls: ['./create-surveys.component.scss'],
})
export class CreateSurveysComponent
  implements OnInit, OnDestroy, OnChanges, AfterContentChecked
{
  @Input() isInsight: boolean = false;
  @Input() surveyId: number;
  @Input() set auth(data: AuthState) {
    this._auth.setState(data);
  }
  @Input() insightsBuyerMargin: number;
  @Input() surveyInitialValues: Partial<SurveyFormData> = {};
  @Input() disableInputFields: boolean = false;
  @Output() surveyValuesUpdated: EventEmitter<Partial<SurveyFormData>> =
    new EventEmitter();
  @ViewChild(FooterComponent) footer: FooterComponent;
  private readonly _syncNestedQuotasEmmiter = new EventEmitter<
    Array<NestedQuota>
  >();
  private _prevSelectedCountries: CountryInterface[] = [];
  private _subscriptions: Subscription = new Subscription();
  private _feasibilityDispatcher = new Subject<{
    dirty: boolean;
    action?: string;
    surveyMetaDataUpdated?: boolean;
  }>();
  private _lostQualifications: string[] = [];
  private _isServiceBuyerLoggedIn: boolean = false;
  private _isCountryChanged: boolean = false;
  private _nestedQuotas: NestedQuota[] = [];
  private _previousNestingQualIds: Array<number> = [];
  private _mcSpvnestedQualificationsIds: {
    [surveyId: number]: number[];
  } = {};
  private _cpiData: CPIData = {} as CPIData;
  private _editableNestingGroupId: string = DEFAULT.NESTED_GROUP_KEY;

  readonly QUALIFICATIONS_RENDER_TYPE = Constants.QUALIFICATIONS_RENDER_TYPE;
  readonly QUALIFICATION_CODES = QUALIFICATION_CODES;
  readonly TEMPLATE_DROPDOWN_CONSTANTS = TEMPLATE_CONSTANTS.DROPDOWN;

  public isMcSurvey: boolean = false;
  public mcSpvParentSurvey: BuyerSurveyResponse;
  public form!: FormGroup;
  public userType: string = '';
  public userDetails?: User = {} as User;
  public categories: CategoriesInterface[] = [];
  public countries: CountryInterface[] = [];
  public languages: LanguageInterface[] = [];
  public locationTabs: Tab[] = [];
  public visibleLocationTabs: Tab[] = [];
  public qualifications: Qualification[] = [];
  public visibleQualifications: Qualification[] = [];
  public preQualifications: Qualification[] = [];
  public locationQualification: Qualification = {} as Qualification;
  public advanceTargetingQualifications: AdvanceTargetingQualification[] = [];
  public quotas: QuotaPayload[] = [];
  public preQuotas: QuotaPayload[] = [];
  public isSurveyCreated: boolean = false;
  public selectedCountryCode: string = '';
  public selectedLanguageCode: string = '';
  public countryCode: string = 'US';
  public languageCode!: string;
  public showPriceRecoModel: boolean = false;
  public activeTabIndex: number = -1;
  public activeTab: Tab = {
    title: '',
    id: 0,
    renderType: 0,
  };
  public templateEnable = false;
  public multiCountryDraft: boolean = false;
  public isEditMode: boolean = false;
  public isLiveSurveyEdit: boolean = false;
  public allowMultipleCountriesSelection: boolean = false;
  public footerButtonStatus = STEPPER_BUTTON_STATUS.DEFINE_AUDIENCE;
  public payloadQualifications: QualificationPayload[] = [];
  public surveyDetails!: BuyerSurveyResponse;
  public nestedQuotasActive: boolean = false;
  totalClicks: number = 0;
  isTCChildSurvey: boolean = false;
  purePriceQuota: NestedQuota[] = [];
  public purePriceData!: MetaDataToPurePrice;
  public manualProcessing: boolean = false;
  public fsacodeFilePath?: string;
  public zipcodeFilePath?: string;
  public surveyPayload: SurveyPayload = {} as SurveyPayload;
  public changeQuotaDistribution: EventEmitter<number> =
    new EventEmitter<number>();
  public deleteLocationQuotas: EventEmitter<string> =
    new EventEmitter<string>();
  public clearLocationData: EventEmitter<string> = new EventEmitter<string>();
  public isTCSPAEditMode: boolean = false;
  public SURVEY_STATUS = SURVEY_STATUS;
  public isQlcLiveSurvey: boolean = false;
  public templates: TemplateListResponse[] = [];
  public templateOption: TreeDropdownOption = {
    uniqueKey: 'id',
    displayKey: 'name',
    navigationKey: 'templates',
    removeItem: false,
    icon: 'fa fa-file-text-o',
    noContentMsg: 'No Template created yet',
    mcIconIdentifier: 'is_mc_template',
  };
  public templateFolderOption: TreeDropdownOption = {
    uniqueKey: 'id',
    displayKey: 'name',
    navigationKey: 'subfolders',
    headerLabel: 'My templates',
    removeItem: false,
    icon: 'fa fa-folder',
    noContentMsg: 'No Template created yet', // Even though it is template-folder content, this message is for ease of user
    mcIconIdentifier: 'is_mc_template',
  };
  public isRemoveSelectedTemplate: boolean = false;
  public selectedTemplateResponse?: TemplateResponse;
  public showAllQualifications = true;
  public quotasForPurePrice: boolean = false;
  public enableMarginForOperatorBuyer: boolean = false;
  public margin_override = {
    buyer_cpi: 0,
    margin: Constants.AVERAGE_VARIABLE_MARGIN,
    supplier_cpi: 0,
  };
  public averageSupplierMarginValue = Constants.AVERAGE_VARIABLE_MARGIN;
  public marginValue: number = Constants.AVERAGE_VARIABLE_MARGIN;
  public marginAccepted = false;
  public showWarningModal = false;
  public isServiceBuyerOperation = false;
  public showCategory: boolean = true;
  public manualScpiDisabled: boolean = false;
  public currencySymbol: string = '$';
  public isTemplateChanged: boolean = false;
  public showClickBalance: boolean = false;
  public requireBuyerCpiConfirmation = false;
  public buyerCpiConfirmation = Confirmation.confirmed();
  public isMcDraftSaved: boolean = false;
  public prePayload: SurveyRequest = {} as SurveyRequest;
  public currentPayload: SurveyRequest = {} as SurveyRequest;
  public isNestingQuotaGroupingEnabled: boolean = false;
  public isNestingMultiQuotaEnabled: boolean = false;
  public manualSCPIOverrideEnabled: boolean = false;
  public purePriceRateCardEnable: boolean = false;
  public marginBelowAverage: boolean = false;
  private _buyerCpiOverrideSnapShot: BuyerCpiOverrideSnapShot =
    new BuyerCpiOverrideSnapShot(0);
  public isAntmanEnabled?: boolean = false;
  public hasNestedGroup: boolean = false;
  public feasibilityExclusionData?: FeasibilityExclusionData;
  private _currencyExchangeRate: CurrencyExchangeRate =
    {} as CurrencyExchangeRate;
  public showFlyoutWindow: boolean = false;
  public coreQualificationsInvalid: Record<string, boolean> = {};
  public shouldLoadNewTemplateConfiguration = false;
  public blendId: string = 'NA';
  private _childAgeGenderQualificationData?: RangeOption[];
  public newCpiConfirmation: string;
  private _confirmationHighCpiCalls: Array<string> = [];
  public attributeQualifications: AttributeQualification[] = [];
  public areSurveyDetailsHandlersInitialized = false;
  private _eligibileSuppliers: Supplier[] = [];
  private _eligibleSuppliersPricingDetails: PurePriceCPI;
  private _supplierBlends: GetSupplierBlendInterface[] = [];
  public isLanguageChange: boolean = false;
  public selectedTemplateId$!: Observable<number | null>;
  public mcQBTotalCompletesText: string = '';
  public hasMoreQuotasThanCompletes: boolean = false;
  public readonly Constants = Constants;

  private _surveyIdCommand$ = new EventEmitter<number>();
  private _surveyDetailsCommand$ = new EventEmitter<BuyerSurveyResponse>();
  private _surveyRouteParamsComposer!: SurveyRouteParamsComposer;
  private _selectTemplateCommand$ =
    new EventEmitter<TemplateListResponse | null>();
  private _mcqbSurveyPayload: McSpvSurveyRequest;
  private _priceOverrideChanges$ = new Subject<number>();
  private _isLoggedInAsOperator: boolean = false;

  allMcChildSurveys: {
    [surveyId: number]: BuyerSurveyResponse;
  } = {};
  allMcRelatedQualifications = new Map<string, Qualification[]>();

  @ViewChild(RangeInputButtonComponent)
  rangeInputButtonComponent: RangeInputButtonComponent;
  @ViewChild(MultiselectInputButtonComponent)
  multiselectInputButtonComponent: MultiselectInputButtonComponent;
  public alwaysShowQualCodes: number[] = [];
  public deprecatedQualifications: Qualification[] = [];
  public isCloneSurvey: boolean = false;
  public considerSingleLanguageSelectionAsAll: boolean = false;
  public unicornQuotas: UnicornQuotas[] = [];
  public difficultQuotas: DifficultQuota[] = [];
  public defaultPurePriceOverride: PurePriceOverride = new PurePriceOverride(
    false,
    0
  );

  constructor(
    private _fb: FormBuilder,
    private _modal: ModalService,
    private _toastr: ToasterService,
    private _commonService: CommonService,
    private _buyerApiService: BuyerApiService,
    private _sharedDataService: SharedDataService,
    private _createSurveyServiceOb: CreateSurveyService,
    private _editSurveyServiceOb: EditSurveyService,
    private _qualificationsService: QualificationsService,
    private _feasibilityComponentService: FeasibilityComponentService,
    private _surveyMetaDataService: SurveyMetaDataService,
    private _multiCountryDraftService: MultiCountryDraftService,
    private _multiCountryService: MultiCountryService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _auth: AuthService,
    private _surveyPayloadService: SurveyPayloadService,
    private _churnZeroService: ChurnZeroService,
    private _marginService: MarginService,
    private _templateService: TemplateService,
    private _marketplaceService: MarketplaceService,
    private _cdRef: ChangeDetectorRef,
    private _location: Location,
    private _feasibilityService: FeasibilityService,
    private _editSupplierModalService: EditSupplierModalService,
    private _activityLogsService: ActivityLogsService,
    private _feasibilityDataV2Service: FeasibilityDataV2Service,
    private _buyerSettingsService: BuyerSettingsService,
    private _quotaModalService: QuotaModalService,
    private _similarProjectsService: SimilarProjectsService,
    private _quotaPayloadService: QuotaPayloadService,
    private _aidaPayloadService: AidaPayloadService,
    private _pricingService: PricingService
  ) {
    this.userType = this._auth.userType;
    this._surveyRouteParamsComposer = new SurveyRouteParamsComposer(
      this._route,
      this._router
    );
  }

  private _getAuthDetails() {
    this.userDetails = this._auth.user;
    this._isLoggedInAsOperator =
      this._auth.userType === 'operator' && !this._auth.serviceBuyer;

    if (this._auth.buyerConfig!.clickBal || this._isLoggedInAsOperator) {
      this.showClickBalance =
        this._auth.buyerConfig!.clickBal || this._isLoggedInAsOperator;
    }
    // enable multi country dropdown only for config enabled buyers
    this.allowMultipleCountriesSelection =
      this._decideMultipleCountriesSelection();

    this.isServiceBuyerOperation = this._auth.isLoggedInAsServiceBuyer();
    this.currencySymbol = this._auth.companyConfig?.currencySymbol;
    this.isNestingQuotaGroupingEnabled =
      this._auth.buyerConfig?.enableNestingQuotaGrouping ?? false;
    this.isNestingMultiQuotaEnabled =
      this._auth.buyerConfig?.enableNestingMultiQuota ?? false;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.form) {
      this._initForm();
    }
    if (!this.categories.length) {
      this._getCategories();
    }
    if (!this.areSurveyDetailsHandlersInitialized) {
      this._handleSurveyDetails();
      this._handleRouteChanges();
      this._handleMcSurveysQualification();
      this.areSurveyDetailsHandlersInitialized = true;
    }
    if (changes.surveyId?.currentValue) {
      this._surveyIdCommand$.next(changes.surveyId.currentValue);
    }

    if (changes.surveyInitialValues?.currentValue) {
      this.form.patchValue(changes.surveyInitialValues?.currentValue, {
        emitEvent: false,
      });
    }
  }

  ngOnInit(): void {
    this._initForm();
    this._getCategories();
    this._getAuthDetails();
    this._handleLocaleChanges();
    this._handleSingleCountryTemplateApplication();
    this._handleMCSvpSurveyCompleteInfoText();
    this._handleNestedQuotasFromPayload();
    const nestedQuotaSubscriber = this._syncNestedQuotasEmmiter.subscribe(
      (nestedQuotas: Array<NestedQuota>) => {
        this._syncNestedQuotas(nestedQuotas);
      }
    );
    this._subscriptions.add(nestedQuotaSubscriber);

    const processingSubscriber = this._surveyMetaDataService
      .getSurveyProcessing()
      .subscribe(({ manual }) => {
        if (typeof manual === 'boolean') {
          this.manualProcessing = manual;
          if (!this.manualProcessing && this.isSurveyCreated) {
            // if survey is already filled save it when switched to real time processing
            this._decideAndSetFeasibility(false);
          }
        }
      });
    this._subscriptions.add(processingSubscriber);
    this._listenSaveSurvey();
    const feasibilityDispatcherSubscription = this._feasibilityDispatcher
      .pipe(
        debounceTime(1000),
        filter(() => this.isSurveyCreated)
      )
      .subscribe((feasibilityParams) => {
        this._decideAndSetFeasibility(
          feasibilityParams.dirty,
          feasibilityParams.action,
          feasibilityParams.surveyMetaDataUpdated
        );
      });

    this._subscriptions.add(feasibilityDispatcherSubscription);

    this._showServiceOperatorInput();

    const getSupplierMetaData = this._surveyMetaDataService
      .getSupplierOverrideValue()
      .subscribe((manualScpiOverrideEnabled) => {
        this.manualSCPIOverrideEnabled = manualScpiOverrideEnabled;
        this.manualScpiDisabled = !!manualScpiOverrideEnabled;
      });

    this._subscriptions.add(getSupplierMetaData);

    this._subscriptions.add(
      this._surveyMetaDataService
        .getPurePriceRateCardEnable()
        .subscribe((purePriceRateCardEnable) => {
          this.purePriceRateCardEnable = purePriceRateCardEnable;
          if (this.surveyDetails) {
            this.surveyDetails.is_pure_price_rate_card_enable =
              purePriceRateCardEnable;
          }
        })
    );

    this._subscriptions.add(
      this._surveyMetaDataService
        .getPurePriceOverride()
        .subscribe((purePriceOverride) => {
          this.defaultPurePriceOverride = purePriceOverride;
        })
    );

    const getCPIddataSubscription = this._surveyMetaDataService
      .getCPIData()
      .pipe(
        tap((data) => {
          if (
            data?.flexibilityPrice?.survey_price_override &&
            this.surveyPayload.price_override !==
              data?.flexibilityPrice?.survey_price_override
          ) {
            this._priceOverrideChanges$.next(
              data?.flexibilityPrice?.survey_price_override
            );
          }
        })
      )
      .subscribe((data: CPIData) => {
        this._cpiData = deepCopy(data);
        // this._updateMarginForWaspSurveyWithManualScpiOverride(data);
        if (this._cpiData.flexibilityPrice?.survey_price_override) {
          this.surveyPayload.offer_price =
            this._cpiData.flexibilityPrice?.survey_price_override;
        }
      });
    this._subscriptions.add(getCPIddataSubscription);

    const formChangesSubscription = this.form.valueChanges
      .pipe(
        filter(() => !this.isSurveyCreated),
        skip(1)
      )
      .subscribe(() => {
        const payload = this._mapPayload();
        this.surveyPayload = payload as any;
      });

    this._subscriptions.add(formChangesSubscription);
    if (!this.surveyId) {
      this._createSurveyServiceOb.setIsPurePriceApplied(false);
    }

    this._subscriptions.add(
      this._multiCountryDraftService
        .getMultiCountrySurveyData()
        .subscribe((data) => {
          this.isMcDraftSaved = !!(data && data.length);
        })
    );

    this._subscriptions.add(
      this._marketplaceService.invokeUpdateSurvey.subscribe(() => {
        if (this.isLiveSurveyEdit) {
          this.footer?.editSurvey();
        } else {
          this.footer?.save('save');
        }
      })
    );

    const cpiFeasibilityData$ = this._surveyMetaDataService
      .getFeasibilityExclusionData()
      .pipe(
        filter(() => !!this.form.valid),
        // Need to fetch CPI & feasibility data only for current survey
        filter((data) => Number(data.survey_id) === Number(this.surveyId)),
        mergeMap((data) => {
          // We are sure to make CPI, feasibility calculations here, so loader should show
          this._showFeasibilityCpiLoading();
          const currentSurveyId = Number(data.survey_id);

          const payload = this._mapPayload();
          const languageCode = this._getLanguageCode('short_code');

          const flexibilityPriceParams$ =
            this._feasibilityDataV2Service.getFlexibilityPriceParams(
              currentSurveyId
            );
          const suppliers$ = this._feasibilityDataV2Service.getSuppliers(
            currentSurveyId,
            languageCode,
            payload
          );

          return forkJoin({
            flexibilityPriceParams: flexibilityPriceParams$,
            suppliers: suppliers$,
          }).pipe(
            mergeMap(({ suppliers, flexibilityPriceParams }) =>
              this._feasibilityDataV2Service.getCpiFeasibilityData(
                currentSurveyId,
                languageCode,
                payload,
                this.qualifications,
                suppliers,
                flexibilityPriceParams,
                this._supplierBlends,
                this._eligibileSuppliers,
                data,
                !!this.surveyDetails?.mc_unique_id,
                this.defaultPurePriceOverride
              )
            )
          );
        }),
        catchError((e) => {
          const error = new FeasibilityError(e, this.quotas as any);
          this._toastr.error(error.message);
          this._hideFeasibilityCpiLoading();
          return of(this._surveyMetaDataService.defaultCpiFeasibilityData);
        })
      );

    this._subscriptions.add(
      cpiFeasibilityData$.subscribe((cpiFeasData) => {
        this._surveyMetaDataService.sendCpiFeasibilityData(cpiFeasData);
        this._surveyMetaDataService.setPurePriceRateCardEnable(
          !!this.surveyDetails?.is_pure_price_rate_card_enable
        );
      })
    );

    const fx = this._auth.companyConfig?.fx || CURRENCY_CODE.US;
    if (this.isServiceBuyerOperation && fx !== CURRENCY_CODE.US) {
      this._getCurrencyExchangeRate(fx);
    }

    this._subscriptions.add(
      this._surveyMetaDataService
        .getSurveyHeaderData()
        .pipe(
          filter((surveyTopBarData) => !!surveyTopBarData.surveyId),
          map((surveyTopBarData) => {
            const {
              location,
              expected_ir,
              expected_loi,
              incl_excl,
              surveyFieldTime,
              survey_meta_data_updated,
              isAidaEnabled,
            } = surveyTopBarData;
            const metaData = {
              location,
              expected_ir,
              expected_loi,
              incl_excl,
              surveyFieldTime,
              survey_meta_data_updated,
              isAidaEnabled,
            };
            return [surveyTopBarData, metaData];
          }),
          distinctUntilChanged(([, x], [, y]) => {
            return isEqual(x, y);
          }),
          map(([surveyTopBarData]) => surveyTopBarData)
        )
        .subscribe((surveyTopBarData) => {
          this._fetchEligibileSuppliers(surveyTopBarData as SurveyTopBarData);
          this._fetchPurePriceCpi(!!surveyTopBarData.isAidaEnabled);
        })
    );

    this._subscriptions.add(
      this.form
        .get('clickBalancing')
        ?.valueChanges.pipe(
          filter(() => this._isMcSpvSurvey()),
          skip(1),
          distinctUntilChanged()
        )
        .subscribe((res) => {
          this._redistributeQuotasForSpvSurveys(!!res);
        })
    );

    this._handleMcSpvGoalChange();
    this._fetchSupplierBlends();
    this._handleMcSvpFlyoutSyncing();
    this._handleSurveyUnicornsAndDifficultQuotasPayload();
    this._handleAidaErrors();
    this._handleAidaEligibiltyCheck();
  }

  private get _combinedQualifications(): CombinedQualification[] {
    return new QualificationsMerger(this.qualifications)
      .replace(this.locationQualification)
      .merge(this.advanceTargetingQualifications)
      .get() as CombinedQualification[];
  }

  private get _surveyNestedQuotas(): SurveyNestedQuotas {
    return new SurveyNestedQuotas(this._nestedQuotas);
  }

  private _handleRouteChanges(): void {
    const nonEditRoute$ = this._surveyRouteParamsComposer
      .nonEdit()
      .pipe(share());
    const navigationExtrasImposter$ = this._multiCountryDraftService
      .getMcSpvSelectedChildId()
      .pipe(
        filter((id): id is number => !!id),
        map((id) => <SurveyNavigationState>{ svp_child_id: id })
      );
    const navigationExtras$ = merge(
      this._surveyRouteParamsComposer.extras() as Observable<SurveyNavigationState>,
      navigationExtrasImposter$
    );

    const routeId$ = merge(
      this._surveyRouteParamsComposer.edit(),
      this._surveyIdCommand$
    );

    /**
     * If it is either normal survey or SVP parent without child Id in extras, then -
     *  A.) Fetch the details of the survey
     *  B.) If the survey is SVP parent, then fetch Id of its first child
     *  C.) If the survey is non SVP, then default flow
     */
    const editRouteNotSvpChildId$ = routeId$.pipe(
      switchMap((routeId) =>
        new SurveyStreamSegregator(
          navigationExtras$,
          routeId
        ).notSvpChildSurveyIdStream()
      )
    );

    const notSvpChildSurvey$ = editRouteNotSvpChildId$.pipe(
      switchMap((surveyId) => this._getSurveyDetails(surveyId)),
      share()
    );

    const nonSvpSurvey$ = notSvpChildSurvey$.pipe(
      filter((survey) => !survey.mc || !survey.mc.SVP)
    );

    const svpParentSurvey$ = notSvpChildSurvey$.pipe(
      filter((survey) => survey.mc && !!survey.mc.SVP),
      share()
    );

    const firstSvpChildSurveyId$ = svpParentSurvey$.pipe(
      map((survey) => {
        this.mcSpvParentSurvey = survey;
        return survey.mc.surveys![0].surveyId;
      })
    );

    // If SVP child Id exists in the route navigation extras, or the survey Id of the first child from parent is fetched
    const editRouteSvpChildId$ = routeId$.pipe(
      switchMap((routeId) =>
        new SurveyStreamSegregator(
          navigationExtras$,
          routeId
        ).svpChildSurveyIdStream()
      )
    );

    const svpChildSurvey$ = merge(
      editRouteSvpChildId$,
      firstSvpChildSurveyId$
    ).pipe(switchMap((surveyId) => this._getSurveyDetails(surveyId, true)));

    this._subscriptions.add(
      nonEditRoute$.subscribe(() => {
        this.manualScpiDisabled = false;
        this._surveyMetaDataService.setSupplierOverrideValue(false);
        this._getCountries();
      })
    );

    this._subscriptions.add(
      merge(nonSvpSurvey$, svpChildSurvey$).subscribe((survey) => {
        this._editSurveySelection(survey);
        this._updateBuyerMarginValidation();
      })
    );

    this._subscriptions.add(
      navigationExtras$.subscribe((state) => {
        this.isCloneSurvey = state?.isCloneSurvey ?? false;
        if (state.is_race_qualification_removed) {
          this._toastr.warning(
            notifyMessage.warningMessage.RACE_QUALIFICATION_REMOVED
          );
        }
      })
    );
  }

  private _getSurveyDetails(
    surveyId: number,
    accessible?: boolean
  ): Observable<BuyerSurveyResponse> {
    return this._buyerApiService.getSurveyById(surveyId, accessible).pipe(
      catchError((_) => {
        if (!this.isInsight) {
          this._router.navigate(['/dashboard']);
        }
        return of(null);
      }),
      filter((survey): survey is BuyerSurveyResponse => !!survey),
      tap(() => this._resetSurveyDetail()) // for mcgroup change survey reset survey detail
    );
  }

  private _handleMcSvpFlyoutSyncing(): void {
    this._subscriptions.add(
      this._multiCountryDraftService
        .getMcSpvSurveyRequest()
        .pipe(
          skip(1),
          filter((data) => !!data),
          tap((survey) => (this._mcqbSurveyPayload = survey)),
          map(
            (data) =>
              data.surveys?.find(
                (childSurvey) =>
                  childSurvey.survey_localization ===
                  this.surveyDetails.survey_localization
              )!
          ),
          filter((survey) => !!survey),
          distinctUntilChanged(
            (a, b) => a.completes_required === b.completes_required
          )
        )
        .subscribe((currentSurvey) => {
          this.form.patchValue({
            numberOfCompletes: currentSurvey.completes_required,
          });
          this.calculateTotalClicks();
          this.form
            .get('numberOfCompletes')
            ?.markAsPristine({ onlySelf: true });
        })
    );
  }

  private _isMcSpvSurvey(): boolean {
    return !!this.surveyDetails?.mc?.SVP;
  }

  private _sendMcSpvDataUpdateForCurrentLocale(
    localization: string,
    qualifcations: QualificationPayload[],
    quotas: QuotaPayload[],
    offer_price: number = 0,
    buyerMargin: number = 0
  ) {
    const cache = {
      for: 'each' as const,
      data: [
        new MultiCountrySpvCreateFormEachDataMapper(
          localization,
          qualifcations,
          quotas,
          this._auth.isLoggedInAsServiceBuyer(),
          offer_price,
          buyerMargin,
          this._auth.buyerConfig
        ).transform(),
      ],
    };
    this._multiCountryDraftService.sendMcSpvUpdateInCacheCommand(cache);
  }

  private _transformMcSvpPayloadAndSendToSave(): void {
    if (!this._isMcSpvSurvey()) {
      return;
    }

    const qualifications = this._mapQualifications();
    const quotas = this._buildSurveyQuotaPayload();
    const currentLocalization = this._buildSurveyLocalization() || '';
    const { buyerCpiOverride: offerPrice, buyerMargin } = this.form.value;
    this._sendMcSpvDataUpdateForCurrentLocale(
      currentLocalization,
      qualifications,
      quotas,
      offerPrice,
      buyerMargin
    );
    if (this.allMcChildSurveys[this.surveyId]) {
      this.allMcChildSurveys[this.surveyId].quotas = quotas as Quotas[];
      this.allMcChildSurveys[this.surveyId].qualifications =
        qualifications as SurveyQualification[];
    }
    this._sendMcSpvDataUpdateForAll();
  }

  private _sendMcSpvDataUpdateForAll() {
    const cache = {
      for: 'all' as const,
      data: new MultiCountrySpvCreateFormAllDataMapper(this.form).transform(),
    };

    if (cache.data.completes_required) {
      this._redistributeQuotasForSpvSurveys(
        !!this.surveyDetails.click_balancing
      );
      this._toastr.info(
        notifyMessage.infoMessage.MC_SINGLE_VIEW.COMPLETES_CHANGED_FOR_ALL
      );
    }

    this._multiCountryDraftService.sendMcSpvUpdateInCacheCommand(cache);
  }

  private _listenSaveSurvey() {
    const listenSurveySubscriber = this._surveyMetaDataService
      .listenSaveSurvey()
      .subscribe(() => {
        if (this.manualProcessing && this.isMcDraftSaved) {
          this._sharedDataService.setIsPageLoaderDisplay(true);
        }
        if (!this.isSurveyCreated) {
          if (!this.isMcDraftSaved && this.manualProcessing) {
            this.prePayload = this.currentPayload;
            this.currentPayload = this._mapPayload();
            if (
              JSON.stringify(this.prePayload) ===
              JSON.stringify(this.currentPayload)
            ) {
              this._toastr.success(
                notifyMessage.successMessage.CPI_FEASIBILITY_ALREADY_UPDATED
              );
              return;
            }
            this.autoSaveSurveyInDraft(true);
          } else {
            this._saveSurvey(false);
          }
        } else {
          this._decideAndSetFeasibility(false);
        }
      });
    this._subscriptions.add(listenSurveySubscriber);
  }

  private _updateMarginForWaspSurveyWithManualScpiOverride(cpiData: CPIData) {
    if (this.enableMarginForOperatorBuyer && cpiData.suppliers?.length) {
      const selectedSuppliers = cpiData.suppliers?.filter(
        (supplier) => supplier.completes_goal
      );
      const updatedMargin = selectedSuppliers[0].scpi
        ? (1 - selectedSuppliers[0].scpi / selectedSuppliers[0].cpi) * 100
        : this.form.value.buyerMargin;
      this.form.controls.buyerMargin.setValue(Number(updatedMargin.toFixed(2)));
      this.calculateSuppCpiOverride(false, false);
    }
  }
  private _decideMultipleCountriesSelection(): boolean {
    // Once surveys is created, not allowing multiple country selection
    if (this.surveyId) {
      return false;
    }

    return (
      !!this._auth.buyerConfig?.enableMultiCountry ||
      !!this._auth.buyerConfig?.enableMultiCountrySingleProjectView
    );
  }

  private _showServiceOperatorInput() {
    const authServiceSubscription =
      this._auth.loggedInAsServiceBuyer$.subscribe((isLoggedIn) => {
        if (isLoggedIn) {
          this._isServiceBuyerLoggedIn = true;
        }
      });
    this._subscriptions.add(authServiceSubscription);
    if (
      this._isServiceBuyerLoggedIn &&
      this._auth.buyerConfig?.enableTargetMargin
    ) {
      this.enableMarginForOperatorBuyer = true;
    }
    this.templateEnable = this._auth.buyerConfig?.enableTemplate ?? false;
    if (this.templateEnable) {
      this.getTemplates();
    }
    // Managing category display
    this._activateCategory();
  }

  setPermissionsForFoldersTemplate(folders: TemplateListResponse[]) {
    return folders.map((folder) => {
      folder.type = 'folder';
      folder.deletable = this.hasPermissionToDeleteFolder(folder);
      folder.templates.forEach((template) =>
        this.setTemplatePermissions(template)
      );
      if (!!folder?.subfolders?.length) {
        this.setPermissionsForFoldersTemplate(folder?.subfolders);
      }
      return folder;
    });
  }

  setTemplatePermissions(templates: Template | TemplateInfo) {
    templates.deletable = this.hasPermissionToDeleteTemplate(templates);
    return templates;
  }

  private getTemplates() {
    const getTemplateSubscription = this._templateService
      .getUpdatedTemplateList()
      .subscribe((templates) => {
        const updatedTemplates =
          this.setPermissionsForFoldersTemplate(templates);
        this.templates = updatedTemplates;
      });
    this._subscriptions.add(getTemplateSubscription);
  }

  private _activateCategory() {
    if (this.templateEnable && this.enableMarginForOperatorBuyer) {
      this.showCategory = false;
    } else if (this.templateEnable && !this.enableMarginForOperatorBuyer) {
      this.showCategory = false;
    } else if (!this.templateEnable && this.enableMarginForOperatorBuyer) {
      this.showCategory = false;
    }
  }

  private _verifyTemplateCategory() {
    if (!this.templateEnable) {
      return;
    }

    const templateCategory = this.categories.find((category) => {
      return category.id === Constants.TEMPLATE_DEFAULT_CATEGORY;
    });
    this.selectCategory(templateCategory!);
  }

  private _initForm() {
    this.form = this._fb.group({
      surveyTitle: [null, [Validators.required]],
      selectedCategory: [null],
      selectedTemplate: [null],
      selectedCountries: [null, [Validators.required]],
      selectedLanguages: [null, [Validators.required]],
      numberOfCompletes: [null, [Validators.required]],
      lengthOfSurvey: [null, [Validators.required]],
      incidence: [null, [Validators.required]],
      fieldTime: [null, [Validators.required]],
      clickBalancing: [0, [Validators.required]],
      buyerCpiOverride: [0],
      buyerMargin: [
        Constants.AVERAGE_VARIABLE_MARGIN,
        [Validators.min(-99), Validators.max(99)],
      ],
      supplierCpiOverride: [0],
    });
    this.form.patchValue(this.surveyInitialValues, { emitEvent: false });
  }

  private _disableInitForm() {
    this.form.disable();
  }

  private _handleLocaleChanges() {
    const countryChange$ = this.form
      .get('selectedCountries')!
      .valueChanges.pipe(
        tap(
          (countries) =>
            (this.considerSingleLanguageSelectionAsAll = countries.length > 1)
        ),
        filter(() => !!this.surveyId && !this.isEditMode),
        pairwise(),
        tap(([prev]) => {
          this._prevSelectedCountries = Array.isArray(prev) ? prev : [prev];
        })
      );

    const languageChange$ = this.form
      .get('selectedLanguages')!
      .valueChanges.pipe(
        // TODO: skiping language change for mc surveys, because was loading old
        // qualifications informations
        filter(() => !!this.surveyId && !this.surveyPayload.mc_unique_id)
      );

    this._subscriptions.add(
      merge(countryChange$, languageChange$).subscribe(() => {
        this.preQuotas = this._createSurveyServiceOb.addQuotaCategory(
          this.quotas
        );
        this.preQualifications = this.qualifications;
      })
    );
  }

  public onCancelTemplateSelection() {
    this._selectTemplateCommand$.next(null);
  }

  private _handleSingleCountryTemplateApplication() {
    this.selectedTemplateId$ = this._selectTemplateCommand$.pipe(
      switchMap((selectedTemplate) => {
        if (!selectedTemplate) {
          return of(null);
        }

        if (!selectedTemplate.templates[0].id) {
          return of(null);
        }

        return of(selectedTemplate.templates[0].id);
      })
    );

    this._subscriptions.add(
      this._selectTemplateCommand$
        .pipe(
          filter(
            (selectedTemplate): selectedTemplate is TemplateListResponse =>
              !!selectedTemplate
          ),
          filter(() => !this.surveyDetails || !this.surveyDetails.mc_unique_id)
        )
        .subscribe((template) => {
          this.selectTemplateOnTopOfSCSurvey(template);
        })
    );
  }

  private _handleMCSvpSurveyCompleteInfoText(): void {
    this._subscriptions.add(
      this._multiCountryDraftService
        .getMcSpvSurveyRequest()
        .pipe(
          skip(1),
          filter(() => this._isMcSpvSurvey())
        )
        .subscribe((data) => {
          this.mcQBTotalCompletesText = `Total Completes: ${data.completes_required}`;
        })
    );
  }

  private _handleNestedQuotasFromPayload(): void {
    this._subscriptions.add(
      this._quotaPayloadService.getNestedQuotas().subscribe((nestedQuotas) => {
        this._previousNestingQualIds =
          this._surveyNestedQuotas.qualificationIds();
        this._nestedQuotas = nestedQuotas;
        this._editableNestingGroupId = this._findEditableNestingGroupId(
          this._surveyNestedQuotas.groupIds()
        );
        this.hasMoreQuotasThanCompletes = this._hasMoreQuotasThanCompletes();
      })
    );
  }

  private _findEditableNestingGroupId(groupIds: string[]): string {
    const lastGroupId = groupIds[groupIds.length - 1];
    return lastGroupId ? lastGroupId : DEFAULT.NESTED_GROUP_KEY;
  }

  private _editSurveySelection(survey: BuyerSurveyResponse) {
    this.isSurveyCreated = true;
    this.surveyId = Number(survey.ps_survey_id);
    this.isEditMode = true;
    this.allowMultipleCountriesSelection =
      this._decideMultipleCountriesSelection();
    this._surveyDetailsCommand$.next(survey);
  }

  private _visibleQualifications(): Qualification[] {
    if (!this.selectedTemplateResponse?.template) {
      return this.qualifications;
    }

    if (this.showAllQualifications) {
      return this.qualifications;
    }

    return this.qualifications.filter((q) =>
      (this.selectedTemplateResponse?.template.qualifications ?? []).some(
        (templateQualificaiton) =>
          Number(templateQualificaiton.qualification_code) ===
          q.qualification_code
      )
    );
  }

  public updateQualificationsVisibility(event: MouseEvent) {
    event.preventDefault();

    this.showAllQualifications = !this.showAllQualifications;
    this._filterQualificationsForTemplate();
  }

  private _filterQualificationsForTemplate() {
    if (
      this.selectedTemplateResponse?.template &&
      !this.showAllQualifications
    ) {
      const selectedQualificationsIds = (
        this.selectedTemplateResponse.template.qualifications ?? []
      ).map((qualification) => qualification.qualification_code);

      this.visibleQualifications = this.qualifications.filter(
        (qualification) =>
          selectedQualificationsIds.includes(
            qualification.qualification_code
          ) || !!qualification.quotas?.length
      );
      this.visibleLocationTabs = this.locationTabs.filter(
        (locationTab) =>
          locationTab.id === this.locationQualification.qualification_code
      );
    } else {
      this.visibleQualifications = this.qualifications;
      this.visibleLocationTabs = this.locationTabs;
      this.activeTabIndex = this.locationTabs.findIndex(
        (tab) => tab.id === this.locationQualification.qualification_code
      );
    }
    // Adding because to help ngFor detect changes in visibleLocationTabs
    this._cdRef.detectChanges();
    this._setLocationQualification(this.locationQualification);
  }

  selectCategory(category: CategoriesInterface) {
    this.form.patchValue({ selectedCategory: category });
    this.autoSaveSurveyInDraft();
  }

  checkTemplateBlenders(blend_id: string) {
    this._surveyMetaDataService.setBlendsData(blend_id);
  }

  selectTemplate(selectedTemplate: TemplateListResponse) {
    this._selectTemplateCommand$.next(selectedTemplate);
  }

  hasPermissionToDeleteFolder(template: TemplateListResponse) {
    const templateDeleteFolder = new FolderPermissions(
      template,
      this._auth.user,
      this._isServiceBuyerLoggedIn
    );
    const result = templateDeleteFolder.check();
    return result;
  }

  hasPermissionToDeleteTemplate(template: Template | TemplateInfo) {
    const templateDeletePermission = new TemplateDelationValidation(
      template,
      this._auth.user,
      this._isServiceBuyerLoggedIn
    );

    const result = templateDeletePermission.check();
    return result;
  }

  deleteTemplate(selectedTemplate: TemplateListResponse) {
    const modalRef = this._modal.open(DeleteTemplateModalComponent, {
      data: selectedTemplate,
    });

    modalRef.onClose$.subscribe((msg: string) => {
      if (!msg || msg === 'cancel') {
        return;
      }

      const deleteObservable =
        selectedTemplate.type === 'folder'
          ? this._templateService.deleteTemplateFolder(selectedTemplate)
          : this._templateService.deleteTemplate(selectedTemplate);

      deleteObservable.subscribe({
        complete: () => {
          const successMessage =
            selectedTemplate.type === 'folder' ? 'Folder' : 'Template';
          this.getTemplates();
          return this._toastr.success(`${successMessage} correctly deleted`);
        },
        error: () => {
          const errorMessage =
            selectedTemplate.type === 'folder'
              ? 'Error on folder'
              : 'Error on template';
          return this._toastr.error(`${errorMessage} delete`);
        },
      });
    });
  }

  public selectTemplateOnTopOfSCSurvey(selectedTemplate: TemplateListResponse) {
    of(!!this.selectedTemplateResponse)
      .pipe(
        switchMap((hasTemplateSelected) => {
          if (hasTemplateSelected) {
            const modalRef = this._modal.open(
              DraftSurveyConfirmationModalComponent,
              {
                data: {
                  text: 'You are changing the template, do you want to delete the current survey?',
                },
              }
            );

            return modalRef.onClose$;
          }

          return of('continue');
        }),
        switchMap((operation) => {
          const delete$ = of(operation).pipe(
            filter((msg) => msg === 'yes'),
            tap(() => this._deleteDraftSurvey(this.surveyId))
          );

          const continue$ = of(operation).pipe(
            filter((msg) => msg === 'continue' || msg === 'no')
          );

          return merge(delete$, continue$).pipe(take(1));
        }),
        tap(() => {
          this.isSurveyCreated = false;
          this.isTemplateChanged = true;
          this.surveyDetails = {} as BuyerSurveyResponse;
        }),
        map(() => {
          this._patchSelectedTemplate(selectedTemplate);
          this.isRemoveSelectedTemplate = true;
          return this._getSelectedTemplateId(selectedTemplate);
        }),
        switchMap((selectedTemplateId) =>
          this._templateService.getById(selectedTemplateId!)
        )
      )
      .subscribe((response) => {
        this.selectedTemplateResponse = response;

        if (this._isMcTemplate(response)) {
          return this._createMcSurveysUsingTemplate(
            response.template.mc_template_id!
          );
        }

        this._patchTemplateData(response.template);
        this.showAllQualifications = false;
        // When locale changes, fetching locale specific qualifications
        this._getQualifications();
      });
  }

  private _patchSelectedTemplate(selectedTemplate: TemplateListResponse) {
    this.form.controls.selectedTemplate.patchValue(selectedTemplate);
  }

  private _getSelectedTemplateId(templateList: TemplateListResponse) {
    return templateList.templates[0]!.id;
  }

  public informOnMcSurveyAdded() {
    this.shouldLoadNewTemplateConfiguration =
      !!this._getTemplateSuppliersOverride().length;
  }

  public async loadNewTemplateConfiguration(
    shouldLoadNewTemplateConfiguration: boolean = false
  ) {
    if (shouldLoadNewTemplateConfiguration) {
      await this._fetchSuppliersAndSetTemplateOverride();
    }
    this.shouldLoadNewTemplateConfiguration = false;
  }

  removeSelectedTemplate() {
    of(!!this.selectedTemplateResponse)
      .pipe(
        filter(() => !!this.selectedTemplateResponse),
        switchMap(() => {
          const modalRef = this._modal.open(
            DraftSurveyConfirmationModalComponent,
            {
              data: {
                text: 'You are removing the template, do you want to delete the current survey?',
              },
            }
          );
          return modalRef.onClose$;
        }),
        switchMap((operation) => {
          const delete$ = of(operation).pipe(
            filter((msg) => msg === 'yes'),
            switchMap(() =>
              this._buyerApiService.deleteDraftSurvey(this.surveyId)
            )
          );

          const continue$ = of(operation).pipe(filter((msg) => msg === 'no'));
          return merge(delete$, continue$).pipe(take(1));
        })
      )
      .subscribe(
        (msg) => {
          if (msg !== 'no') {
            this._toastr.success(
              notifyMessage.successMessage.DRAFT_SURVEY_DELETED
            );
          }
          // Reloading the new empty survey
          this._router.navigateByUrl('/create-surveys').then(() => {
            window.location.reload();
          });
        },
        (err) => {
          const errMsg =
            typeof err === 'string'
              ? err
              : notifyMessage.errorMessage.DRAFT_SURVEY_DELETE;
          this._toastr.error(errMsg);
        }
      );
  }

  private _patchTemplateData(template: Template) {
    this.form.patchValue({
      surveyTitle: template.survey_title,
      numberOfCompletes: template.completes_required,
      lengthOfSurvey: template.expected_loi,
      incidence: template.expected_ir,
      fieldTime: template.field_time,
      clickBalancing: template.click_balancing,
    });
    this.calculateTotalClicks();
    this._patchTemplateLocale(template.survey_localization);
    this.surveyDetails = {
      ...this.surveyDetails,
      ...this._modifyTemplateQualifications(template),
      ...this._transformTemplateData(template),
    } as any;
    const nestedQuotas: NestedQuota[] = [];
    this._quotaPayloadService.setNestedQuotas(nestedQuotas);
  }

  private _patchTemplateLocale(surveyLocalization: string = '') {
    const [language, shortCode] = surveyLocalization.split('_');

    const selectedCountry = this.countries.find(
      (country) => country.short_Code === shortCode
    );
    this.form.patchValue({ selectedCountries: [selectedCountry] });

    if (selectedCountry) {
      this.languages = selectedCountry.lang;
      const selectedLanguage = this.languages.find(
        (lang) => lang.transalte_code === language
      );
      this.form.patchValue({ selectedLanguages: [selectedLanguage] });
    }
  }

  private _modifyTemplateQualifications(template: Template) {
    template.qualifications = (template.qualifications ?? []).map((q) => {
      q.data = (q.condition_codes ?? []).map((code) => {
        return {
          id: code,
          name: this._getDatumName(code, q.condition_names),
        };
      }) as Datum[];
      return q;
    });
    return template;
  }

  private _getDatumName(
    code: string,
    condition_names: Record<string, string>[] = []
  ) {
    const condition = condition_names.find((name) => {
      return name[code];
    });
    return condition ? condition[code] : '';
  }

  private _transformTemplateData(template: Template) {
    const {
      survey_title,
      completes_required,
      expected_ir,
      expected_loi,
      field_time,
      survey_category_code,
      survey_localization,
      click_balancing,
      estimated_clicks,
      is_feot_enabled,
      schedule_launch,
      soft_launch,
      offer_price = 0,
      margin = Constants.AVERAGE_VARIABLE_MARGIN,
    } = template;
    let waspPayload = {};
    if (this.enableMarginForOperatorBuyer) {
      waspPayload = {
        price_override: offer_price,
        margin_override: {
          margin,
        },
      };
      this.form.patchValue({
        buyerCpiOverride: offer_price,
        buyerMargin: margin,
      });
      this.marginValue = margin;
      this.calculateSuppCpiOverride();
    }
    return {
      survey_title,
      completes_required,
      expected_ir,
      expected_loi,
      field_time,
      survey_category_code,
      survey_localization,
      click_balancing,
      estimated_clicks,
      is_feot_enabled,
      schedule_launch,
      soft_launch,
      ...waspPayload,
    };
  }

  selectCountries(countries: CountryInterface[]) {
    if (!countries.length) {
      this.form.patchValue({
        selectedCountries: [],
        selectedLanguages: [],
      });
      this.attributeQualifications = [];
      this._setMultiCountryDataToSetForPredraftStage();
      return;
    }
    // this is event emitter of country dropdown(component `ps-ui-select-dropdown`)  when multi set to TRUE
    this._triggerCountrySelection(countries);
  }

  selectCountry(country: CountryInterface) {
    const countries = [country];
    if (this.isSurveyCreated && !this.isEditMode) {
      this._showDraftSurveyConfirmationModal(countries);
    } else if (
      !this.isInsight ||
      (this.isInsight &&
        (this.countryCode === country?.short_Code || !this.isEditMode))
    ) {
      // this is event emitter of country dropdown(component `ps-ui-select-dropdown`) when multi set to FALSE
      this._triggerCountrySelection(countries);
    }
  }

  get locale() {
    if (!this._selectedCountry() || !this._selectedLanguage()) {
      return '';
    }
    const cntCode = this._getCountryCode();
    const lngCode = this._getLanguageCode('short_code');
    return `${lngCode}_${cntCode}`;
  }

  get selectedCountry() {
    return this._selectedCountry();
  }

  get selectedLanguage() {
    return this._selectedLanguage();
  }

  get selectedCountries() {
    return this._selectedCountries();
  }

  get selectedLanguages() {
    return this._selectedLanguages();
  }

  private _showDraftSurveyConfirmationModal(countries: CountryInterface[]) {
    if (
      this._prevSelectedCountries.find((prev) => prev.id === countries[0].id)
    ) {
      return;
    }
    const modalRef = this._modal.open(DraftSurveyConfirmationModalComponent, {
      data: {
        text: 'You are changing the country, do you want to delete the current survey?',
      },
    });
    modalRef.onClose$.subscribe((msg) => {
      if (msg === 'yes') {
        this._isCountryChanged = true;
        this._deleteDraftSurvey(this.surveyId);
        this._createSurveyForChangedCountry(countries);
      } else if (msg === 'no') {
        this._isCountryChanged = true;
        this._createSurveyForChangedCountry(countries);
      } else if (msg === 'cancel') {
        // Patching the previously selected country in case of 'cancel'
        this.form.patchValue({
          selectedCountries: this._prevSelectedCountries,
        });
        this._prevSelectedCountries = this._selectedCountries();
      }
    });
  }

  private _deleteDraftSurvey(surveyId: number) {
    this._buyerApiService.deleteDraftSurvey(surveyId).subscribe(
      (_) => {
        this._toastr.success(notifyMessage.successMessage.DRAFT_SURVEY_DELETED);
      },
      (err) => {
        const errMsg =
          err && err.error && err.error.msg
            ? err.error.msg
            : notifyMessage.errorMessage.DRAFT_SURVEY_DELETE;
        this._toastr.error(errMsg);
      }
    );
  }

  // Creating new survey with the newly selected country
  private _createSurveyForChangedCountry(countries: CountryInterface[]) {
    this.isSurveyCreated = false;
    this.surveyDetails = {} as BuyerSurveyResponse;
    this._triggerCountrySelection(countries);
  }

  private _triggerCountrySelection(countries: CountryInterface[]) {
    this.form.patchValue({ selectedCountries: countries });
    this._decideActionBasedOnCountryLanguageSelections('country-change');
  }

  private _decideActionBasedOnCountryLanguageSelections(
    scenario: 'language-change' | 'country-change'
  ) {
    const countries = this._selectedCountries();
    const languages = this._selectedLanguages();
    if (countries?.length === 1 && languages?.length <= 1) {
      // single locale selection
      this.selectedCountryCode = countries[0].short_Code;
      this.setLanguageForCountry(countries[0]);
      return;
    }
    this._handleAllLanguagesSelectionScenarios(scenario);
    this._getQualifications();
  }

  private _handleAllLanguagesSelectionScenarios(
    scenario: 'language-change' | 'country-change'
  ): void {
    const countries = this._selectedCountries();
    const languages = this._selectedLanguages();
    // When there are multiple countries selected, then select all languages of a any multi-lingual country
    if (countries.length > 1) {
      this.languages = countries[0].lang;
      this.form.patchValue({ selectedLanguages: this.languages });
      return;
    }
    // When there is previously selected languages list, and the country selection comes back to 1, then select all the languages of that country
    if (
      scenario === 'country-change' &&
      countries.length === 1 &&
      languages.length > 1
    ) {
      this.languages = countries[0].lang;
      this.form.patchValue({ selectedLanguages: this.languages });
    }
  }

  public selectLanguages(languages: LanguageInterface[]) {
    if (!languages.length) {
      this.form.patchValue({ selectedLanguages: [] });
      this._setMultiCountryDataToSetForPredraftStage();
      return;
    }
    this.form.patchValue({ selectedLanguages: languages });
    this.languageCode = languages?.length === 1 ? languages[0].short_code : '';
    this._decideActionBasedOnCountryLanguageSelections('language-change');
  }

  setLanguage(language: LanguageInterface, isChange: boolean = false) {
    if (language) {
      this.selectedLanguageCode = language.transalte_code;
    }
    this.form.patchValue({ selectedLanguages: [language] });
    this.isLanguageChange = isChange;
    this._getQualifications();
    this._fetchSupplierBlends();
  }

  setLanguageForCountry(country: CountryInterface) {
    this.languages = country.lang;
    if (this.languageCode) {
      // if language is selected in draft edit
      const selectedLangs = this.languages.filter(
        (lang) => lang.short_code === this.languageCode
      );
      const languageToSelect = selectedLangs.length
        ? selectedLangs[0]
        : this.languages[0];
      this.setLanguage(languageToSelect);
    } else {
      // by default first language will be selected.
      this.setLanguage(this.languages[0]);
    }
    // change feasibility of changed language
    if (this.isSurveyCreated) {
      this.autoSaveSurveyInDraft();
    }
  }

  clearLocation() {
    const isLocationApplied =
      this.locationQualification &&
      this.locationQualification.data &&
      this.locationQualification?.data.some((item) => {
        if (
          this.locationQualification.db_qualification_code ===
          QUALIFICATION_CODES.ZIPCODE
        ) {
          // on zipcode only check exist
          return !!item;
        } else {
          // in non zipcode we are checking its selected TRUE or not.
          return item.selected;
        }
      });
    if (!isLocationApplied) {
      this._toastr.warning(notifyMessage.warningMessage.NO_LOCATION);
      return;
    }

    if (
      this._showQuotaEditNotAllowedErrorForAida(
        this.locationQualification.quotas,
        this.locationQualification.hasNestedQuotas
      )
    ) {
      this._aidaPayloadService.sendAidaError(
        notifyMessage.errorMessage.AIDA.LIVE_QUOTA_CHANGE_RESTRICTION
      );
      return;
    }

    const modalRef = this._modal.open(ConfirmationModalComponent, {
      data: {
        text: Constants.CONFIRM_MODAL_TEXT.CLEAR_LOCATION_QUOTA,
      },
    });
    modalRef.onClose$.subscribe((msg) => {
      if (msg === 'ok') {
        this._deleteLocationData();
      }
    });
  }

  private _handleSurveyDetails() {
    const surveyDetailsSubscription = this._surveyDetailsCommand$.subscribe(
      async (response) => {
        if (
          response.ps_survey_status === SURVEY_STATUS.DRAFT &&
          this.userType === 'operator' &&
          !this._isServiceBuyerLoggedIn &&
          response.channel_type !== TRAFFIC_CHANNEL.SPECIFIC_AUDIENCE
        ) {
          this._router.navigateByUrl('/dashboard');
          return;
        }
        this.surveyDetails = { ...response };
        this.isMcSurvey = !!this.surveyDetails?.mc_unique_id;
        this.isAntmanEnabled = this.surveyDetails.is_enable_margin_max;
        this.surveyPayload =
          this._surveyPayloadService.mapSurveyDetails(response);
        this.countries = await this._commonService.getCountries().toPromise();
        this.countryCode = response.locale.country_code;
        this.languageCode = response.locale.language_code;
        this.alwaysShowQualCodes = await this.getAlwaysShowQualCodes(response);
        let selectedCountries = this.countries.find(
          (country) => country.short_Code === this.countryCode
        ) as CountryInterface;
        selectedCountries = await this._getDefaultCountry(selectedCountries);
        if (this.allowMultipleCountriesSelection) {
          this.selectCountries([selectedCountries]);
        } else if (selectedCountries) {
          this.selectCountry(selectedCountries);
        }
        this._sharedDataService.setSurveyData(response);
        this._surveyMetaDataService.sendBuyerSurveyData(response);
        if (response.channel_type === TRAFFIC_CHANNEL.SPECIFIC_AUDIENCE) {
          this.isTCSPAEditMode = true;
        }
        this._surveyMetaDataService.setSurveyData({
          surveyId: response.ps_survey_id,
          mcUniqueId: response.mc_unique_id,
          channel_type: response.channel_type,
          activeTrafficChannel: response.is_tc_children_active,
        });

        this.isLiveSurveyEdit =
          response.ps_survey_status !== SURVEY_STATUS.DRAFT;

        const aidaPayload = this.isLiveSurveyEdit
          ? this.surveyPayload.aida
          : DEFAULT.AIDA_PAYLOAD;
        this._aidaPayloadService.sendAidaLiveEditPayload(aidaPayload);
        // disable quota add/delete/census button if QLC is enabled for live edit
        this.isQlcLiveSurvey = this.isLiveSurveyEdit && response.qlc?.enable;
        // populate category
        const category = this._editSurveyServiceOb.populateCategory(
          this.categories,
          response.survey_category_code
        );
        if (
          this.surveyDetails.trafficChannels &&
          this.surveyDetails.trafficChannels.channelSurveys
        ) {
          this.isTCChildSurvey = true;
        }
        this._getSelectedTemplateDetails(response.template_id);
        this.form.patchValue({
          surveyTitle: response.survey_title,
          selectedCategory: category,
          numberOfCompletes: response.completes_required,
          lengthOfSurvey: response.expected_loi,
          incidence: response.expected_ir,
          fieldTime: response.field_time,
          clickBalancing: response.click_balancing,
          buyerCpiOverride: response.price_override,
          buyerMargin: response.margin_override?.margin,
        });
        if (this.disableInputFields) {
          this._disableInitForm();
        }
        this.calculateTotalClicks();
        this.hasQuotasForPurePrice();
        this._buildPurePriceWindowData();
        this._surveyMetaDataService.setSupplierOverrideValue(
          this.surveyDetails.manual_scpi_override_enabled
        );
        if (this.isServiceBuyerOperation) {
          this.manualScpiDisabled = !!response.manual_scpi_override_enabled;
          this._surveyMetaDataService.setSupplierOverrideValue(
            this.manualScpiDisabled
          );
          this.marginValue =
            response.margin_override?.margin ||
            Constants.AVERAGE_VARIABLE_MARGIN;
          const doSurveyUpdate = false;
          this.calculateSuppCpiOverride(true, true, doSurveyUpdate);
        }
      }
    );
    this._subscriptions.add(surveyDetailsSubscription);
  }

  private _getSelectedTemplateDetails(templateId?: number) {
    if (!templateId) {
      this.form.controls.selectedTemplate.patchValue(null);
      this.selectedTemplateResponse = {} as TemplateResponse;
      return;
    }
    this._templateService.getById(templateId).subscribe((response) => {
      this.selectedTemplateResponse = response;
      const selectedTemplate =
        this._templateService.prepareSelectedTemplate(response);
      this.form.controls.selectedTemplate.patchValue(selectedTemplate);
      this.showAllQualifications = false;
      this._filterQualificationsForTemplate();
      if (response.suppliers_data.blend_id) {
        this.checkTemplateBlenders(response.suppliers_data.blend_id);
      }
    });
  }

  public updateTemplate(templateResp: TemplateResponse) {
    this.selectedTemplateResponse = templateResp;
    this._filterQualificationsForTemplate();
  }

  showWarningForDeprecatedQualOnClone() {
    this.surveyDetails?.qualifications.forEach((qualification) => {
      const matchingQualification = this.deprecatedQualifications.find(
        (qual) => qual.qualification_code === qualification.qualification_code
      );
      if (matchingQualification?.deprecated) {
        this.showDeprecatedInfoMessage(
          matchingQualification.orig_qualification_name ??
            matchingQualification.qualification_name
        );
      }
    });
  }

  filterTemplateQualificationAndQuota(
    responseQualificationCodes: number[] = []
  ) {
    if (
      !responseQualificationCodes.length ||
      !this.selectedTemplateResponse ||
      !this.selectedTemplateResponse?.template
    ) {
      return;
    }
    this.selectedTemplateResponse.template.qualifications =
      this.selectedTemplateResponse?.template?.qualifications.filter((qual) =>
        responseQualificationCodes.includes(qual.qualification_code)
      );
    this.selectedTemplateResponse.template.quotas = (
      this.selectedTemplateResponse?.template?.quotas ?? []
    ).filter((quota: any) =>
      responseQualificationCodes.includes(quota.criteria[0].qualification_code)
    );
    this.quotas = this.selectedTemplateResponse.template.quotas;
  }

  showDeprecatedInfoMessage(deprecatedQualName: string) {
    const inProgressToastrConfig = {
      disableTimeOut: true,
    } as IndividualConfig;
    this._toastr.warning(
      `Qualification "${deprecatedQualName}" has been updated / deprecated. Please review available qualifications and edit survey set up.`,
      '',
      inProgressToastrConfig
    );
  }

  private _populateQualificationsAndQuotas() {
    if (!this.selectedTemplateResponse?.template?.id) {
      this.showWarningForDeprecatedQualOnClone();
    }
    // populate advance qualifications and its associated quotas
    this.advanceTargetingQualifications =
      this._editSurveyServiceOb.populateAdvanceQualifications(
        this.surveyDetails.qualifications,
        this.surveyDetails.quotas,
        this.qualifications
      );
    // populate core qualifications and its associated quotas
    this.qualifications = this._editSurveyServiceOb.populateCoreQualifications(
      this.qualifications,
      this.surveyDetails.qualifications,
      this.advanceTargetingQualifications,
      this.surveyDetails.quotas
    );
    //populate location qualification and its associated quotas
    const selectedLocationQualification =
      this._editSurveyServiceOb.populateLocationQualification(
        this.qualifications,
        this.surveyDetails.qualifications,
        this.surveyDetails.quotas
      );
    if (selectedLocationQualification) {
      this._setLocationQualification(selectedLocationQualification);
    } else {
      this.locationQualification = {} as Qualification;
      this._setLocationTabs();
    }
    // populate hasQuotas flag
    this.quotas = this._editSurveyServiceOb.populateQuotas(
      this.surveyDetails.quotas
    );
    this._setUpNestedQuotas();
    this.hasQuotasForPurePrice();
  }

  private _setUpNestedQuotas() {
    const nestedQuotas = this.quotas.filter(
      (quota) => quota.quota_category === QUALIFICATIONS_CATEGORIES.NESTED
    );
    this._quotaPayloadService.setNestedQuotas(nestedQuotas);
    this.hasNestedGroup = this._hasNestedGroup();
    this._assignNestedQuotaFlagToQualifications();
  }

  private _setLocationQualification(qualification: Qualification) {
    // set location tab data
    this.locationQualification = qualification;
    // set active location tab
    this.activeTabIndex = this.visibleLocationTabs.findIndex(
      (tab) => tab.id === this.locationQualification.qualification_code
    );
    this.activeTab = {
      title: this.locationQualification.qualification_name,
      id: this.locationQualification.qualification_code,
      active: true,
      renderType: this.locationQualification.render_type,
    };
  }

  get isSpecificAudienceTcSurvey() {
    return this.surveyDetails?.channel_type === 'SPA';
  }

  setCoreQualification(qualification: Qualification) {
    this._updateCoreQualification(qualification);
    this.autoSaveSurveyInDraft();
  }

  _updateCoreQualification(qualification: Qualification) {
    this._validateQualification(qualification);

    const coreIndex = this.qualifications.findIndex(
      (qual) => qual.qualification_code === qualification.qualification_code
    );

    if (coreIndex >= 0) {
      this.qualifications[coreIndex].data = qualification.data;
    }
  }

  private _validateQualification(qualification: Qualification) {
    const selectedOptions = qualification.data.filter((data) => data.selected);
    if (this.isSurveyCreated && !selectedOptions.length) {
      this.coreQualificationsInvalid[qualification.qualification_name] = true;
    } else {
      this.coreQualificationsInvalid[qualification.qualification_name] = false;
      // set form valid, if still there is some core qualification
      // unselected/invalid _validateCoreQualifications will set invalid again
      this.form.setErrors(null);
    }
    this._validateCoreQualifications();
  }

  private _validateCoreQualifications() {
    // reverse to show last added invalid first
    const invalidQualification = Object.entries(this.coreQualificationsInvalid)
      .reverse()
      .find(([_, isInvalid]) => isInvalid);
    if (invalidQualification) {
      this.form.setErrors({ incorrect: true });
      const [name] = invalidQualification;
      this._toastr.error(
        Constants.CREATE_SURVEY_ERROR_MESSAGE.ENTER_CRITERIA_SELECTION.replace(
          '@qualification',
          name
        )
      );
    }
  }

  setRangeQualification(qualification: Qualification) {
    if (this._validateRange(qualification)) {
      this.autoSaveSurveyInDraft();
    }
  }

  private _validateRange(qualification: Qualification) {
    if (
      qualification.qualification_code === QUALIFICATION_CODES.AGE &&
      Number(qualification.data[0].minValue) <
        Number(qualification.data[0].minPlaceholder)
    ) {
      qualification.data[0].minValue = Number(
        qualification.data[0].minPlaceholder
      );
      this._toastr.error(
        `Invalid minimum ${qualification.qualification_name}. Please enter a value that is greater than or equal to 13 to continue.`
      );
      return false;
    }

    if (
      Number(qualification.data[0].maxValue) &&
      Number(qualification.data[0].maxValue) <
        Number(qualification.data[0].minValue)
    ) {
      qualification.data[0].maxValue = qualification.data[0].maxPlaceholder;
      this._toastr.error(
        `Invalid maximum ${qualification.qualification_name}. Please enter a value that is greater than the minimum ${qualification.qualification_name} to continue.`
      );
      return false;
    }
    if (
      qualification.data[0].maxValue &&
      Number(qualification.data[0].maxValue) == 0
    ) {
      qualification.data[0].maxValue = qualification.data[0].maxPlaceholder;
      this._toastr.error(
        `Invalid maximum ${qualification.qualification_name}. Please enter a value that is greater than the minimum ${qualification.qualification_name} to continue.`
      );
      return false;
    }

    return true;
  }

  private _setFileUploadAddressToMcSpvChildSurveys(
    qualification: Qualification
  ) {
    if (
      this._isMcSpvSurvey() &&
      LOCATION_QUALIFICATION_CODES.includes(qualification.qualification_code)
    ) {
      let zipCodeAddress = {};

      if (QUALIFICATION_CODES.FSA === qualification.qualification_code) {
        zipCodeAddress = {
          fsacode_file_path: qualification.fsacodeFilePath,
        };
      } else {
        zipCodeAddress = {
          zipcode_file_path: qualification.zipcodeFilePath,
        };
      }

      const cache = {
        for: 'each' as const,
        data: [
          {
            survey_localization: this.surveyDetails.survey_localization,
            ...zipCodeAddress,
          },
        ],
      };

      this._multiCountryDraftService.sendMcSpvUpdateInCacheCommand(cache);
    }
  }

  setLocationQualification(qualification: Qualification) {
    /*
    Every time we do an update on this.locationQualification we
    need to update the qualifications itself inside of this.qualifications or
    we are gonna lose this information.
    */
    const isRegionOrDivision = [
      QUALIFICATION_CODES.REGION,
      QUALIFICATION_CODES.DIVISION,
    ].includes(qualification?.qualification_code);
    if (
      ![QUALIFICATION_CODES.ZIPCODE, QUALIFICATION_CODES.FSA].includes(
        qualification?.qualification_code
      )
    ) {
      const sortByFieldName = isRegionOrDivision ? 'text' : 'name';
      sortDataByFieldName(qualification?.data, sortByFieldName);
    }
    this._setFileUploadAddressToMcSpvChildSurveys(qualification);

    if (
      LOCATION_QUALIFICATION_CODES.includes(qualification.qualification_code)
    ) {
      const index = this.qualifications.findIndex(
        (quali) => quali.qualification_code === qualification.qualification_code
      );
      this.qualifications[index] = qualification;
    }

    const { data, quotas } = this.locationQualification;
    this.locationQualification = { ...qualification, data, quotas };
    this.fsacodeFilePath = qualification.fsacodeFilePath;
    this.zipcodeFilePath = qualification.zipcodeFilePath;
    this.autoSaveSurveyInDraft();
  }

  // Using bind as this is being passed as a callback and needs to keep reference to `this`
  public checkSelectedLocations = this._checkSelectedLocations.bind(this);

  /**
   * Check for selected locations and notify the user before changing
   */
  private _checkSelectedLocations(
    currentTab?: number,
    nextTab?: number
  ): Observable<boolean> | boolean {
    const selectedLocations = this.getLocationConditionCodes();

    if (selectedLocations.length && nextTab !== currentTab) {
      // avoid changing location if AT question has dependency on location qualification
      if (this.locationQualification?.isAdvanceTargetingParent) {
        return false;
      }

      if (
        this._showQuotaEditNotAllowedErrorForAida(
          this.locationQualification.quotas,
          this.locationQualification.hasNestedQuotas
        )
      ) {
        this._aidaPayloadService.sendAidaError(
          notifyMessage.errorMessage.AIDA.LIVE_QUOTA_CHANGE_RESTRICTION
        );
        return false;
      }

      const modalRef = this._modal.open(ConfirmationModalComponent, {
        data: {
          text: Constants.CONFIRM_MODAL_TEXT.SWITCH_LOCATION,
        },
      });
      const modalCloseSubs = modalRef.onClose$.pipe(map((msg) => msg === 'ok'));
      modalCloseSubs.subscribe((isYes) => {
        if (isYes) {
          this._deleteLocationData();
        }
      });
      return modalCloseSubs;
    }

    return true;
  }

  private _deleteLocationData() {
    this.deleteLocationQuotas.emit('ok');
    this.clearLocationData.emit('ok');
    if (this.locationQualification?.data) {
      this.locationQualification.data.forEach((quota) => {
        if (quota.selected) {
          quota.selected = false;
        }
      });

      if (
        [QUALIFICATION_CODES.ZIPCODE, QUALIFICATION_CODES.FSA].includes(
          this.locationQualification.qualification_code
        )
      ) {
        this.locationQualification.data = [];
      }
    }
    this.autoSaveSurveyInDraft();
  }

  public setLocation(currentIndex: number, activeTabIndex: number) {
    if (
      currentIndex !== activeTabIndex &&
      this.locationQualification?.data?.length
    ) {
      this.locationQualification = {} as Qualification;
    }
    this.activeTabIndex = currentIndex;
    this.activeTab = this.visibleLocationTabs[currentIndex];
    this.visibleLocationTabs[currentIndex].active = true;
    const qualification = this.qualifications.find(
      (qual) => qual.qualification_name === this.activeTab.title
    );
    if (qualification) {
      this.locationQualification = { ...qualification };
    }
  }

  private _filterQuotasByQualification(
    quotas: QuotaPayload[],
    qualificationCode: number
  ): QuotaPayload[] {
    return quotas.filter((quota) => {
      return !quota.criteria.find(
        (item) => item.qualification_code === qualificationCode
      );
    });
  }

  private _mcSurveysQualifcations$ = new EventEmitter<{
    survey: Partial<BuyerSurveyResponse>;
    response: QuotaResponse;
  }>();

  private _handleMcSurveysQualification() {
    const _mcSpvSurveysQualifcations$ = this._mcSurveysQualifcations$.pipe(
      filter(({ response: { isMc } }) => !!isMc),
      switchMap(({ survey, response }) =>
        from(survey?.mc?.surveys!).pipe(
          mergeMap(({ countryCode, languageCode }) => {
            if (
              this.allMcRelatedQualifications.has(
                `${languageCode}_${countryCode}`
              )
            ) {
              return EMPTY;
            }
            return this._qualificationsService
              .getUIQualifications(countryCode, languageCode)
              .pipe(
                tap((qualifications) =>
                  this.allMcRelatedQualifications.set(
                    `${languageCode}_${countryCode}`,
                    qualifications
                  )
                )
              );
          }),
          toArray(),
          map(() => ({ survey, response }))
        )
      ),
      shareReplay()
    );

    const filterQualificationForSurvey$ = _mcSpvSurveysQualifcations$.pipe(
      switchMap(({ survey: { mc }, response }) =>
        from(mc?.surveys!).pipe(
          mergeMap(({ countryCode, languageCode, surveyId }) =>
            of(
              this.allMcRelatedQualifications.get(
                `${languageCode}_${countryCode}`
              ) ?? []
            ).pipe(
              map((allQualifications) =>
                this._findQualification(
                  allQualifications,
                  response.quotas,
                  response.qualification,
                  countryCode,
                  languageCode,
                  response.msg
                )
              ),
              filter((surveyQualification) => !!surveyQualification),
              map((qualification) => ({
                surveyId,
                qualification,
                appliedQualification: response.qualification,
                response,
              }))
            )
          )
        )
      ),
      shareReplay()
    );

    const _mcSurveysAddQuotaQualifcations$ = filterQualificationForSurvey$.pipe(
      filter(({ response: { msg }, surveyId }) =>
        [
          Constants.QUOTA_MODAL_ACTION.APPLY_QUOTA,
          Constants.QUOTA_MODAL_ACTION.APPLY_NEST_QUOTA,
        ].includes(msg)
      ),
      tap(
        ({ appliedQualification, qualification, surveyId, response }) =>
          surveyId !== this.surveyId &&
          this._mapAddQuotasForMc(
            response,
            surveyId,
            qualification!,
            appliedQualification,
            response.quotas,
            response.msg === Constants.QUOTA_MODAL_ACTION.APPLY_NEST_QUOTA
          )
      ),
      tap(() =>
        this._toastr.success(
          notifyMessage.successMessage.MC_QUOTA.ALL_SURVEY_QUOTA_APPLIED
        )
      )
    );

    const _mcSurveysApplyCensusQualifcations$ =
      filterQualificationForSurvey$.pipe(
        filter(
          ({ response: { msg }, qualification, surveyId }) =>
            msg === Constants.QUOTA_MODAL_ACTION.APPLY_CENSUS &&
            !!qualification?.census.data?.length
        ),
        map(({ surveyId, qualification, appliedQualification, response }) => {
          surveyId !== this.surveyId &&
            this._createQuotasAndQualificationForMCSurveys(
              surveyId,
              qualification!,
              appliedQualification,
              true
            );
          return { response, surveyId };
        }),
        tap(() =>
          this._toastr.success(
            notifyMessage.successMessage.MC_QUOTA.ALL_SURVEY_CENSUS_APPLIED
          )
        )
      );

    const _mcSurveysDeleteQualifcations$ = filterQualificationForSurvey$.pipe(
      filter(
        ({ response: { msg } }) =>
          msg === Constants.QUOTA_MODAL_ACTION.DELETE_QUOTA
      ),
      tap(({ surveyId, appliedQualification, response }) => {
        surveyId !== this.surveyId &&
          this._deleteQuotasFromMcSurveys(surveyId, appliedQualification);
      }),
      tap(() =>
        this._toastr.success(
          notifyMessage.successMessage.MC_QUOTA.ALL_SURVEY_QUOTA_DELETED
        )
      )
    );

    this._subscriptions.add(
      merge(
        _mcSurveysAddQuotaQualifcations$,
        _mcSurveysApplyCensusQualifcations$,
        _mcSurveysDeleteQualifcations$
      )
        .pipe(
          tap(({ surveyId }) => {
            if (!!this.surveyDetails?.mc?.SVP) {
              const {
                survey_localization,
                quotas = [],
                qualifications = [],
              } = this.allMcChildSurveys[surveyId];
              this._sendMcSpvDataUpdateForCurrentLocale(
                survey_localization,
                qualifications,
                quotas
              );
            }
          }),
          filter(
            () =>
              !this.surveyDetails.mc.SVP && !!this.surveyDetails.mc_unique_id
          ),
          tap(() =>
            this._multiCountryDraftService.sendMcTraditionalSurveyRequest(
              this.allMcChildSurveys
            )
          )
        )
        .subscribe()
    );

    const mcSpvSurveys$ =
      this._multiCountryDraftService.getMcSvpUpdatedSurveysResponse();

    const mcTraditionalSurveys$ =
      this._multiCountryDraftService.getMcTraditionalSurveysResponse();

    this._subscriptions.add(
      merge(mcSpvSurveys$, mcTraditionalSurveys$)
        .pipe(
          filter((surveys) => !!surveys.length),
          mergeMap((surveys) =>
            from(surveys).pipe(
              map((survey: any) => ({ surveyId: survey.ps_survey_id, survey })),
              tap(({ surveyId, survey }) => {
                this.allMcChildSurveys[surveyId] = survey;
                if (!this._mcSpvnestedQualificationsIds[surveyId]) {
                  this._mcSpvnestedQualificationsIds[surveyId] = [];
                }
              }),
              map(({ survey }) => survey),
              toArray()
            )
          ),
          filter(() => !!this.surveyDetails?.mc?.SVP),
          map((surveys) => new McSvpParentChildSegregator(surveys).parent())
        )
        .subscribe((survey) => (this.mcSpvParentSurvey = survey))
    );
  }

  private _deleteQuotasFromMcSurveys(
    surveyId: number,
    appliedQualification: Qualification
  ) {
    const {
      completes = 0,
      totalClicks = 0,
      surveyQuotas = [],
    } = this._fetchDataForNestedQuotasBuilding(surveyId);
    const nestedQuotas = surveyQuotas.filter(
      (q) => q.quota_category === QUALIFICATIONS_CATEGORIES.NESTED
    );

    const recreatedNestedQuotas =
      this._recreateNestedQuotasForAllGroupsAfterDeletion(
        nestedQuotas,
        appliedQualification.qualification_code
      );

    const filteredSurveyQuotas = surveyQuotas.filter(
      (q) =>
        !q.criteria.some(
          (criterion) =>
            Number(criterion.qualification_code) ===
            Number(appliedQualification.qualification_code)
        )
    );
    const recreatedQuotas = this._mapQuotasForNestingEdit(
      filteredSurveyQuotas,
      totalClicks,
      completes
    );
    const unNestedQuotas = recreatedQuotas.filter(
      (quota) =>
        !quota.criteria.some((criterion) =>
          new SurveyNestedQuotas(nestedQuotas)
            .qualificationIds()
            .includes(Number(criterion.qualification_code))
        )
    );

    this.allMcChildSurveys[surveyId].quotas = [
      ...unNestedQuotas,
      ...this._buildNestedQuotaPayload(recreatedNestedQuotas),
    ] as Quotas[];
  }

  private _isQuotasConditionCodeAvailable(
    qualification: Qualification,
    quotas: SelectedQuota[]
  ) {
    if (qualification.is_range || this._isGroupQuotaAvailable(quotas)) {
      return true;
    }
    const conditionCodes = qualification.data.map((qd) => qd.id);
    return quotas.every((q) => conditionCodes.includes(q.id));
  }

  private _isQuotasOutOfRange(
    qualification: Qualification,
    quotas: SelectedQuota[],
    isCensus: boolean = false
  ) {
    if (
      !qualification.is_range ||
      !quotas.length ||
      this._isGroupQuotaAvailable(quotas) ||
      isCensus
    ) {
      return false;
    }
    const filteredQuotas = quotas.filter(
      (quota) => quota.min !== null && quota.max !== null
    );
    const rangeStart = filteredQuotas.reduce(
      (min, q) => (Number(q.min) < min ? Number(q.min) : min),
      filteredQuotas[0].min || 0
    );
    const rangeEnd = filteredQuotas.reduce(
      (max, q) => (Number(q.max) > max ? Number(q.max) : max),
      filteredQuotas[0].max || 0
    );
    return (
      rangeStart < qualification.data[0].min ||
      rangeEnd > qualification.data[0].max
    );
  }

  private _findQualification(
    allQualifications: Qualification[],
    appliedQuotas: SelectedQuota[],
    toFind: Qualification,
    countryCode: string,
    languageCode: string,
    applyMsg: string
  ): Qualification | void {
    const qualification = allQualifications.find(
      (q) => q.qualification_code === toFind.qualification_code
    );
    if (
      qualification &&
      [QUALIFICATION_CODES.CHILD_AGE_GENDER].includes(
        qualification.db_qualification_code
      )
    ) {
      return qualification;
    }
    const isCensusApplied =
      applyMsg === Constants.QUOTA_MODAL_ACTION.APPLY_CENSUS;
    if (
      (!qualification ||
        (!qualification.census.data?.length && isCensusApplied) ||
        qualification.data.length !== toFind.data.length ||
        !this._isQuotasConditionCodeAvailable(qualification, appliedQuotas) ||
        this._isQuotasOutOfRange(
          qualification,
          appliedQuotas,
          isCensusApplied
        )) &&
      applyMsg !== Constants.QUOTA_MODAL_ACTION.DELETE_QUOTA
    ) {
      let message =
        notifyMessage.warningMessage.QUALIFICATIONS_NOT_AVAILABLE_FOR_MC;
      if (qualification?.is_range) {
        message = notifyMessage.warningMessage.RANGE_NOT_AVAILABLE_FOR_MC;
      }
      return this._toastr.warning(
        message.replace(
          '<COUNTRY_AND_LANGUAGE>',
          `${countryCode}-${languageCode}`
        )
      );
    }
    return qualification;
  }

  getSurveyForMcQuotaApply() {
    if (!!this.surveyDetails.mc.SVP) {
      return this.mcSpvParentSurvey;
    }
    const survey = { mc: {} as Partial<McSurveyStats> };
    survey.mc.surveys = Object.values(this.allMcChildSurveys).map(
      ({
        ps_survey_id: surveyId,
        locale: { language_translate: languageCode, country_code: countryCode },
      }) => {
        return { surveyId, languageCode, countryCode };
      }
    );
    return survey;
  }

  private _createQuotasAndQualificationForMCSurveys(
    surveyId: number,
    qualification: Qualification,
    appliedQualification: CombinedQualification,
    isCensusApplied: boolean = false,
    surveyQuotas: SelectedQuota[] = [],
    isNesting: boolean = false
  ) {
    let mappedQuotas = surveyQuotas;
    let finalQuotas: Quotas[] = [];

    if (isCensusApplied) {
      const completes = this.allMcChildSurveys[surveyId].click_balancing
        ? this.allMcChildSurveys[surveyId].estimated_clicks
        : this.allMcChildSurveys[surveyId].completes_required;
      mappedQuotas = new ApplyCensusToMcSurveys(
        completes,
        qualification,
        this._quotaModalService
      ).applyCensus()!;
    }

    finalQuotas = isNesting ? (surveyQuotas as unknown as Quotas[]) : [];
    if (!isNesting) {
      finalQuotas = this._createFinalQuotas(
        mappedQuotas,
        qualification
      ) as Quotas[];
    }

    const finalQualification = this._mapMcQualificationsToAppliedQualification(
      qualification,
      appliedQualification,
      isCensusApplied
    );

    const mappedQualification = this._mapQualifications(
      [finalQualification],
      finalQuotas,
      true
    ) as SurveyQualification[];

    this.allMcChildSurveys = new McQuotaQualificationActions(
      this.allMcChildSurveys,
      appliedQualification
    ).updateQuotaQualificationInMcSurveys(
      surveyId,
      qualification,
      finalQuotas,
      mappedQualification
    );

    return Object.values(this.allMcChildSurveys) as BuyerSurveyResponse[];
  }

  private _createFinalQuotas(
    mappedQuotas: SelectedQuota[],
    qualification: Qualification
  ) {
    const finalQuotas: QuotaPayload[] = [];
    mappedQuotas.forEach((quota: NestedQuota | SelectedQuota) => {
      if (quota.number || (quota as NestedQuota).required_count) {
        const finalQuota = this._createQuotasForQualifications(
          qualification,
          quota as SelectedQuota,
          qualification.flexible!
        );
        finalQuotas.push(finalQuota as Quotas);
      }
    });
    return finalQuotas;
  }

  private _mapAddQuotasForMc(
    response: QuotaResponse,
    surveyId: number,
    qualification: Qualification,
    appliedQualification: CombinedQualification,
    appliedQuota: SelectedQuota[],
    isNesting = false
  ) {
    if (
      qualification.data.length !== appliedQualification.data.length &&
      ![QUALIFICATION_CODES.CHILD_AGE_GENDER].includes(
        qualification.db_qualification_code
      )
    ) {
      return;
    }

    const completes = this.allMcChildSurveys[surveyId].click_balancing
      ? this.allMcChildSurveys[surveyId].estimated_clicks
      : this.allMcChildSurveys[surveyId].completes_required;

    let quotas: QuotaPayload[] | SelectedQuota[] =
      this._quotaModalService.reDistributeQuotaQuantites(
        appliedQuota,
        completes,
        response.flexible
      );
    if (isNesting) {
      const finalQuotas: QuotaPayload[] = this._createFinalQuotas(
        quotas,
        qualification
      );

      const surveyQuotas = this.allMcChildSurveys[surveyId].quotas.filter(
        (q) =>
          !q.criteria.some(
            (item) =>
              item.qualification_code === qualification.qualification_code
          )
      );

      const nestedQuota = this._prepareNestedQuotasForMcSurvey(
        response,
        this.allMcRelatedQualifications.get(
          this.allMcChildSurveys[surveyId].survey_localization
        ) || [],
        [...surveyQuotas, ...finalQuotas] as QuotaPayload[],
        surveyId
      );

      if (nestedQuota) {
        qualification.hasNestedQuotas = !!nestedQuota.length;
        quotas = this._buildNestedQuotaPayload(nestedQuota);
      }

      this._mcSpvnestedQualificationsIds[surveyId].push(
        qualification.db_qualification_code
      );

      // Removing any singleton quota if it is already nested
      this.allMcChildSurveys[surveyId].quotas = this.allMcChildSurveys[
        surveyId
      ]?.quotas.filter(
        (q) =>
          !q.criteria.some((item) =>
            this._mcSpvnestedQualificationsIds[surveyId].includes(
              item.qualification_code
            )
          )
      );
    }

    const surveys = this._createQuotasAndQualificationForMCSurveys(
      surveyId,
      qualification,
      appliedQualification,
      false,
      quotas as SelectedQuota[],
      isNesting
    );

    if (this._isMcSpvSurvey()) {
      this._multiCountryDraftService.sendSpvSurveyResponseData(surveys);
    }
  }

  private _mapMcQualificationsToAppliedQualification(
    qualification: Qualification,
    appliedQualification: Qualification,
    isCensusApplied: boolean = false
  ) {
    if (isCensusApplied) {
      qualification.census.data!.forEach((option, index) => {
        if (
          qualification?.data?.[index] &&
          qualification?.data?.[index]?.id === option.condition_code
        ) {
          qualification.data[index].selected = !!option.rep_value;
        }
      });
      return {
        ...appliedQualification,
        data: qualification.data,
        census: qualification.census,
      };
    }

    let data = [...appliedQualification.data];

    if (
      !qualification.is_range &&
      qualification.qualification_code !== QUALIFICATION_CODES.CHILD_AGE_GENDER
    ) {
      const qualificationIds: {
        [id: number]: any;
      } = {};

      appliedQualification.data.forEach((q) => (qualificationIds[q.id] = q));
      data = qualification.data.map((q) => {
        if (Object.keys(qualificationIds).includes(q.id?.toString())) {
          q = qualificationIds[q.id];
        }
        return q;
      });
    }

    return { ...appliedQualification, data };
  }

  private _applyQuotaNesting(response: QuotaResponse) {
    const isNestingAlreadyApplied = this._surveyNestedQuotas.hasQualification(
      response.qualification.qualification_code
    );
    if (isNestingAlreadyApplied) {
      return;
    }

    if (
      this._hasHitTheNestedQuotaLimit(
        this._groupSpecificNestingIds(this._editableNestingGroupId)
      )
    ) {
      this._toastr.warning(
        notifyMessage.warningMessage.QUOTA.MAXIMUM_QUOTAS_TO_NEST.replace(
          ':NESTED_QUOTA_LIMIT',
          `${QuotasConstants.NESTED_QUOTA_LIMIT}`
        )
      );
      return;
    }

    const update: Partial<CombinedQualification> = {
      hasNestedQuotas: true,
    };

    this._patchQualificationsWithUpdate(update, response);
    const groupSpecificNestingIds = [
      ...this._groupSpecificNestingIds(this._editableNestingGroupId),
      response.qualification.qualification_code,
    ];
    const groupSpecificNestedQuotas = this._recreateNestedQuotasForEachGroup(
      this._editableNestingGroupId,
      groupSpecificNestingIds
    );
    const nestedQuotas = [
      ...this._nestedQuotas.filter(
        (q) => q.group_key !== this._editableNestingGroupId
      ),
      ...groupSpecificNestedQuotas,
    ];
    this._syncNestedQuotas(nestedQuotas);
  }

  private _hasHitTheNestedQuotaLimit(qualIds: number[]): boolean {
    return qualIds.length >= QuotasConstants.NESTED_QUOTA_LIMIT;
  }

  private _groupSpecificNestingIds(groupId: string): number[] {
    return this._surveyNestedQuotas.kind(groupId).qualificationIds();
  }

  private _prepareNestedQuotasForMcSurvey(
    response: QuotaResponse,
    qualifications: CombinedQualification[],
    quotaPayloads: QuotaPayload[],
    surveyId: number
  ): NestedQuota[] | undefined {
    if (!surveyId) {
      return;
    }
    const {
      completes = 0,
      totalClicks = 0,
      surveyQuotas = [],
    } = this._fetchDataForNestedQuotasBuilding(surveyId);
    const nestedQuotas = surveyQuotas.filter(
      (q) => q.quota_category === QUALIFICATIONS_CATEGORIES.NESTED
    );
    this._mcSpvnestedQualificationsIds[surveyId] = new SurveyNestedQuotas(
      nestedQuotas
    ).qualificationIds();

    const editableNestingGroupId = this._findEditableNestingGroupId(
      new SurveyNestedQuotas(nestedQuotas).groupIds()
    );

    const isNestingAlreadyApplied = new SurveyNestedQuotas(nestedQuotas)
      .qualificationIds()
      .includes(response.qualification.qualification_code);

    if (isNestingAlreadyApplied) {
      return;
    }

    const currentNestedQualificationIds = new SurveyNestedQuotas(nestedQuotas)
      .kind(editableNestingGroupId)
      .qualificationIds();

    if (this._hasHitTheNestedQuotaLimit(currentNestedQualificationIds)) {
      this._toastr.warning(
        notifyMessage.warningMessage.QUOTA.MAXIMUM_QUOTAS_TO_NEST.replace(
          ':NESTED_QUOTA_LIMIT',
          `${QuotasConstants.NESTED_QUOTA_LIMIT}`
        )
      );
      return;
    }

    const allocation = totalClicks || completes;
    const quotas = this._mapQuotasForNestingEdit(
      quotaPayloads,
      totalClicks,
      completes
    );
    const nestingQualifications = new NestedQualificationBuilder(
      qualifications,
      quotas,
      [
        ...currentNestedQualificationIds,
        response.qualification.qualification_code,
      ]
    ).build();
    const selectedQualifications = nestingQualifications.filter(
      (q) => q.isChecked
    );
    return new NestedQuotaBuilder(
      quotas,
      selectedQualifications,
      allocation
    ).build();
  }

  private _calculateTotalClicks(
    completes: number,
    expectedIr: number,
    clickBalancing: boolean = true
  ): number {
    return new SurveyClicksCalculator(
      completes,
      expectedIr,
      clickBalancing
    ).calculate();
  }

  private _isGroupQuotaAvailable(quotas: SelectedQuota[]) {
    return quotas.some((q) => !!q.selectedForGroup);
  }

  private _mapForGroupQuotas(
    quotas: SelectedQuota[],
    qualification: Qualification
  ) {
    if (!this._isGroupQuotaAvailable(quotas)) {
      return quotas;
    }

    quotas.forEach((quota) => {
      if (
        quota.selectedForGroup &&
        quota.options?.length !== quota.oldQuotasText
      ) {
        const options: string[] = [];
        quota.oldQuotasText?.forEach((text) => {
          const qualificationData = qualification.data.find(
            (d) => d.text?.toLowerCase().trim() === text.toLowerCase().trim()
          );
          if (qualificationData) {
            options.push(qualificationData.id?.toString());
          }
        });
        quota.options = options;
      }
    });
    return quotas;
  }

  _mapQualificationIdToQuotas(
    qualification: Qualification,
    quotas: SelectedQuota[]
  ) {
    const quotaWithIds: SelectedQuota[] = [];
    qualification.data.forEach((qd) => {
      const quota = quotas.find(
        (q) =>
          (qd.text ?? qd.name)?.toLowerCase()?.toLowerCase() ===
          q.text?.toLowerCase()
      );
      if (quota && qd.id) {
        quota.id = Number(qd.id);
        quotaWithIds.push(quota);
      }
    });
    return quotaWithIds;
  }

  private _mapQuotasIdsWithQuotalificationData(
    quotas: SelectedQuota[],
    qualification: Qualification
  ) {
    if (this._isGroupQuotaAvailable(quotas)) {
      return this._mapForGroupQuotas(quotas, qualification);
    }

    if (
      !qualification.is_range &&
      qualification.qualification_code !== QUALIFICATION_CODES.CHILD_AGE_GENDER
    ) {
      return this._mapQualificationIdToQuotas(qualification, quotas);
    }

    return quotas;
  }

  setQuotas(response: QuotaResponse) {
    const isLocationQualification =
      response.qualification?.qualification_code ===
      this.locationQualification?.qualification_code;
    if (isLocationQualification) {
      sortDataByFieldName(response.quotas, 'text');
    }
    if (!!response.isMc && this.isSurveyCreated) {
      response.quotas = this._mapQuotasIdsWithQuotalificationData(
        response.quotas,
        response.qualification
      );
      this._mcSurveysQualifcations$.next({
        survey: this.getSurveyForMcQuotaApply(),
        response,
      });
    }

    if (response.msg !== 'deleteQuota') {
      // This filtering needs to be looked at. Because it should only run in case of 'Apply' and 'Apply & Nest' cases (if I am not wrong).
      // But right now, it is even running in case of completes change scenario. For non-nested quotas it is okay (because they ultimately get replaced) but for nested quotas this creates problem.
      const isNestedQuotaCompleteChangeScenario =
        response.msg === Constants.QUOTA_MODAL_ACTION.APPLY_QUOTA &&
        !response.quotas.length &&
        response.qualification.hasNestedQuotas;
      if (!isNestedQuotaCompleteChangeScenario) {
        this.quotas = this._filterQuotasByQualification(
          this.quotas,
          response.qualification.qualification_code
        );
      }

      const mappedQuotas: QuotaPayload[] = [];
      response.quotas.forEach((quota: SelectedQuota) => {
        if (quota.number) {
          const mappedQuota = this._createQuotasForQualifications(
            response.qualification,
            quota,
            response.flexible
          );
          mappedQuotas.push(mappedQuota);
        }
      });

      this.quotas = [...this._buildSurveyQuotaPayload(), ...mappedQuotas];

      if (response.msg === Constants.QUOTA_MODAL_ACTION.APPLY_NEST_QUOTA) {
        this._applyQuotaNesting(response);
      }

      if (
        response.hasOwnProperty('isMc') &&
        !response.isMc &&
        this.isMcSurvey &&
        [
          Constants.QUOTA_MODAL_ACTION.APPLY_CENSUS,
          Constants.QUOTA_MODAL_ACTION.APPLY_QUOTA,
        ].includes(response.msg)
      ) {
        const msg =
          response.msg === Constants.QUOTA_MODAL_ACTION.APPLY_CENSUS
            ? notifyMessage.successMessage.SINGLE_QUOTA.CENSUS_APPLIED
            : notifyMessage.successMessage.SINGLE_QUOTA.QUOTA_APPLIED;
        this._toastr.success(msg);
      }
    } else {
      this._deleteQuotas(response);
    }
    if (isLocationQualification) {
      // updating location qualification
      this._setAutoNestedLocationQual(this.qualifications);
    }
    this.hasQuotasForPurePrice();
    this.autoSaveSurveyInDraft(true, false);
  }

  private _deleteQuotas(response: QuotaResponse) {
    const matchingQualification = this._combinedQualifications.find((q) =>
      [Number(q.db_qualification_code), Number(q.qualification_code)].includes(
        Number(response.qualification.db_qualification_code)
      )
    );
    if (!matchingQualification) {
      return;
    }

    if (matchingQualification.hasNestedQuotas) {
      this._deleteQualificationsFromNestedQuotas(response);
    }

    this.quotas = this._filterQuotasByQualification(
      this.quotas,
      response.qualification.qualification_code
    );
    this._showSuccessMessageOnSingleMcQuotaDeletion(response);
  }

  private _deleteQualificationsFromNestedQuotas(response: QuotaResponse) {
    const update: Partial<CombinedQualification> = {
      hasNestedQuotas: false,
    };
    this._patchQualificationsWithUpdate(update, response);

    const nestedQuotas = this._recreateNestedQuotasForAllGroupsAfterDeletion(
      this._surveyNestedQuotas.get(),
      response.qualification.qualification_code
    );
    this._syncNestedQuotas(nestedQuotas);
  }

  private _recreateNestedQuotasForAllGroupsAfterDeletion(
    nestedQuotas: NestedQuota[],
    qualificationToRemove: number
  ): NestedQuota[] {
    return new SurveyNestedQuotas(nestedQuotas)
      .groupIds()
      .map((groupId) => {
        let groupSpecificNestingIds = new SurveyNestedQuotas(nestedQuotas)
          .kind(groupId)
          .qualificationIds();
        if (!groupSpecificNestingIds.includes(Number(qualificationToRemove))) {
          return new SurveyNestedQuotas(nestedQuotas).kind(groupId).get();
        }

        groupSpecificNestingIds = groupSpecificNestingIds.filter(
          (qId) => qId !== Number(qualificationToRemove)
        );
        return this._recreateNestedQuotasForEachGroup(
          groupId,
          groupSpecificNestingIds
        );
      })
      .flat();
  }

  private _patchQualificationsWithUpdate(
    update: Partial<CombinedQualification>,
    response: QuotaResponse
  ) {
    this.qualifications = this.qualifications.map(
      (q) =>
        new SurveyQualificationUpdater()
          .normal(q)
          .update(
            response.qualification.db_qualification_code,
            update
          ) as Qualification
    );
    this.advanceTargetingQualifications =
      this.advanceTargetingQualifications.map(
        (q) =>
          new SurveyQualificationUpdater()
            .advanced(q)
            .update(
              response.qualification.qualification_code,
              update
            ) as AdvanceTargetingQualification
      );
    this.locationQualification = new SurveyQualificationUpdater()
      .normal(this.locationQualification)
      .update(
        response.qualification.db_qualification_code,
        update
      ) as Qualification;
    this.visibleQualifications = this._visibleQualifications();
  }

  private _recreateNestedQuotasForEachGroup(
    groupId: string,
    groupSpecificNestingIds: number[]
  ): NestedQuota[] {
    if (!groupSpecificNestingIds || !groupSpecificNestingIds.length) {
      return [];
    }

    const {
      totalClicks = 0,
      completes = 0,
      surveyQuotas = [],
    } = this._fetchDataForNestedQuotasBuilding();
    const allocation = totalClicks || completes;
    const recreatedQuotas = this._mapQuotasForNestingEdit(surveyQuotas);
    const nestingQualifications = new NestedQualificationBuilder(
      this._combinedQualifications,
      recreatedQuotas,
      groupSpecificNestingIds
    ).build();
    const selectedQualifications = nestingQualifications.filter(
      (q) => q.isChecked
    );
    return new NestedQuotaBuilder(
      recreatedQuotas,
      selectedQualifications,
      allocation,
      groupId
    ).build();
  }

  private _fetchDataForNestedQuotasBuilding(mcSurveyId?: number) {
    if (mcSurveyId) {
      const {
        completes_required,
        expected_ir,
        click_balancing,
        quotas = [],
      } = this.allMcChildSurveys[mcSurveyId];
      return {
        completes: completes_required,
        totalClicks: this._calculateTotalClicks(
          completes_required,
          expected_ir,
          !!click_balancing
        ),
        surveyQuotas: (quotas || []) as QuotaPayload[],
      };
    }
    return {
      totalClicks: this.totalClicks,
      completes: this.form.value.numberOfCompletes,
      surveyQuotas: this.quotas,
    };
  }

  private _showSuccessMessageOnSingleMcQuotaDeletion(response: QuotaResponse) {
    if (response.hasOwnProperty('isMc') && !response.isMc && this.isMcSurvey) {
      this._toastr.success(
        notifyMessage.successMessage.SINGLE_QUOTA.QUOTA_DELETED
      );
    }
  }

  private _setAutoNestedLocationQual(qualifications: Qualification[]): void {
    const sortByFieldName = this.locationQualification?.data?.some(
      (a) => a.eng_text
    )
      ? 'eng_text'
      : 'text';
    sortDataByFieldName(this.locationQualification?.data, sortByFieldName);
    const locationQualification = qualifications.find(
      (qual) =>
        qual.qualification_code ===
        this.locationQualification.qualification_code
    );
    if (locationQualification) {
      this.setLocationQualification(locationQualification);
    }
  }

  private _createQuotasForQualifications(
    qualification: CombinedQualification,
    quota: SelectedQuota,
    flexible: Flexible
  ) {
    const isAdvanceQualification =
      qualification && qualification.hasOwnProperty('hasDependency');
    let appliedQuota: QuotaPayload = {
      buyer_quota_id:
        quota?.buyer_quota_id || this._commonService.getRandomString(),
      required_count: Number(quota.number),
      flexible: flexible,
      group_name: quota?.group_name,
      criteria: !isAdvanceQualification
        ? this._mapQuotaCriteria(qualification, quota)
        : this._mapQuotaCriteriaForAdvanceQual(qualification, quota),
      maximum: quota.maximum,
      minimum: quota.minimum,
      percentage: quota.percentage,
      ...(quota.suppliers && quota.suppliers.length
        ? { suppliers: quota.suppliers }
        : {}),
    };

    appliedQuota.criteria = this._saveOriginalQuotaValue(
      appliedQuota.criteria,
      quota,
      flexible
    );

    if (!isAdvanceQualification) {
      return appliedQuota;
    }

    appliedQuota = {
      ...appliedQuota,
      quota_category: QUALIFICATIONS_CATEGORIES.ADVANCE,
    };
    return appliedQuota;
  }

  private _saveOriginalQuotaValue(
    criteria: Criteria[],
    quota: SelectedQuota,
    flexible?: Flexible
  ): Criteria[] {
    const { enable, value } = flexible ? flexible : { enable: true, value: 0 }; // setting default value if flexible is undefined
    return criteria.map((element) => ({
      ...element,
      original_maximum: +quota.maximum,
      original_minimum: +quota.minimum,
      original_percentage: +quota.percentage,
      original_required_count: +quota.number,
      original_number: +quota.number,
      original_flex: { enable, value: +value },
    }));
  }

  private _mapQuotaCriteria(
    qualification: Qualification,
    quota: SelectedQuota
  ) {
    switch (qualification.render_type) {
      case this.QUALIFICATIONS_RENDER_TYPE.RANGE:
        return this._mapRangeCriteria(qualification, quota);
      case this.QUALIFICATIONS_RENDER_TYPE.MULTIPUNCH_DROPDOWN:
        return this._mapConditionCodeCriteria(qualification, quota);
      case this.QUALIFICATIONS_RENDER_TYPE.MULTIPUNCH_LIST:
        return this._mapConditionCodeCriteria(qualification, quota);
      case this.QUALIFICATIONS_RENDER_TYPE.FILE_UPLOAD:
        return this._mapConditionCodeCriteriaForFileUpload(
          qualification,
          quota
        );
      case this.QUALIFICATIONS_RENDER_TYPE.SEARCH:
        return this._mapConditionCodeCriteria(qualification, quota);
      default:
        return [];
    }
  }

  private _mapQuotaCriteriaForAdvanceQual(
    qualification: CombinedQualification,
    quota: SelectedQuota
  ) {
    const advanceQuotaCriteria = this._mapQuotaCriteria(qualification, quota);
    if (!qualification.hasDependency || !qualification.dependency) {
      return advanceQuotaCriteria;
    }
    const parentQuotaCriteriaObj =
      this._mapQuotaCriteriaForDependentParent(qualification);
    if (parentQuotaCriteriaObj) {
      return [...advanceQuotaCriteria, parentQuotaCriteriaObj];
    }

    return advanceQuotaCriteria;
  }

  private _mapQuotaCriteriaForDependentParent(
    childQualification: CombinedQualification
  ) {
    const parentQualIndex = this.qualifications.findIndex(
      (coreQualification) =>
        coreQualification.db_qualification_code ===
        childQualification.dependency?.parentQualId
    );

    // When parent qualification is core qualfication
    if (parentQualIndex !== -1) {
      const condition_codes: string[] = [];
      if (childQualification.dependency?.matchCriteria === 'in') {
        this.qualifications[parentQualIndex].data.forEach((item) => {
          if (childQualification.dependency?.condition.includes(item.id)) {
            condition_codes.push(`${item.id}`);
          }
        });
      } else if (childQualification.dependency?.matchCriteria === 'nin') {
        this.qualifications[parentQualIndex].data.forEach((item) => {
          if (!childQualification.dependency?.condition.includes(item.id)) {
            condition_codes.push(`${item.id}`);
          }
        });
      }

      return {
        qualification_name:
          this.qualifications[parentQualIndex].qualification_name,
        qualification_code:
          this.qualifications[parentQualIndex].qualification_code,
        condition_codes: condition_codes,
      };
    } else {
      // When parent qualification is advance qualification as well
      const parentAdvanceQual = this.advanceTargetingQualifications.find(
        (advanceQualification) =>
          advanceQualification.qualification_code ===
          childQualification.dependency?.parentQualId
      );
      if (!parentAdvanceQual) {
        return null;
      }
      return {
        qualification_name: parentAdvanceQual.qualification_name,
        qualification_code: parentAdvanceQual.qualification_code,
        condition_codes: parentAdvanceQual.selectedAnswers.map(
          (code) => `${code.id}`
        ),
      };
    }
  }

  private _mapConditionCodeCriteria(
    qualification: Qualification,
    quota: SelectedQuota
  ): Criteria[] {
    if (qualification.qualification_code === QUALIFICATION_CODES.CHILDREN) {
      return this._mapChildrenCriteria(qualification, quota);
    } else if (
      qualification.qualification_code === QUALIFICATION_CODES.CHILD_AGE_GENDER
    ) {
      return this._mapChildAgeGenderCriteria(qualification, quota);
    } else {
      return this._mapDefaultConditionCodeCriteria(qualification, quota);
    }
  }

  private _mapChildrenCriteria(
    qualification: Qualification,
    quota: SelectedQuota
  ): Criteria[] {
    if (quota.text === 'No Children') {
      let conditionObj = this._getConditionCode(qualification, quota);
      return [
        {
          qualification_name: qualification.qualification_name,
          qualification_code: qualification.qualification_code,
          condition_names: conditionObj.text
            ? [conditionObj.text]
            : conditionObj.codenames,
          condition_codes: conditionObj.id
            ? [`${conditionObj.id}`]
            : conditionObj.quotaIds,
        },
      ];
    } else {
      let conditionObj = this._getConditionCode(qualification, quota);
      return [
        {
          qualification_name: qualification.qualification_name,
          qualification_code: qualification.qualification_code,
          condition_names: conditionObj.text
            ? [conditionObj.text]
            : conditionObj.codenames,
          condition_codes: conditionObj.id
            ? [`${conditionObj.id}`]
            : conditionObj.quotaIds,
        },
        {
          qualification_name: qualification.qualification_name,
          qualification_code: QUALIFICATION_CODES.CHILD_GENDER,
          condition_names: [quota.gender],
          condition_codes:
            quota.gender === Constants.CHILD_GENDER.BOTH
              ? [Constants.CHILD_GENDER.BOY, Constants.CHILD_GENDER.GIRL]
              : [quota.gender],
        },
        {
          qualification_name: qualification.qualification_name,
          qualification_code: QUALIFICATION_CODES.CHILD_AGE,
          range_sets: [
            {
              from: quota.min,
              to: quota.max,
              units: quota.ageunit,
            },
          ],
        },
      ];
    }
  }

  private _mapChildAgeGenderCriteria(
    qualification: Qualification,
    quota: SelectedQuota
  ): Criteria[] {
    const childAgeGenderCriteria: Criteria = {
      qualification_name: qualification.qualification_name,
      qualification_code: QUALIFICATION_CODES.CHILD_AGE_GENDER,
      child_age_unit: quota.ageunit,
      condition_names: [],
      condition_codes: [],
    };
    if (quota.text === 'No Children') {
      const noChildren = Child.createNoChildren();
      childAgeGenderCriteria.condition_codes = [noChildren.code.toString()];
      childAgeGenderCriteria.condition_names =
        this._getChildAgeGenderConditionNames(
          childAgeGenderCriteria.condition_codes
        );
      return [childAgeGenderCriteria];
    } else if (
      typeof quota.min === 'undefined' ||
      typeof quota.max === 'undefined'
    ) {
      throw new Error(notifyMessage.errorMessage.INVALID_CHILDREN_AGES);
    }
    const { gender, min: fromAge, max: toAge } = quota;
    const isBaby = quota.ageunit === Constants.UNITS.AGE.MONTH;
    if (!gender) {
      throw new Error('Child gender age quota gender is not defined');
    }
    const children = Children.create(isBaby, gender, fromAge, toAge);
    childAgeGenderCriteria.condition_codes = children
      .toEachChild()
      .map(({ code }) => code.toString());
    childAgeGenderCriteria.condition_names =
      this._getChildAgeGenderConditionNames(
        childAgeGenderCriteria.condition_codes
      );
    return [childAgeGenderCriteria];
  }

  private _getChildAgeGenderConditionNames(conditionCodes: string[]): string[] {
    if (!this._childAgeGenderQualificationData) {
      return [];
    }
    return this._childAgeGenderQualificationData
      .filter(({ id }) => conditionCodes.includes(id.toString()))
      .map(({ text }) => text);
  }

  private _mapDefaultConditionCodeCriteria(
    qualification: Qualification,
    quota: SelectedQuota
  ): Criteria[] {
    let conditionObj = this._getConditionCode(qualification, quota);
    const condition_name = conditionObj.text
      ? [conditionObj.text]
      : conditionObj.codenames;
    let condition_codes = conditionObj.id
      ? [`${conditionObj.id}`]
      : [conditionObj.text];
    if (conditionObj.quotaIds) {
      condition_codes = conditionObj.quotaIds;
    }
    return [
      {
        qualification_name: qualification.qualification_name,
        qualification_code: qualification.qualification_code,
        condition_names: condition_name,
        condition_codes: condition_codes,
      },
    ];
  }

  private _mapConditionCodeCriteriaForFileUpload(
    qualification: Qualification,
    quota: SelectedQuota
  ) {
    return [
      {
        qualification_name: qualification.qualification_name,
        qualification_code: qualification.qualification_code,
        condition_codes: this._getConditionCodeForFileUpload(quota),
        group_name: quota.group_name,
      },
    ];
  }

  private _getConditionCodeForFileUpload(quota: SelectedQuota) {
    return quota.options?.length ? quota.options : [`${quota.text}`];
  }

  private _getConditionCode(
    qualification: Qualification,
    quota: SelectedQuota
  ) {
    const quotaOptionArray = quota.oldQuotasText;
    const quotaOptionCodes = quota.options;
    if (!quotaOptionArray?.length) {
      const [option] = qualification.data.filter(
        (data) =>
          (data.text ?? data.name)?.toLowerCase()?.trim() ===
            quota.text?.toLowerCase()?.trim() || data.id === quota.id
      );
      return option;
    } else {
      return this._getConditionCodeForGroupedQuota(
        quotaOptionArray,
        quotaOptionCodes,
        qualification
      );
    }
  }

  private _getConditionCodeForGroupedQuota(
    quotaOptionArray: string[] | undefined,
    quotaOptionCodes: String[] | undefined,
    qualification: Qualification
  ) {
    let quotaIds: string[] = [];
    let codenames: string[] = [];
    quotaOptionArray?.forEach((quotaOption: string, index: number) => {
      const matchedQuota = qualification.data.find(
        (data) =>
          (data.text ?? data.name).toLowerCase() ===
            quotaOption.toLowerCase() ||
          quotaOptionCodes?.[index] === data?.id?.toString()
      );
      if (matchedQuota) {
        quotaIds.push(matchedQuota.id.toString());
        codenames.push(matchedQuota.text);
      }
    });
    return { quotaIds, codenames };
  }

  private _mapRangeCriteria(
    qualification: Qualification,
    quota: SelectedQuota
  ) {
    return [
      {
        qualification_code: qualification.qualification_code,
        qualification_name: qualification.qualification_name,
        range_sets: [
          {
            from: Number(quota.min),
            to: Number(quota.max),
            units: this._commonService.getUnit(
              qualification.qualification_code
            ),
          },
        ],
      },
    ];
  }

  private _deleteQuotasOfAdvancedTargetingQualification(
    qualification: AdvanceTargetingQualification,
    type: string
  ) {
    if (type === Constants.OPERATIONS.PARENT) {
      this._deleteAdvanceQualificationsFromNestedQuotas(
        qualification.dependency?.parentQualId!
      );
      this.quotas = this.quotas.filter(
        (quota) =>
          quota.criteria[0].qualification_code !==
          qualification.dependency?.parentQualId
      );
    } else if (type === Constants.OPERATIONS.CHILD) {
      this._deleteAdvanceQualificationsFromNestedQuotas(
        qualification.qualification_code
      );
      this.quotas = this.quotas.filter(
        (quota) =>
          quota.criteria[0].qualification_code !==
          qualification.qualification_code
      );
    }
  }

  private _deleteAdvanceQualificationsFromNestedQuotas(
    qualificationCode: number
  ) {
    if (!this._surveyNestedQuotas.hasQualification(qualificationCode)) {
      return;
    }
    const qualification = {
      qualification_code: qualificationCode,
    } as AdvanceTargetingQualification;
    const quotas: SelectedQuota[] = [];

    this._deleteQualificationsFromNestedQuotas({
      qualification,
      quotas,
      msg: Constants.QUOTA_MODAL_ACTION.DELETE_QUOTA,
    } as QuotaResponse);
  }

  private _getCategories() {
    const categories = this._commonService.getCategories().subscribe(
      (categorysData) => {
        this.categories = categorysData.sample.values;
        this._verifyTemplateCategory();
      },
      (err) => {
        this._toastr.error(err.error.msg);
      }
    );
    this._subscriptions.add(categories);
  }

  private _getCountries() {
    const countries = this._commonService.getCountries().subscribe(
      async (countriesData) => {
        this.countries = countriesData;
        let firstSelectedCountry = this.countries[0];
        firstSelectedCountry = await this._getDefaultCountry(
          firstSelectedCountry
        );
        if (this.allowMultipleCountriesSelection) {
          this.selectCountries([firstSelectedCountry]);
        } else {
          this.selectCountry(firstSelectedCountry);
        }
      },
      (err) => {
        this._toastr.error(err.error.msg);
      }
    );
    this._subscriptions.add(countries);
  }

  private async _getDefaultCountry(
    firstSelectedCountry: CountryInterface
  ): Promise<CountryInterface> {
    if (!this.surveyDetails?.ps_survey_status) {
      const buyerId =
        (this.isServiceBuyerOperation
          ? this._auth.serviceBuyer?.id
          : this._auth.user?.cmp) || 0;
      const {
        buyer: { default_country: defaultCountryCode },
      } = await this._buyerSettingsService
        .getBuyerSettings(buyerId)
        .toPromise();
      if (defaultCountryCode) {
        const defaultCountry = this.countries.find(
          (country) => country.short_Code === defaultCountryCode
        ) as CountryInterface;
        if (defaultCountry) {
          return defaultCountry;
        }
      }
      return firstSelectedCountry;
    }
    return firstSelectedCountry;
  }

  private _getQualifications() {
    if (!this.isLanguageChange) {
      this.locationQualification = {} as Qualification;
    }

    if (
      this.isLanguageChange &&
      !!this.surveyId &&
      Object.keys(this.locationQualification).length //
    ) {
      this._editSurveyServiceOb.populateLocationQuotas(
        this.quotas as Quotas[],
        this.locationQualification
      );
    }

    let countryCode = this._getCountryCode();
    let languageCode = this._getLanguageCode();
    if (this.form.value.selectedCountries.length > 1) {
      const selectCountry = this.form.value.selectedCountries.slice(-1);
      countryCode = selectCountry[0]?.short_Code;
      languageCode = selectCountry[0].lang[0].transalte_code;
    }
    const qualificationsSub = this._qualificationsService
      .getUIQualifications(
        countryCode,
        languageCode,
        this._isCaseOfMcPreDraft(),
        false,
        this._auth.buyerConfig?.enableExtraCoreQualification
      )
      .subscribe(
        (qualifications: Qualification[]) => {
          this._aidaPayloadService.setQualifications(qualifications);
          if (this._isSetMcAttributeQualification()) {
            qualifications = this._setAttributQualification(
              countryCode,
              languageCode,
              qualifications
            );
          }
          this.deprecatedQualifications = qualifications.filter(
            (qual) => qual.deprecated
          );
          this._filterQualifications(qualifications);
          // populate quota  and qualifications
          if (
            this.surveyDetails &&
            Object.keys(this.surveyDetails).length &&
            !this.isLanguageChange
          ) {
            this._populateQualificationsAndQuotas();
          }
          this._filterQualificationsForTemplate();
          if (
            this.surveyId &&
            this.preQualifications?.length &&
            !this.isTemplateChanged
          ) {
            const surveyQualifications =
              this._editSurveyServiceOb.convertQualificationsIntoSurveyQualifications(
                this.qualifications,
                this.preQualifications,
                this.preQuotas
              );
            this.qualifications =
              this._editSurveyServiceOb.populateCoreQualifications(
                this.qualifications,
                surveyQualifications,
                this.advanceTargetingQualifications,
                this.preQuotas as Quotas[]
              );
            this._lostQualifications =
              this._editSurveyServiceOb.identifyLostQualifications(
                this.qualifications,
                this.preQualifications,
                this.preQuotas
              );
            this.quotas = this._getQuotasWithQualification(this.quotas);
            const nestedQuotas = this._getQuotasWithQualification(
              this._nestedQuotas
            );
            this._quotaPayloadService.setNestedQuotas(nestedQuotas);
            this._handleQuotasOnCountryChange(surveyQualifications);
            this._assignNestedQuotaFlagToQualifications();
          }
          let createSurvey = false;
          if (this._isCountryChanged || this.isTemplateChanged) {
            createSurvey = true;
          }
          this.isLanguageChange = false;
          this.autoSaveSurveyInDraft(false, createSurvey);
        },
        (err) => {
          this._toastr.error(err.error.msg);
        }
      );
    this._subscriptions.add(qualificationsSub);
  }

  private _handleQuotasOnCountryChange(
    surveyQualifications: SurveyQualification[]
  ) {
    for (const qualification of this.qualifications) {
      if (qualification.qualification_code !== GENDER_QUALIFICATION_CODE) {
        continue;
      }

      const quotaConditionCodes = this.quotas
        .filter(
          (q) =>
            q.criteria[0].qualification_code ===
              qualification.qualification_code &&
            q.criteria[0].hasOwnProperty('condition_codes')
        )
        .map((q) => q.criteria[0]?.condition_codes?.[0]);

      const qualificationConditionCodes =
        qualification?.data?.map((q) => q?.id?.toString()) || [];

      const selectedConditionCodes =
        surveyQualifications.find(
          (q) => q.qualification_code === qualification.qualification_code
        )?.condition_codes || [];

      const isDifferentQuotaOrQualification =
        quotaConditionCodes.some(
          (code) => !qualificationConditionCodes.includes(code)
        ) ||
        selectedConditionCodes.some(
          (code: any) => !qualificationConditionCodes.includes(code)
        );

      if (isDifferentQuotaOrQualification) {
        qualification.quotas = [];
        this.quotas = this.quotas.filter(
          (q) =>
            q.criteria[0].qualification_code !==
            qualification.qualification_code
        );

        const isAllQualificationDeselected = !qualification?.data?.every(
          (q) => q?.selected
        );

        if (isAllQualificationDeselected) {
          qualification?.data?.map((q) => (q.selected = true));
        }
        this._toastr.warning(
          notifyMessage.warningMessage.QUOTA_RESET_ON_COUNTRY_CHANGE
        );
      }
    }
  }

  private _getQuotasWithQualification(quotas: QuotaPayload[]) {
    return quotas.filter((quota: QuotaPayload) => {
      return quota.criteria.every((criteria) => {
        if (
          criteria.qualification_code >
          Constants.ADVANCE_TARGETING_QUAL_ID_START_INDEX
        ) {
          return this.advanceTargetingQualifications.some(
            (qualification) =>
              qualification.qualification_code === criteria.qualification_code
          );
        } else {
          return this.qualifications.some(
            (qualification) =>
              qualification.qualification_code === criteria.qualification_code
          );
        }
      });
    });
  }

  private _validateLoi() {
    const formValidator: FormControl = this.form.get(
      'lengthOfSurvey'
    ) as FormControl;
    if (formValidator.value > 60) {
      formValidator.setErrors({ incorrect: true });
      this._toastr.error(
        Constants.CREATE_SURVEY_ERROR_MESSAGE.LENGTH_OF_SURVEY_VALUE
      );
    } else if (formValidator.value === 0) {
      formValidator.setErrors({ incorrect: true });
      this._toastr.error(
        Constants.CREATE_SURVEY_ERROR_MESSAGE
          .LENGTH_OF_SURVEY_VALUE_GREATER_THAN_ZERO
      );
    } else {
      formValidator.setErrors(null);
    }
  }
  private _validateIr() {
    const formValidator: FormControl = this.form.get(
      'incidence'
    ) as FormControl;
    if (formValidator.value === 0) {
      formValidator.setErrors({ incorrect: true });
      this._toastr.error(
        Constants.CREATE_SURVEY_ERROR_MESSAGE.INCIDENCE_VALUE_GREATER_THAN_ZERO
      );
    } else {
      formValidator.setErrors(null);
    }
  }

  private _validateFieldTime() {
    const formValidator: FormControl = this.form.get(
      'fieldTime'
    ) as FormControl;
    const fieldTime: number = formValidator.value;
    if (fieldTime < 1 || fieldTime > 999) {
      formValidator.setErrors({ incorrect: true });
      this._toastr.error(
        Constants.CREATE_SURVEY_ERROR_MESSAGE.INVALID_FIELD_OF_TIME
      );
    } else {
      formValidator.setErrors(null);
    }
  }

  private _validateNumOfCompletes() {
    const formValidator: FormControl = this.form.get(
      'numberOfCompletes'
    ) as FormControl;
    const numberOfCompletes: number = formValidator.value;
    const category = this.form.value.selectedCategory
      ? this.form.value.selectedCategory.id
      : null;

    // If it's not augment survey, the completes cannot be zero
    if (
      category !== Constants.SURVEY_CATEGORY_CODE.AUGMENT &&
      numberOfCompletes === 0
    ) {
      this._toastr.error(
        notifyMessage.errorMessage.COMPLETES_REQUIRED.CANNOT_BE_ZERO
      );
      formValidator.setErrors({ incorrect: true });
      return;
    }

    const fielded = !isNaN(this.surveyDetails?.fielded)
      ? this.surveyDetails?.fielded
      : 0;
    if (
      category !== Constants.SURVEY_CATEGORY_CODE.AUGMENT &&
      numberOfCompletes < Number(fielded)
    ) {
      formValidator.setErrors({ incorrect: true });
      this._toastr.warning(
        notifyMessage.warningMessage.INVALID_NUM_OF_COMPLETES
      );
    } else {
      formValidator.setErrors(null);
    }
  }

  private _validateBuyerCpi() {
    // If form is already invalid, it means that error/warning is already thrown because of other meta data fields
    if (this.form.invalid) {
      return;
    }
    if (!this.enableMarginForOperatorBuyer) {
      return;
    }
    const formValidator: FormControl = this.form.get(
      'buyerCpiOverride'
    ) as FormControl;
    const buyerCpi: number = formValidator.value;
    if (!buyerCpi || buyerCpi <= 0) {
      formValidator.setErrors({ incorrect: true });
      this._toastr.error(
        Constants.CREATE_SURVEY_ERROR_MESSAGE.INVALID_BUYER_CPI
      );
    } else {
      formValidator.setErrors(null);
    }
  }

  // eslint-disable-next-line complexity
  private _validateSurveyCategoryCode() {
    if (
      this.templateEnable ||
      this.isServiceBuyerOperation ||
      this._selectedCountries().length <= 1
    ) {
      return;
    }
    const numberOfCompletes = this.form.value.numberOfCompletes;
    const lengthOfSurvey = this.form.value.lengthOfSurvey;
    const incidence = this.form.value.incidence;
    const fieldTime = this.form.value.fieldTime;
    const formValidator: FormControl = this.form.get(
      'selectedCategory'
    ) as FormControl;
    const selectedCategory = formValidator.value;
    if (
      selectedCategory &&
      numberOfCompletes &&
      lengthOfSurvey &&
      incidence &&
      fieldTime
    ) {
      formValidator.setErrors(null);
    }
    if (
      !selectedCategory &&
      numberOfCompletes &&
      lengthOfSurvey &&
      incidence &&
      fieldTime
    ) {
      formValidator.setErrors({ incorrect: true });
      this._toastr.error(
        Constants.CREATE_SURVEY_ERROR_MESSAGE.REQUIRED_CATEGORY_CODE
      );
    }
  }
  // eslint-disable-next-line complexity
  autoSaveSurveyInDraft(
    dirty: boolean = true,
    createSurvey: boolean = true,
    action?: string
  ) {
    if (this.isServiceBuyerOperation) {
      this.countryCode = this._getCountryCode();
    }
    this._validateSurveyCategoryCode();
    // do not validate again if it's already invalid
    if (this.form.invalid) {
      return;
    }
    if (this.manualProcessing && this.isMcDraftSaved) {
      this._sharedDataService.setIsPageLoaderDisplay(false);
    }
    this._verifyAllocationChange();
    const payload = this._mapPayload(dirty);
    this.mapForSimilarProjectsPayload(payload as unknown as SurveyPayload);
    this._validateNumOfCompletes();
    this._validateLoi();
    this._validateFieldTime();
    this._validateCoreQualifications();
    this._validateBuyerCpiWhenTemplateApplied();
    this._validateBuyerCpi();

    // do not save if is some of validations turned form invalid (and probably shown a toast)
    if (this.form.invalid) {
      return;
    }
    this._prevSelectedCountries = this._selectedCountries();
    this._setMultiCountryDataToSetForPredraftStage(payload);

    // Sending MC-QB data to be saved in cache
    this._transformMcSvpPayloadAndSendToSave();

    if (this._isCaseOfMcPreDraft()) {
      const surveyHeaderData = {
        surveyFieldTime: this.form.value.fieldTime,
      } as SurveyTopBarData;
      this._surveyMetaDataService.patchSurveyHeaderData(surveyHeaderData);
    } else {
      if (!this.isSurveyCreated && createSurvey) {
        this._saveSurvey();
      } else if (this.surveyDetails?.ps_survey_status == SURVEY_STATUS.DRAFT) {
        const surveyMetaDataUpdated =
          this.surveyDetails?.expected_ir != this.form.value.incidence ||
          this.surveyDetails?.expected_loi != this.form.value.lengthOfSurvey
            ? true
            : false;
        this._feasibilityDispatcher.next({
          dirty,
          action,
          surveyMetaDataUpdated,
        });
        const selectedLanguage = this._selectedLanguage()!;
        const localeData = {
          ...this.surveyDetails.locale,
          ...{
            language_code: selectedLanguage.short_code,
            language_name: selectedLanguage.name,
            language_translate: selectedLanguage.transalte_code,
          },
        };
        this._sharedDataService.setLocaleData(localeData);
      }
    }
    this._buildPurePriceWindowData();
  }

  private _setMultiCountryDataToSetForPredraftStage(
    payload = this._mapPayload()
  ) {
    const mcDataToSet =
      this._buildMultiCountryDataToSetForPredraftStage(payload);
    this._createSurveyServiceOb.setMultiCountryData(mcDataToSet);
  }

  private _buildMultiCountryDataToSetForPredraftStage(
    payload = this._mapPayload()
  ): MultiCountryData {
    const isDraft = this._isCaseOfMcPreDraft();
    return {
      selectedCountries:
        this._selectedCountries() as SelectedCountryInterface[],
      selectedLanguages: this._selectedLanguages(),
      expectedIr: this.form.value.incidence,
      clickBalancing: payload.click_balancing,
      completesRequired: payload.completes_required,
      expectedLoi: payload.expected_loi,
      fieldTime: payload.field_time,
      source: payload.source,
      surveyCategoryCode: payload.survey_category_code,
      surveyTitle: payload.survey_title,
      surveyId: this.surveyId,
      qualifications: this.qualifications,
      payloadQualifications: this.payloadQualifications,
      quotas: this._buildSurveyQuotaPayload(),
      offerPrice: this.form.value.buyerCpiOverride,
      buyerMargin: this.form.value.buyerMargin,
      isDraft,
    };
  }

  private _isCaseOfMcPreDraft(): boolean {
    const countries = this._selectedCountries();
    if (countries.length > 1) {
      return true;
    }

    const languages = this._selectedLanguages();
    if (languages.length > 1) {
      return true;
    }

    return false;
  }

  mapForSimilarProjectsPayload(payload: SurveyPayload) {
    if (!this._auth.buyerConfig?.enableSimilarSurveys) {
      return;
    }
    const similarProjectPayload = new SimilarProjectDataMapper(
      payload
    ).mapPayload();
    this._similarProjectsService.setSimilarProjectPayload(
      similarProjectPayload
    );
  }

  private _verifyAllocationChange() {
    if (
      this.form.value.numberOfCompletes != this.surveyPayload.completes_required
    ) {
      this._toastr.warning(notifyMessage.warningMessage.GOAL_CHANGE);
    }
  }

  private _showFeasibilityCpiLoading() {
    this._surveyMetaDataService.sendFeasibilityLoading();
    this._surveyMetaDataService.sendCPILoading();
  }

  private _hideFeasibilityCpiLoading() {
    this._surveyMetaDataService.sendFeasibilityData({ loading: false });
    this._surveyMetaDataService.sendCPIData({ loading: false });
  }

  private _getProcessingPermissionToSave(isAutoSave: boolean) {
    // show feasibility cpi loader on creation, feasibility service will hide it after saving
    // on update show loading it too, to indicate that feasibility and cpi is not calculated yet
    // it will not leave this state until survey is saved
    this._showFeasibilityCpiLoading();
    if (this.manualProcessing && !this.form.valid && !this.isSurveyCreated) {
      this._hideFeasibilityCpiLoading();
    }
    return this._surveyPayloadService.getProcessingPermissionToSave(
      this.manualProcessing,
      isAutoSave,
      this.form.valid,
      this.isSurveyCreated
    );
  }

  filterAdvancedTargetingQualifications(deprecatedQualifications: any[] = []) {
    if (!this.advanceTargetingQualifications?.length) {
      return;
    }
    this.advanceTargetingQualifications =
      this.advanceTargetingQualifications.filter((qualification: any) => {
        const mismatchQualifications = deprecatedQualifications.find(
          (qual: any) =>
            qual.qualification_code === qualification.qualification_code
        );
        return !mismatchQualifications;
      });
  }

  removeDeprecatedQualifications(surveyResponse: BuyerSurveyResponse) {
    const responseQualificationCodes = surveyResponse?.qualifications.map(
      (obj: any) => obj.qualification_code
    );
    if (
      !this.selectedTemplateResponse ||
      !this.selectedTemplateResponse?.template
    ) {
      return;
    }
    const deprecatedQualifications =
      this.selectedTemplateResponse?.template?.qualifications.filter(
        (obj) => !responseQualificationCodes.includes(obj.qualification_code)
      );
    if (deprecatedQualifications.length) {
      deprecatedQualifications.forEach((qual: any) => {
        this.showDeprecatedInfoMessage(
          qual.orig_qualification_name || qual.qualification_name
        );
      });
      if (this.selectedTemplateResponse?.template?.id && !this.isCloneSurvey) {
        this.filterTemplateQualificationAndQuota(responseQualificationCodes);
        this.filterAdvancedTargetingQualifications(deprecatedQualifications);
      }
    }
  }

  private _saveSurvey(isAutoSave: boolean = true) {
    if (!this._getProcessingPermissionToSave(isAutoSave)) {
      return;
    }

    const payload = this._mapPayload();
    const addSurvey = this._buyerApiService.addSurvey(payload).subscribe(
      async (surveyCreatedResponse) => {
        const response = surveyCreatedResponse as BuyerSurveyResponse;
        if (Object.keys(surveyCreatedResponse).length) {
          this._marketplaceService.setNewSurveyDetails(response);
          this.surveyDetails = response;
          this.defaultPurePriceOverride = new PurePriceOverride(
            !!response.default_pure_price_override,
            response.offer_price || 0
          );
          this._surveyMetaDataService.setPurePriceOverride(
            this.defaultPurePriceOverride
          );
          this.removeDeprecatedQualifications(response);
          this.surveyPayload =
            this._surveyPayloadService.mapSurveyDetails(response);
          this._updateSurveyHeaderData(this.surveyDetails, true);
          this._surveyMetaDataService.setPurePriceRateCardEnable(
            this.surveyDetails.is_pure_price_rate_card_enable
          );
          this._surveyMetaDataService.setIsFormDirty(false);
          this.isSurveyCreated = true;
          if (!this.isEditMode) {
            this.isTemplateChanged = false;
          }
          this._sharedDataService.setIsSurveyCreatedTrue(
            this.isSurveyCreated,
            surveyCreatedResponse.ps_survey_id
          );
          this.surveyId = surveyCreatedResponse.ps_survey_id;
          this.allowMultipleCountriesSelection =
            this._decideMultipleCountriesSelection();

          this.alertHighCpiConfirmation(this.surveyId);

          this._churnZeroService.sendEvent(
            ChurnZeroConsts.events.draftSurveyCreated,
            ChurnZeroConsts.eventDescriptions.draftSurveyCreated.replace(
              ':id',
              this.surveyId + ''
            )
          );
          try {
            // Sending survey details for extra call removal in Supplier Edit modal
            this._sharedDataService.setLocaleData(this.surveyDetails.locale);
            this._surveyMetaDataService.sendBuyerSurveyData(this.surveyDetails);
            await this.loadNewTemplateConfiguration(true);
            this._decideAndSetFeasibility(false);
            this._buildPurePriceWindowData();
            if (this._lostQualifications.length) {
              this._toastr.warning(
                notifyMessage.warningMessage.COUNTRY_CHANGE.LOST_QUALIFICATIONS.replace(
                  '<QUALIFICATIONS_NAME>',
                  `${this._lostQualifications.toString()}`
                )
              );
            }
          } catch (e: any) {
            this._toastr.error(e.message);
          }
          if (
            this.isSurveyCreated &&
            this.isEditMode &&
            this.isTemplateChanged
          ) {
            this.isTemplateChanged = false;
            this._location.replaceState(
              `/surveys/${surveyCreatedResponse.ps_survey_id}/edit`
            );
          }
          this._toastr.success(notifyMessage.successMessage.SURVEY_ADDED);
        }
      },
      (err) => {
        if (
          err.error.ps_api_response_message ==
          "Errors are : should have required property 'survey_category_code'"
        ) {
          this._toastr.error(
            Constants.CREATE_SURVEY_ERROR_MESSAGE.REQUIRED_CATEGORY_CODE
          );
        } else {
          this._toastr.error(err.error.ps_api_response_message);
        }
      }
    );
    this._subscriptions.add(addSurvey);
  }

  alertHighCpiConfirmation(surveyId: number) {
    for (
      let index = 0;
      index < this._confirmationHighCpiCalls.length;
      index++
    ) {
      const cpi = this._confirmationHighCpiCalls[index];
      const activityPayload = new HighCpiConfirmationActivityGenerator(
        surveyId,
        cpi
      ).generate();
      this._postActivityLog(activityPayload);
    }
    this._confirmationHighCpiCalls = [];
  }

  private async _fetchSuppliersAndSetTemplateOverride() {
    const templateSuppliersOverride = this._getTemplateSuppliersOverride();
    if (!templateSuppliersOverride.length) {
      return;
    }
    const {
      expected_ir,
      expected_loi,
      field_time,
      locale: { language_code = '' } = {},
    } = this.surveyDetails;
    const suppliers = await this._feasibilityService.getSuppliers(
      this.surveyId,
      expected_ir,
      expected_loi,
      language_code,
      field_time
    );
    const completesNeeded =
      this.surveyDetails.click_balancing === 0
        ? this.surveyDetails.completes_required
        : this.surveyDetails.estimated_clicks;
    const suppliersToUpdate = this._setTemplateSuppliersOverride(
      suppliers,
      templateSuppliersOverride,
      completesNeeded
    );
    const payload = this._mapUpdateSupplierPayload(
      suppliersToUpdate,
      completesNeeded
    );
    await this._editSupplierModalService.updateSupplierList(
      this.surveyId,
      payload
    );
  }

  private _mapUpdateSupplierPayload(
    suppliersToUpdate: SupplierResponseInterface[],
    completesNeeded: number
  ): UpdateSupplierPayloadInterface {
    const totalAllocations = suppliersToUpdate.reduce(
      (total, sup) => total + (sup.quantity || 0),
      0
    );
    const hasGroupSupplier = suppliersToUpdate.some((sup) => sup.group_name);
    const payload = {
      suppliers: suppliersToUpdate,
      flexValue: suppliersToUpdate[0].flexValue,
      isCpiConfirmed: true,
      totalAllocations,
      completesNeeded,
      operatorOverrideMdl: this._getOperatorOverrideForSupplierPayload(),
      manualOverrideMdl: hasGroupSupplier,
      surveyPauseThreshold: 12,
      blend_id: this.blendId,
      defaultPurePriceOverride: this.defaultPurePriceOverride.isActive(),
    };

    return payload;
  }

  // By default, we are keeping global override as 0, because we are not saving it in the template yet
  private _getOperatorOverrideForSupplierPayload() {
    if (this.enableMarginForOperatorBuyer) {
      return this.selectedTemplateResponse?.template.offer_price || 0;
    }
    return 0;
  }

  private _setTemplateSuppliersOverride(
    suppliers: SupplierResponseInterface[],
    templateSuppliersOverride: TemplateSupplierAllocation[],
    completesNeeded: number
  ): SupplierResponseInterface[] {
    const groupSuppliersInTemplate = templateSuppliersOverride.filter(
      (s) => s.group_name
    );
    // Using it to average out the group supplier CPI
    const groupSupplierCpiObj =
      this._calculateGroupSuppliersCpiPresentInTemplate(
        suppliers,
        groupSuppliersInTemplate
      );
    suppliers = this._filterOutSuppliersPresentAsGroupInTemplate(
      suppliers,
      groupSuppliersInTemplate
    );
    suppliers.push(
      ...this._mapSupplierGroupsInTemplate(
        groupSuppliersInTemplate,
        groupSupplierCpiObj,
        completesNeeded
      )
    );
    return suppliers.map((sup) => {
      const templateSupplier = templateSuppliersOverride.find((o) => {
        return (
          !o.group_name &&
          Number(sup.suppliers[0].id) === Number(o.suppliers[0].id)
        );
      });
      const quantity = templateSupplier
        ? templateSupplier.quantity
        : sup.quantity;
      const flexValue = templateSupplier
        ? templateSupplier.flexValue
        : sup.flexValue;
      return <SupplierResponseInterface>{
        ...sup,
        ...(!!templateSupplier && templateSupplier),
        is_active: quantity > 0, // Even when get suppliers API has a supplier inactive by current measures, if template has that supplier active, we should activate them
        max: this._calculateSupplierMax(quantity, flexValue, completesNeeded),
      };
    });
  }

  private _calculateGroupSuppliersCpiPresentInTemplate(
    suppliers: SupplierResponseInterface[],
    groupSuppliersInTemplate: TemplateSupplierAllocation[]
  ) {
    return suppliers.reduce(
      (
        acc: { [key: string]: { cpi: number; count: number } },
        curr: SupplierResponseInterface
      ) => {
        const suppAsGroupInTemplate =
          this._findSupplierPresentAsGroupInTemplate(
            curr,
            groupSuppliersInTemplate
          );
        if (!suppAsGroupInTemplate) {
          return acc;
        }
        const groupName = suppAsGroupInTemplate.group_name;
        if (!acc.hasOwnProperty(groupName)) {
          acc[groupName] = {
            cpi: 0,
            count: 0,
          };
        }
        acc[groupName].count += 1;
        acc[groupName].cpi += curr.cpi ? Number(curr.cpi) : 0;
        return acc;
      },
      {} as { [key: string]: { cpi: number; count: number } }
    );
  }

  private _filterOutSuppliersPresentAsGroupInTemplate(
    suppliers: SupplierResponseInterface[],
    groupSuppliersInTemplate: TemplateSupplierAllocation[]
  ) {
    // Filtering out the suppliers already present in the group suppliers in the template configuration
    return suppliers.filter(
      (sup) =>
        !this._findSupplierPresentAsGroupInTemplate(
          sup,
          groupSuppliersInTemplate
        )
    );
  }

  private _findSupplierPresentAsGroupInTemplate(
    sup: SupplierResponseInterface,
    groupSuppliersInTemplate: TemplateSupplierAllocation[]
  ) {
    return groupSuppliersInTemplate.find((grpSupInTemplate) =>
      grpSupInTemplate.suppliers.some((s) => s.id === sup.suppliers[0].id)
    );
  }

  private _mapSupplierGroupsInTemplate(
    groupSuppliersInTemplate: TemplateSupplierAllocation[],
    groupSupplierCpiObj: { [key: string]: { cpi: number; count: number } },
    completesNeeded: number
  ): SupplierResponseInterface[] {
    const groupNamesInSupplierList = Object.keys(groupSupplierCpiObj);
    return (
      groupSuppliersInTemplate
        // Filtering out groups that are not in the eligible suppliers list
        .filter((grpSupInTemplate) =>
          groupNamesInSupplierList.includes(grpSupInTemplate.group_name)
        )
        .map((grpSupInTemplate) => {
          const { quantity, flexValue, group_name } = grpSupInTemplate;
          // Averaging out the CPI
          const cpi =
            groupSupplierCpiObj[group_name].cpi /
            groupSupplierCpiObj[group_name].count;
          return {
            ...grpSupInTemplate,
            cpi,
            max: this._calculateSupplierMax(
              quantity,
              flexValue,
              completesNeeded
            ),
            is_active: true,
          } as SupplierResponseInterface;
        })
    );
  }

  private _calculateSupplierMax(
    quantity: number,
    flexValue: number,
    completesNeeded: number
  ) {
    if (!flexValue) {
      return quantity;
    }
    if (flexValue >= 100) {
      return completesNeeded;
    }
    const max = quantity + (quantity * flexValue) / 100;
    return Math.floor(Math.min(max, completesNeeded));
  }

  private _getTemplateSuppliersOverride(): TemplateSupplierAllocation[] {
    if (!this.selectedTemplateResponse) {
      return [];
    }
    if (!this.selectedTemplateResponse.is_suppliers_list) {
      return [];
    }
    return (
      this.selectedTemplateResponse.suppliers_data.suppliers_allocation || []
    );
  }

  private _fetchEligibileSuppliers(surveyTopBarData: SurveyTopBarData): void {
    this._feasibilityDataV2Service
      .getEligibleSuppliers(surveyTopBarData)
      .subscribe((data) => {
        this._eligibileSuppliers = data.suppliers;
      });
  }

  private _getPurePricePayload() {
    const currentSurveyId = Number(1);
    const {
      fieldTime,
      lengthOfSurvey,
      incidence,
      numberOfCompletes,
      clickBalancing,
    } = this.form.value;
    const fldTime = fieldTime ?? this.surveyDetails?.field_time;
    const N = numberOfCompletes ?? this.surveyDetails.completes_required;
    const LOI = lengthOfSurvey ?? this.surveyDetails.expected_loi;
    const IR = incidence ?? this.surveyDetails.expected_ir;
    const payloadfeas =
      this._feasibilityDataV2Service.getFeasibilityRequestPayload(
        currentSurveyId,
        this._mapPayload(),
        this.qualifications,
        []
      );
    const quotas =
      this.qualifications?.length && this._mapQualifications().length
        ? payloadfeas.request.quotas
        : [];
    return {
      N,
      gpp: { LOI, IR },
      fldTime,
      clickBalance: clickBalancing ? 1 : 0,
      estmClicks: this.totalClicks ? this.totalClicks : 0,
      quotas,
      ...(this.qualifications?.length && {
        qualifications: this._mapQualifications(),
      }),
    };
  }

  private _fetchPurePriceCpi(isAidaEnabled: boolean) {
    if (isAidaEnabled) {
      const payload = this._getPurePricePayload();
      this._pricingService
        .getPurePriceBuyerCpi(this.surveyDetails.ps_survey_id, payload)
        .subscribe((data) => {
          this._eligibleSuppliersPricingDetails = data;
        });
    }
  }

  private async _fetchPurePriceCpiFeas(): Promise<PurePriceCPI> {
    const payload = this._getPurePricePayload();
    return await this._pricingService
      .getPurePriceBuyerCpi(this.surveyDetails.ps_survey_id, payload)
      .toPromise();
  }

  private _fetchSupplierBlends(): void {
    const locale = this._buildSurveyLocalization();
    this._buyerApiService.getSupplierBlends(locale).subscribe((data) => {
      this._supplierBlends = data;
    });
  }

  private _buildSurveyLocalization() {
    if (!this._selectedLanguage()) {
      return null;
    }
    return `${this._getLanguageCode()}_${this._getCountryCode()}`;
  }

  private async _decideAndSetFeasibility(
    isAutoSave: boolean = true,
    action?: string,
    surveyMetaDataUpdated: boolean = false
  ) {
    if (this.defaultPurePriceOverride.isActive()) {
      const purePrice = await this._fetchPurePriceCpiFeas();
      this.defaultPurePriceOverride = new PurePriceOverride(
        true,
        purePrice.CPI
      );
      this._surveyMetaDataService.setPurePriceOverride(
        this.defaultPurePriceOverride
      );
    }

    if (this._auth.buyerConfig?.enableAdvanceFeasibilityDropDown) {
      this._setAdvanceFeasibility(isAutoSave, action, surveyMetaDataUpdated);
      return;
    }
    this._setFeasibility(isAutoSave, action, surveyMetaDataUpdated);
  }

  private _setAdvanceFeasibility(
    isAutoSave = true,
    action: string = '',
    surveyMetaDataUpdated = false
  ) {
    const shouldSetFeasibility =
      this._surveyPayloadService.getProcessingPermissionToSave(
        this.manualProcessing,
        isAutoSave,
        this.form.valid,
        this.isSurveyCreated
      );
    if (!shouldSetFeasibility) {
      this._showFeasibilityCpiLoading();
      this._createSurveyServiceOb.setIsManualProcessingPermission(false);
      return;
    }

    if (surveyMetaDataUpdated) {
      const locale = {
        country_code: this._getCountryCode(),
        language_code: this._getLanguageCode('short_code'),
      } as unknown as Locale;
      const surveyDetails: BuyerSurveyResponse = {
        ...this.surveyDetails,
        locale,
      };
      this._updateSurveyHeaderData(surveyDetails, surveyMetaDataUpdated);
    }

    if (action === 'update') {
      this._updateSurvey(this._mapPayload());
      return;
    }

    this._sendSurveyQuotasForLocalSaving();
    this._sendFeasibilityRequestMetaData();
  }

  private _sendSurveyQuotasForLocalSaving() {
    const surveyQuotas = this.surveyPayload.quotas || [];
    this._quotaPayloadService.setQuotas(surveyQuotas);
  }

  private _sendFeasibilityRequestMetaData() {
    const quotas = this.surveyPayload.quotas || [];
    const qualifications = this.surveyPayload.qualifications || [];
    const project_id = this.surveyId;
    const payload = this._mapPayload();
    this._surveyMetaDataService.sendFeasibilityRequestMetaData({
      ...payload,
      project_id,
      quotas,
      qualifications,
    });
  }

  private async _setFeasibility(
    isAutoSave: boolean = true,
    action?: string,
    surveyMetaDataUpdated: boolean = false
  ) {
    if (!this._getProcessingPermissionToSave(isAutoSave)) {
      this._createSurveyServiceOb.setIsManualProcessingPermission(false);
      return;
    }

    const payload = this._mapPayload();
    if (action === 'update') {
      this._updateSurvey(payload);
    } else {
      try {
        const languageCode = this._getLanguageCode('short_code');
        // Sending survey details for extra call removal in Supplier Edit modal
        if (surveyMetaDataUpdated) {
          this._updateSurveyHeaderData(
            this.surveyDetails,
            surveyMetaDataUpdated
          );
        }
        this._surveyMetaDataService.sendBuyerSurveyData(this.surveyDetails);
        this._surveyMetaDataService.setSupplierOverrideValue(
          this.manualSCPIOverrideEnabled
        );
        this._surveyMetaDataService.setPurePriceRateCardEnable(
          this.surveyDetails.is_pure_price_rate_card_enable
        );
        this._sendSurveyQuotasForLocalSaving();
        await this.loadNewTemplateConfiguration(
          this.shouldLoadNewTemplateConfiguration
        );
        await this._feasibilityComponentService.setFeasibility(
          this.surveyId,
          languageCode,
          payload,
          this.qualifications,
          !!this.surveyDetails.mc_unique_id,
          true
        );
        this._createSurveyServiceOb.setIsManualProcessingPermission(true);
      } catch (e: any) {
        this._toastr.error(e.message);
      }
    }
  }

  private _updateSurveyHeaderData(
    {
      aida,
      field_time,
      creation_date,
      mod_on,
      ps_survey_status,
      ps_survey_id,
      completes_required,
      cpi,
      pm,
      bpm,
      locale,
      incl_excl,
      currency_symbol,
      buyer_company,
      is_pure_price_rate_card_enable,
    }: BuyerSurveyResponse,
    surveyMetaDataUpdated: boolean
  ) {
    const obj = {
      surveyFieldTime: field_time,
      surveyLastSaved: mod_on ? mod_on : creation_date,
      surveyId: ps_survey_id,
      surveyStatus: ps_survey_status,
      totalClicks: this.totalClicks || 0,
      completes: completes_required,
      cpi: cpi.feasibility_cpi, // always show feasibility_cpi in header
      total_cost: completes_required * cpi.feasibility_cpi,
      pmObj: pm,
      bpmObj: bpm,
      buyerCompany: buyer_company,
      location: {
        countryCode: locale.country_code,
        languageShort: locale.language_code,
      },
      expected_loi: this.form.value.lengthOfSurvey,
      expected_ir: this.form.value.incidence,
      incl_excl,
      currencySymbol: currency_symbol,
      survey_meta_data_updated: surveyMetaDataUpdated,
      is_pure_price_rate_card_enable,
    };
    this._surveyMetaDataService.sendSurveyHeaderData(obj);
  }

  private _updateSurvey(payload: SurveyRequest | SurveyPayload) {
    if (this._isMcSpvSurvey()) {
      this._updateMcSvpSurvey();
      return;
    }
    const updateSurvey = this._buyerApiService
      .updateSurvey(payload as SurveyPayload, this.surveyDetails?.ps_survey_id)
      .subscribe(
        async (surveyCreatedResponse) => {
          const response = surveyCreatedResponse as BuyerSurveyResponse;
          if (Object.keys(surveyCreatedResponse).length) {
            this.surveyDetails = { ...this.surveyDetails, ...response };
            this._surveyMetaDataService.setIsFormDirty(false);
            this._churnZeroService.sendEvent(
              ChurnZeroConsts.events.draftSurveyCreated,
              ChurnZeroConsts.eventDescriptions.draftSurveyCreated.replace(
                ':id',
                this.surveyId + ''
              )
            );
            try {
              // Sending survey details for extra call removal in Supplier Edit modal
              this._surveyMetaDataService.sendBuyerSurveyData(
                this.surveyDetails
              );
              this._buildPurePriceWindowData();
              this._decideAndSetFeasibility();
            } catch (e: any) {
              this._toastr.error(e.message);
            }
          }
        },
        (err) => {
          this._toastr.error(err.error.ps_api_response_message, '', {
            enableHtml: true,
          });
        }
      );
    this._subscriptions.add(updateSurvey);
  }

  private _updateMcSvpSurvey() {
    this._multiCountryService
      .updateSurvey(
        this._mcqbSurveyPayload,
        this.mcSpvParentSurvey?.ps_survey_id
      )
      .subscribe(() => {
        this._surveyMetaDataService.setIsFormDirty(false);
        this._surveyMetaDataService.sendBuyerSurveyData(this.surveyDetails);
        this._buildPurePriceWindowData();
        this._decideAndSetFeasibility();
      });
  }

  private _getCountryCode(): string {
    return this._selectedCountry()?.short_Code!;
  }

  private _getLanguageCode(
    key: 'transalte_code' | 'short_code' = 'transalte_code'
  ): string {
    return this._selectedLanguage()?.[key]!;
  }

  private _selectedCountries(): CountryInterface[] {
    if (!this.form.value.selectedCountries) {
      return [];
    }

    if (!Array.isArray(this.form.value.selectedCountries)) {
      return [this.form.value.selectedCountries];
    }

    return this.form.value.selectedCountries;
  }

  private _selectedLanguages(): LanguageInterface[] {
    if (!this.form.value.selectedLanguages) {
      return [];
    }

    if (!Array.isArray(this.form.value.selectedLanguages)) {
      return [this.form.value.selectedLanguages];
    }

    return this.form.value.selectedLanguages;
  }

  private _selectedCountry(): CountryInterface | undefined {
    return this._selectedCountries()[0];
  }

  private _selectedLanguage(): LanguageInterface | undefined {
    return this._selectedLanguages()[0];
  }

  private _mapPayload(dirty: boolean = true, isMc: boolean = false) {
    const { id: buyer_user_id, cmp: buyer_company } = this._auth.user!;
    let payload = {
      survey_title: this.form.value.surveyTitle,
      click_balancing: this.form.value.clickBalancing ? 1 : 0,
      estimated_clicks: this.totalClicks ? this.totalClicks : 0,
      // by default category 232 for service operator buyer
      survey_category_code: this.isServiceBuyerOperation
        ? Constants.SURVEY_CATEGORY_CODE.EXCITING_NEW
        : this.form.value.selectedCategory?.id,
      survey_localization: `${this._getLanguageCode()}_${this._getCountryCode()}`,
      completes_required: this.form.value.numberOfCompletes,
      expected_ir: this.form.value.incidence,
      expected_loi: this.form.value.lengthOfSurvey,
      field_time: this.form.value.fieldTime,
      source: this.isInsight ? 'insights' : 'platform',
      buyer_user_id,
      fsacodeFilePath: this.fsacodeFilePath,
      zipcodeFilePath: this.zipcodeFilePath,
      price_reco: this.surveyPayload.price_reco,
      qbp: this.surveyPayload.qbp,
      aida: this.surveyPayload.aida,
      //If the user logged in is a wasp user, we need use the id of the emulated buyer
      buyer_company: this._auth.isLoggedInAsServiceBuyer()
        ? this._auth.serviceBuyer!.id
        : buyer_company,
      // In case of non-draft survey, live_url is a required field for the API
      ...(this.surveyDetails &&
        this.surveyDetails.ps_survey_status !== SURVEY_STATUS.DRAFT && {
          live_url: this.surveyDetails.live_url,
        }),
      ...(this.selectedTemplateResponse &&
        this.selectedTemplateResponse.template?.id &&
        !this.selectedTemplateResponse?.is_mc_template && {
          template_id: this.selectedTemplateResponse.template.id,
          blend_id: this._getTemplateSuppliersOverride().length ? 'NA' : '',
        }),
    };

    if (this.isServiceBuyerOperation && this.enableMarginForOperatorBuyer) {
      const serviveOperatorPayload = this._getServiceOperatorPayload();
      payload = { ...payload, ...serviveOperatorPayload };
    }

    const qualifications = this._mapQualifications();
    this.payloadQualifications = qualifications;
    payload = { ...payload, ...{ qualifications: qualifications } }; // IF mapped qualifications is blank then let he payload qualification be blank as well

    if (this.quotas?.length) {
      payload = { ...payload, ...{ quotas: this._buildSurveyQuotaPayload() } };
    } else {
      delete this.surveyPayload.quotas;
    }

    if (this.defaultPurePriceOverride.isActive()) {
      payload = {
        ...payload,
        ...{
          default_pure_price_override: this.defaultPurePriceOverride.isActive(),
        },
      };
    }

    this._surveyMetaDataService.setIsFormDirty(dirty);
    this._adjustOfferPricePayload(payload);
    if (!isMc) {
      this.surveyPayload = Object.assign(this.surveyPayload, payload);
      this.sendFieldValueToHeader();
    }
    return payload;
  }

  sendFieldValueToHeader() {
    if (!this.isLiveSurveyEdit && this.isSurveyCreated) {
      if (
        this.surveyDetails?.expected_ir != this.form.value.incidence ||
        this.surveyDetails?.expected_loi != this.form.value.lengthOfSurvey ||
        this.surveyDetails?.field_time != this.form.value.fieldTime
      ) {
        this.surveyDetails = Object.assign(this.surveyDetails, {
          field_time: this.form.value.fieldTime,
          expected_loi: this.form.value.lengthOfSurvey,
          expected_ir: this.form.value.incidence,
        });
      }
      this._surveyPayloadService.addTotalClickInHeaderBar(
        { ...this.surveyDetails },
        this.totalClicks
      );
    }
  }

  hasQuotas(qualification: Qualification) {
    return this._editSurveyServiceOb.qualificationHasQuotas(qualification, [
      ...new Set([...this.quotas, ...this._nestedQuotas]),
    ]);
  }

  private _getServiceOperatorPayload() {
    return this._marginService.getServiceOperatorPayload(
      this.form.value.buyerMargin,
      this.form.value.buyerCpiOverride,
      this._auth.buyerConfig?.enableTargetMargin,
      this.marginAccepted
    );
  }

  private _mapQualifications(
    countryQualifications: Qualification[] = this.qualifications,
    quotas: QuotaPayload[] = this.quotas,
    isMcApplied: boolean = false
  ) {
    const qualifications: QualificationPayload[] = [];

    /**Core Qualifications */
    countryQualifications?.forEach((qualification) => {
      if (
        qualification.render_type ===
        this.QUALIFICATIONS_RENDER_TYPE.MULTIPUNCH_DROPDOWN
      ) {
        qualification = this._prepareChildAgeGenderQualification(
          qualification,
          quotas
        );
        const selectedOptions = qualification.data.filter(
          (data) => data.selected
        );
        if (
          selectedOptions.length !== qualification.data.length ||
          this.hasQuotas(qualification) ||
          isMcApplied
        ) {
          qualifications.push({
            qualification_name: qualification.qualification_name,
            qualification_code: qualification.qualification_code,
            condition_codes: qualification.data
              .filter((code) => code.selected)
              .map((code) => `${code.id}`),
            condition_names: qualification.data
              .filter((code) => code.selected)
              .map((code) => {
                let codeObj = {} as any;
                codeObj[`${code.id}`] = `${code.text}`;
                return codeObj;
              }),
          });
        }
      }

      if (
        qualification.render_type === this.QUALIFICATIONS_RENDER_TYPE.RANGE &&
        (qualification.data[0].minValue ||
          qualification.data[0].minValue === 0) &&
        qualification.data[0].maxValue
      ) {
        qualifications.push({
          qualification_name: qualification.qualification_name,
          qualification_code: qualification.qualification_code,
          range_sets: [
            {
              from: Number(qualification.data[0].minValue),
              to: Number(qualification.data[0].maxValue),
              units: this._commonService.getUnit(
                qualification.qualification_code
              ),
            },
          ],
        });
      }
    });

    if (isMcApplied) {
      // for now not including advance and location quotas in mcqb feature
      return qualifications;
    }

    /**Advanced Targeting Qualifications */
    if (this.advanceTargetingQualifications?.length) {
      this.advanceTargetingQualifications.forEach((qualification) => {
        let advancedQualification: QualificationPayload = {
          qualification_name: qualification.qualification_name,
          qualification_code: qualification.qualification_code,
        };
        if (
          qualification.question_type === 'range' ||
          qualification.isRangeType
        ) {
          const rangeSets = {
            from: qualification.rangeSet?.from || 0,
            to: qualification.rangeSet?.to || 99999,
            units: Constants.UNITS.AGE.YEAR,
          };
          advancedQualification.range_sets = [rangeSets];
        } else {
          advancedQualification.condition_codes =
            qualification.selectedAnswers.map((code) => `${code.id}`);
          advancedQualification.condition_names =
            qualification.selectedAnswers.map((code) => {
              let codeObj = {} as any;
              codeObj[`${code.id}`] = `${code.text}`;
              return codeObj;
            });
        }
        qualifications.push(advancedQualification);
      });
    }

    /**Location Qualifications */
    const selectedLocationQualifications = this.getLocationConditionCodes();
    const selectedLocationQualificationsNames =
      this._getLocationConditionCodesNames();
    if (selectedLocationQualifications.length) {
      qualifications.push({
        db_qualification_code: this.locationQualification.db_qualification_code,
        qualification_name: this.locationQualification.qualification_name,
        qualification_code: this.locationQualification.qualification_code,
        condition_codes: selectedLocationQualifications,
        condition_names: selectedLocationQualificationsNames,
      });
    }
    return qualifications;
  }

  //this function can be move to the mapper library (in the same way we do for mapper.childrenConditionCodesToChildGenderAgeConditionCodes)
  childAgeGenderConditionCodeFromQuotas(quotas: QuotaPayload[]): string[] {
    const childAgeConditionCodes = [];

    //get all the conditions codes from child age gender qualification and add it in childAgeConditionCodes
    for (let index = 0; index < quotas.length; index++) {
      const quota = quotas[index];
      for (let index = 0; index < quota.criteria.length; index++) {
        const criteria = quota.criteria[index];
        if (
          criteria.qualification_code === QUALIFICATION_CODES.CHILD_AGE_GENDER
        ) {
          if (criteria.condition_codes) {
            childAgeConditionCodes.push(...criteria.condition_codes);
          }
        }
      }
    }

    //remove duplicated condition codes
    const noDuplications = [...new Set(childAgeConditionCodes)];

    //ordered the conditions codes
    const ordered = noDuplications.sort();

    return ordered;
  }

  hasChildAgeGenderQuota(quotas: QuotaPayload[]): boolean {
    return !!quotas.find((quota) =>
      quota.criteria.find(
        (criteria) =>
          criteria.qualification_code === QUALIFICATION_CODES.CHILD_AGE_GENDER
      )
    );
  }

  private _prepareChildAgeGenderQualification(
    qualification: Qualification,
    quotas: QuotaPayload[] = this.quotas
  ): Qualification {
    if (
      qualification.qualification_code === QUALIFICATION_CODES.CHILD_AGE_GENDER
    ) {
      const mapper = new QualificationChildGenderAgeMapper();
      qualification = deepCopy(qualification);
      let conditionCodes: string[] = qualification.data
        .filter((qual) => qual.selected)
        .map(({ id }) => id.toString());
      //if we have a quota for child age gender we need to get the condition code from the quotas
      if (this.hasChildAgeGenderQuota(quotas)) {
        conditionCodes = this.childAgeGenderConditionCodeFromQuotas(quotas);
      } else {
        conditionCodes =
          mapper.childrenConditionCodesToChildGenderAgeConditionCodes(
            conditionCodes
          );
      }
      qualification.data = this
        ._childAgeGenderQualificationData as QualificationData[];
      qualification.data.forEach(
        (datum) =>
          (datum.selected = conditionCodes.includes(datum.id.toString()))
      );
    }
    return qualification;
  }

  public getLocationConditionCodes() {
    if (this.locationQualification?.data?.length) {
      return this._getMultiselectLocationConditionCodes();
    }

    return [];
  }
  private _getLocationConditionCodesNames() {
    if (this.locationQualification?.data?.length) {
      return this._getMultiselectLocationConditionNames();
    }

    return [];
  }

  private _getMultiselectLocationConditionCodes() {
    if (
      this.locationQualification.render_type ===
      this.QUALIFICATIONS_RENDER_TYPE.FILE_UPLOAD
    ) {
      return this.locationQualification.data.map((code) => `${code.id}`);
    }
    const selectedLocations = this.locationQualification.data.filter(
      (qual) => qual.selected
    );

    if (selectedLocations.length) {
      return this.locationQualification.data
        .filter((code) => code.selected)
        .map((code) => `${code.id}`);
    }

    return [];
  }

  private _getMultiselectLocationConditionNames() {
    if (
      this.locationQualification.render_type ===
      this.QUALIFICATIONS_RENDER_TYPE.FILE_UPLOAD
    ) {
      return this.locationQualification.data.map((code) => `${code.name}`);
    }
    const selectedLocations = this.locationQualification.data.filter(
      (qual) => qual.selected
    );

    if (selectedLocations.length) {
      return this.locationQualification.data
        .filter((code) => code.selected)
        .map((code) => {
          let codeObj = {} as any;
          codeObj[`${code.id}`] = code.name ? `${code.name}` : `${code.text}`;
          return codeObj;
        });
    }

    return [];
  }

  private _filterQualifications(qualifications: Qualification[]) {
    this._setChildAgeGenderQuafication(qualifications);
    const excludeChildQualificationCode =
      this._getChildrenQuaficationExcludeCode();
    this.qualifications = qualifications
      .filter(({ qualification_code, deprecated }) => {
        if (
          deprecated &&
          !this.alwaysShowQualCodes?.includes(qualification_code)
        ) {
          return false;
        }
        return qualification_code !== excludeChildQualificationCode;
      })
      .map((qualification: Qualification) => {
        if (qualification.is_range) {
          const data = qualification.data[0];

          let details: { [key: string]: unknown } = {
            min: String(data.min).toString(),
            max: String(data.max).toString(),
            minValue: null,
            maxValue: null,
            minPlaceholder: String(data.min).toString(),
            maxPlaceholder: String(data.max).toString(),
            maxlengthMin: String(data.max).length,
            maxlengthMax: String(data.max).length,
          };
          if (qualification.qualification_code === QUALIFICATION_CODES.INCOME) {
            details.preFix = data.prefix;
          }
          qualification.data = [details];
        } else if (!qualification.is_location) {
          qualification.data = qualification.data.map((item) => {
            item.selected = true;
            return item;
          });
        }
        qualification.quotas = [];
        return qualification;
      });
    this._setLocationTabs();
  }

  private _getChildrenQuaficationExcludeCode(): number {
    if (!this.isEditMode) {
      // if new survey, set new child age gender qualification by excluding the old one
      return QUALIFICATION_CODES.CHILDREN;
    }
    // else get current child qualification code and exclude the other
    // meaning to use current child qualification
    const currentCode = this._getCurrentChildrenQualificationCode();
    return currentCode === QUALIFICATION_CODES.CHILD_AGE_GENDER
      ? QUALIFICATION_CODES.CHILDREN
      : QUALIFICATION_CODES.CHILD_AGE_GENDER;
  }

  private _getCurrentChildrenQualificationCode(): number {
    if (!this.surveyDetails || !Object.keys(this.surveyDetails).length) {
      // survey not define meaning to default to new child age gender qualification
      return QUALIFICATION_CODES.CHILD_AGE_GENDER;
    }
    const childrenQualificationCodes = [
      QUALIFICATION_CODES.CHILDREN,
      QUALIFICATION_CODES.CHILD_AGE_GENDER,
    ];
    const currentChildQualificationSet = this.surveyDetails.qualifications
      .filter(({ qualification_code }) =>
        childrenQualificationCodes.includes(qualification_code)
      )
      .map((qualification) => qualification.qualification_code);
    if (currentChildQualificationSet.length > 1) {
      throw new Error(
        notifyMessage.errorMessage.INVALID_MULTIPLE_CHILDREN_QUALIFICATION
      );
    } else if (currentChildQualificationSet.length) {
      const [currentCode] = currentChildQualificationSet;
      return currentCode;
    }
    return QUALIFICATION_CODES.CHILD_AGE_GENDER;
  }

  private _setChildAgeGenderQuafication(qualifications: Qualification[]): void {
    const current_qualification_code =
      this._getCurrentChildrenQualificationCode();
    if (current_qualification_code === QUALIFICATION_CODES.CHILDREN) {
      return;
    }
    const childAgeGenderQualification = qualifications.find(
      ({ qualification_code }) =>
        qualification_code === QUALIFICATION_CODES.CHILD_AGE_GENDER
    );
    const childrenQualification = qualifications.find(
      ({ qualification_code }) =>
        qualification_code === QUALIFICATION_CODES.CHILDREN
    );
    if (!childrenQualification || !childAgeGenderQualification) {
      return;
    }
    // save child age gender qualification for later use
    this._childAgeGenderQualificationData = deepCopy(
      childAgeGenderQualification.data
    );
    // set child age gender qualification as the old children to show in the same way ou UI
    childAgeGenderQualification.data = childrenQualification.data;
    // map child age gender values to old children structure
    if (this.surveyDetails && Object.keys(this.surveyDetails).length) {
      const childAgeGenderSurveyQualification =
        this.surveyDetails.qualifications.find(
          ({ qualification_code }) =>
            qualification_code === QUALIFICATION_CODES.CHILD_AGE_GENDER
        );
      if (
        !childAgeGenderSurveyQualification ||
        childAgeGenderSurveyQualification.condition_codes_mapped
      ) {
        return;
      }
      const mapper = new QualificationChildGenderAgeMapper();
      childAgeGenderSurveyQualification.condition_codes =
        mapper.childGenderAgeConditionCodesToChildrenConditionCodes(
          childAgeGenderSurveyQualification.condition_codes
        );
      childAgeGenderSurveyQualification.condition_codes_mapped = true;
    }
  }

  private _setLocationTabs() {
    this.locationTabs = [];
    this.qualifications
      .filter((qualification: Qualification) => qualification.is_location)
      .forEach((qualification: Qualification) => {
        if (
          !(
            this.selectedCountryCode === 'KZ' &&
            qualification.qualification_code === QUALIFICATION_CODES.ZIPCODE
          )
        ) {
          this.locationTabs.push({
            title: qualification.qualification_name,
            id: qualification.qualification_code,
            active: false,
            renderType: qualification.render_type,
          });
        }
      });
    this.activeTabIndex = -1;

    this.activeTab = {
      title: '',
      id: 0,
      active: false,
      renderType: 0,
    };
  }

  private _mapQuotasForNestingEdit(
    quotas: QuotaPayload[] = this.quotas,
    totalClicks = this.totalClicks,
    completes = this.form.value.numberOfCompletes
  ): QuotaPayload[] {
    return new Nesting(quotas, this._commonService).recreate(
      totalClicks,
      completes
    );
  }

  public openQuotaNestingModal() {
    const { isNestingMultiQuotaEnabled, isNestingQuotaGroupingEnabled } =
      this._buyerNestingConfig();

    if (isNestingMultiQuotaEnabled) {
      this._openMultiQuotaNestingModal();
      return;
    }

    if (isNestingQuotaGroupingEnabled) {
      this._openGroupQuotaNestingModal();
      return;
    }

    this._openDefaultQuotaNestingModal();
  }

  private _openMultiQuotaNestingModal() {
    const modalRef = this._modal.open(MultiQuotaNestingModalComponent, {
      data: {
        quotas: this._mapQuotasForNestingEdit(cloneDeep(this.quotas)),
        qualifications: this._combinedQualifications,
        nestedQuotas: cloneDeep(this._nestedQuotas),
        completes: this.totalClicks || this.form.value.numberOfCompletes,
        surveyDetails: this.surveyDetails,
      },
      maxWidth: '80%',
      width: '100%',
      interceptClose: true,
    });

    this._subscriptions.add(
      modalRef.onClose$.subscribe((response?: MultiQuotaNestingReturn) => {
        if (!response) {
          return;
        }
        const { canceled, quotas: nestedQuotas } = response;
        if (!canceled) {
          this._syncNestedQuotas(nestedQuotas);
          const groupCount = new SurveyNestedQuotas(nestedQuotas).groupIds()
            .length;
          const message =
            groupCount === 0
              ? notifyMessage.successMessage.MULTI_QUOTA_NESTING
                  .NO_QUOTA_APPLIED_FOR_SURVEY
              : notifyMessage.successMessage.MULTI_QUOTA_NESTING.APPLIED_SUCCESSFULLY.replace(
                  '<NUMBER_OF_QUOTA_NESTING>',
                  groupCount.toString()
                );

          this._toastr.success(message);
        }
      })
    );
  }

  private _openGroupQuotaNestingModal() {
    this._modal.open(QuotaGroupingModalComponent, {
      data: {
        quotas: this._mapQuotasForNestingEdit(),
        qualifications: this._combinedQualifications,
        nestedQuotas: this._surveyNestedQuotas
          .kind(this._editableNestingGroupId)
          .get(),
        completes: this.totalClicks || this.form.value.numberOfCompletes,
        syncNestedQuotasEmmiter: this._syncNestedQuotasEmmiter,
        surveyDetails: this.surveyDetails,
      },
      maxWidth: '80%',
      width: '100%',
    });
  }

  private _openDefaultQuotaNestingModal() {
    this._modal.open(QuotaNestingModalComponent, {
      data: {
        quotas: this._mapQuotasForNestingEdit(),
        qualifications: this.qualifications,
        locationQualification: this.locationQualification,
        advanceTargetingQualifications: this.advanceTargetingQualifications,
        nestedQuotas: this._surveyNestedQuotas
          .kind(this._editableNestingGroupId)
          .get(),
        completes: this.totalClicks || this.form.value.numberOfCompletes,
        isEditMode: this.isEditMode,
        syncNestedQuotasEmmiter: this._syncNestedQuotasEmmiter,
      },
      maxWidth: '80%',
      width: '100%',
    });
  }

  private _syncNestedQuotas(nestedQuotas: Array<NestedQuota>) {
    this._quotaPayloadService.setNestedQuotas(nestedQuotas);
    this.hasNestedGroup = this._hasNestedGroup();
    this._assignNestedQuotaFlagToQualifications();
    this.hasQuotasForPurePrice();
    this.autoSaveSurveyInDraft();
  }

  private _hasNestedGroup(): boolean {
    return this._surveyNestedQuotas.hasGroupedQuotas();
  }

  private _assignNestedQuotaFlagToQualifications(): void {
    this.qualifications = this._mapQualificationsForNesting(
      this.qualifications
    );

    this.visibleQualifications = this._visibleQualifications();

    this.advanceTargetingQualifications = this._mapQualificationsForNesting(
      this.advanceTargetingQualifications
    );

    this.locationQualification =
      this._mapQualificationsForNesting([this.locationQualification])[0] ?? {};

    this.nestedQuotasActive = this._areNestedQuotasActiveInSurvey();

    const deselected = this._previousNestingQualIds.filter(
      (old) => !this._surveyNestedQuotas.qualificationIds().includes(old)
    );

    // This scenario works in case of clear nesting (when all nested quotas are removed from the survey) or whenever any deselection in nesting is applied
    if (deselected.length > 0) {
      const nesting = new NestingQuotaFilter(
        this._buildSurveyQuotaPayload(),
        this._mapQuotasForNestingEdit(),
        deselected,
        this._previousNestingQualIds
      ).filter();

      this.quotas = nesting.quotas();

      this.qualifications = new AssignQuota(nesting.missing()).to(
        this.qualifications
      );
      this.visibleQualifications = this._visibleQualifications();
      this.advanceTargetingQualifications = new AssignQuota(
        nesting.missing()
      ).to(
        this.advanceTargetingQualifications as CombinedQualification[]
      ) as AdvanceTargetingQualification[];

      this.locationQualification = new AssignQuota(nesting.missing()).to([
        this.locationQualification,
      ])[0];
    }

    this.quotas = this._buildSurveyQuotaPayload();
  }

  private _mapQualificationsForNesting<
    T extends Qualification | AdvanceTargetingQualification
  >(qualifications: T[] = []) {
    return qualifications.map((qualification) => {
      const hasNestedQuotas = this._surveyNestedQuotas.hasQualification(
        qualification.qualification_code
      );

      return {
        ...qualification,
        hasNestedQuotas,
        quotas: hasNestedQuotas ? [] : qualification.quotas,
      };
    });
  }

  public openPriceRecoModal(isManualClose = false) {
    if (!isManualClose) {
      this.showPriceRecoModel = true;
    } else {
      this.showPriceRecoModel = false;
    }
  }

  public addAdvanceQualification(
    qualifications: AdvanceTargetingQualification[]
  ) {
    this.advanceTargetingQualifications = qualifications;
    this.autoSaveSurveyInDraft();
  }

  setCoreQualificationIfDependent(
    payload: UpdateAdvanceTargetingCoreQualification
  ) {
    const parentQualificationIndex = this.qualifications.findIndex(
      (coreQualification) =>
        payload.question &&
        payload.question.hasDependency &&
        payload.question.dependency &&
        coreQualification.db_qualification_code ===
          payload.question.dependency.parentQualId
    );

    if (parentQualificationIndex !== -1) {
      this.qualifications = this.qualifications.map(
        (qualification: Qualification, index: number) => {
          if (index === parentQualificationIndex) {
            qualification = this._handleAdvanceTargetingCoreQualification(
              payload,
              qualification
            );
          }
          return qualification;
        }
      );
    }
    // If any advanced qualification has quotas added, we need to delete them as well.
    if (payload.action === Constants.ADVANCE_TARGETING_ACTION.DELETE) {
      this._deleteQuotasOfAdvancedTargetingQualification(
        payload.question,
        Constants.OPERATIONS.CHILD
      );
    }
  }

  private _handleAdvanceTargetingCoreQualification(
    payload: UpdateAdvanceTargetingCoreQualification,
    qualification: Qualification
  ) {
    if (payload.action === Constants.ADVANCE_TARGETING_ACTION.ADD) {
      qualification.isAdvanceTargetingParent = true;
      qualification.data = qualification.data.map((item) => {
        if (payload.question.dependency?.matchCriteria === 'in') {
          item.selected = payload.question.dependency.condition.includes(
            item.id
          );
        }
        if (payload.question.dependency?.matchCriteria === 'nin') {
          item.selected = !payload.question.dependency.condition.includes(
            item.id
          );
        }
        return item;
      });
      if (qualification.is_location) {
        this._setLocationQualification(qualification);
      }
      // In case any dependent parent quota already exists, needs to be deleted.
      this._deleteQuotasOfAdvancedTargetingQualification(
        payload.question,
        Constants.OPERATIONS.PARENT
      );
    } else if (payload.action === Constants.ADVANCE_TARGETING_ACTION.DELETE) {
      qualification.isAdvanceTargetingParent = false;
      qualification.data = qualification.data.map((item) => {
        item.selected = true;
        return item;
      });
      if (qualification.is_location) {
        this.locationQualification = {} as Qualification;
        this._setLocationTabs();
      }
    }
    qualification.quotas = [];
    return qualification;
  }

  hasQuotasForPurePrice() {
    this.purePriceQuota = [];
    this.quotasForPurePrice = false;
    const qualApplicableForPurePrice = APPLICABLE_QUALIFICATION_FOR_PURE_PRICE;
    if (this._nestedQuotas?.length) {
      this._nestedQuotas.forEach((nestedQuota: NestedQuota) => {
        if (
          nestedQuota.criteria.filter((crit: Criteria) =>
            qualApplicableForPurePrice.includes(crit.qualification_code)
          ).length
        ) {
          nestedQuota.required_count = nestedQuota.maximum || 0;
          this.purePriceQuota.push(nestedQuota as NestedQuota);
          this.quotasForPurePrice = true;
        }
      });
    }
    {
      this.quotas?.map((quota) => {
        if (
          quota.criteria.filter((crit) =>
            qualApplicableForPurePrice.includes(crit.qualification_code)
          ).length
        ) {
          this.purePriceQuota.push(quota as NestedQuota);
          this.quotasForPurePrice = true;
        }
      });
    }
    if (!PURE_PRICE_COUNTRIES.includes(this.selectedCountryCode)) {
      this.quotasForPurePrice = false;
    }
    if (this.purePriceData && this.purePriceData.quotas) {
      this.purePriceData.quotas = this.purePriceQuota;
    }
  }

  private _buildPurePriceWindowData() {
    if (
      (!this.surveyDetails?.is_pure_price_rate_card_enable &&
        !this.purePriceQuota.length) ||
      !this._hasSurveyMetadataFields()
    ) {
      return;
    }
    const { numberOfCompletes, fieldTime, incidence, lengthOfSurvey } =
      this.form.value;
    const survey: Survey = {
      completes_required: numberOfCompletes,
      fielded: this.surveyDetails.fielded,
      ps_survey_id: this.surveyId,
      incidence: {
        recent: incidence,
      },
      loi: {
        recent: lengthOfSurvey,
      },
      field_time: fieldTime,
      locale: {
        country_name: this.surveyDetails.locale.country_name,
        language_code: this.surveyDetails.locale.language_code,
      },
      is_pure_price_rate_card_enable:
        this.surveyDetails.is_pure_price_rate_card_enable || false,
    };
    this.purePriceData = {
      survey,
      quotas: this.purePriceQuota || [],
      purePriceApplied: this.surveyDetails.price_reco,
    };
  }

  private _areNestedQuotasActiveInSurvey(): boolean {
    return this._combinedQualifications
      .filter((q) => !!q)
      .some((q) => !!q.hasNestedQuotas);
  }

  private _buildSurveyQuotaPayload(): QuotaPayload[] {
    const nestedQuotaPayload = this._buildNestedQuotaPayload();
    const recreatedQuotas = this._mapQuotasForNestingEdit(this.quotas);
    const unNestedQuotaPayload = new UnNestedQuotaFilter(
      recreatedQuotas,
      this._nestedQuotas
    ).quotas();

    return [...nestedQuotaPayload, ...unNestedQuotaPayload];
  }

  private _buildNestedQuotaPayload(
    nestedQuotas?: NestedQuota[]
  ): QuotaPayload[] {
    const surveyAllocation = Number(
      this.totalClicks || this.form.value.numberOfCompletes
    );
    return new QuotaPayloadMapper()
      .fromNesting(nestedQuotas || this._nestedQuotas, surveyAllocation)
      .map();
  }

  private _redistributeQuotasForSpvSurveys(isClickBalanceEnabled: boolean) {
    Object.values(this.allMcChildSurveys).forEach((survey) => {
      if (
        survey.ps_survey_id === this.surveyId ||
        (!!survey?.mc?.SVP && !!survey?.mc?.parent)
      ) {
        return;
      }
      survey = this._quotaModalService.redistributeQuotasForSpvSurvey(
        survey,
        isClickBalanceEnabled
      );
    });
  }

  calculateTotalClicks() {
    if (this.form.value.numberOfCompletes && this.form.value.incidence) {
      this.surveyDetails.click_balancing = this.form.value.clickBalancing
        ? 1
        : 0;
      this.form.value.clickBalancing = this.form.value.clickBalancing ? 1 : 0;
      this.totalClicks = this._calculateTotalClicks(
        this.form.value.numberOfCompletes,
        this.form.value.incidence,
        this.form.value.clickBalancing
      );
      this.changeQuotaDistribution.emit(
        this.totalClicks || this.form.value.numberOfCompletes
      );
      //nested Quotas Redistribution
      if (this._nestedQuotas?.length) {
        this._nestedQuotasRedistribution();
      }
    } else if (
      this.isSurveyCreated &&
      (this.form.value.selectedCategory.id !==
        Constants.SURVEY_CATEGORY_CODE.AUGMENT ||
        !this.form.value.incidence)
    ) {
      this._toastr.warning(
        notifyMessage.warningMessage.COMPLETES_AND_INCIDENCE_REQUIRED
      );
      this._validateIr();
    }
  }

  private _nestedQuotasRedistribution(
    nestedQuotas = this._nestedQuotas,
    completes?: number
  ) {
    const totalCompletesOrClicks = !!completes
      ? completes
      : Number(this.totalClicks || this.form.value.numberOfCompletes);
    nestedQuotas = this._quotaModalService.nestedQuotasRedistribution(
      nestedQuotas,
      totalCompletesOrClicks
    );
    if (!!completes) {
      return nestedQuotas;
    }
    this._syncNestedQuotas(this._nestedQuotas);
    return [];
  }

  private _hasMoreQuotasThanCompletes(): boolean {
    const allocation = this.totalClicks || this.form.value.numberOfCompletes;
    return this._surveyNestedQuotas
      .groupIds()
      .some(
        (groupId) =>
          this._surveyNestedQuotas.kind(groupId).get().length > allocation
      );
  }

  public applyPurePrice(quotaPurePriceApplied: QuotaPurePrice) {
    this._createSurveyServiceOb.setIsPurePriceApplied(!!quotaPurePriceApplied);
    this.surveyPayload.price_reco = !!quotaPurePriceApplied;
    this.surveyDetails.price_reco = !!quotaPurePriceApplied;
    if (this.surveyPayload.price_reco) {
      this.surveyPayload.quotas = this.surveyPayload!.quotas!.map((quota) => {
        const purePriceQuota = quotaPurePriceApplied.criterias.filter(
          (purePriceQuotaCriteria) =>
            purePriceQuotaCriteria.id === quota.buyer_quota_id
        );
        if (!purePriceQuota.length) {
          return quota;
        }
        const priceRecommended = this._getCPIForPurePriceQuotaCriteria(
          quotaPurePriceApplied,
          quota
        );
        return { ...quota, priceRecommended };
      });
    }
    this._updateSurveyWithPurePriceData(quotaPurePriceApplied);
    this._buildPurePriceWindowData();
  }

  public applyPurePriceV2(fieldTime: number) {
    this.form.patchValue({ fieldTime });
    this._decideAndSetFeasibility(true, 'update');
    this._surveyMetaDataService.applyPurePriceV2(true);
  }

  public trackByQualificationCode(_index: number, item: Qualification) {
    return item.qualification_code;
  }

  private _updateSurveyWithPurePriceData(quotaApplied?: QuotaPurePrice) {
    let qbpEnable = false;
    let quotasIds: string[] = [];
    let fieldTime = this.form.value.fieldTime;
    if (quotaApplied) {
      qbpEnable = true;
      quotasIds = quotaApplied.criterias.map((quota) => quota.id!);
      fieldTime = quotaApplied.fieldTime;
    }
    this.surveyPayload.qbp = {
      enable: qbpEnable,
      quota_ids: quotasIds,
    };
    this.form.patchValue({ fieldTime });
  }

  /**
   * Gets the price recommendation value from a specific quota
   */
  private _getCPIForPurePriceQuotaCriteria(
    purePriceQuotas: QuotaPurePrice,
    surveyQuota: QuotaPayload
  ): number {
    const pricedQuota = purePriceQuotas.criterias.find(
      (purePriceQuota) => purePriceQuota.id === surveyQuota.buyer_quota_id
    );
    return pricedQuota!.priceRecommended!;
  }

  private _hasSurveyMetadataFields() {
    const { numberOfCompletes, fieldTime, incidence, lengthOfSurvey } =
      this.form.value;
    return !!(
      !isNaN(this.surveyDetails?.fielded) &&
      this.surveyDetails.locale.country_name &&
      numberOfCompletes &&
      fieldTime &&
      incidence &&
      lengthOfSurvey
    );
  }

  private _handleMcSpvGoalChange() {
    this._subscriptions.add(
      this._multiCountryDraftService
        .getMcSpvGoalChangedCommand()
        .pipe(distinctUntilChanged())
        .subscribe((goal) => {
          this._redistributeQuotasForSpvSurveys(
            !!this.surveyDetails.click_balancing
          );
          const cache = {
            for: 'each' as const,
            data: Object.values(this.allMcChildSurveys)
              .filter((survey) => !!survey.mc.SVP && !survey.mc.parent)
              .map(({ quotas, survey_localization }) => {
                return {
                  survey_localization,
                  quotas,
                };
              }),
          };
          this._multiCountryDraftService.sendMcSpvUpdateInCacheCommand(cache);
        })
    );
  }

  private _adjustOfferPricePayload(obj: any) {
    // sometimes this value come as string and gives us a server error
    if (obj?.offer_price && typeof obj.offer_price !== 'number') {
      obj.offer_price = Number(obj.offer_price);
    }
  }

  public getInitialValueState() {
    this._buyerCpiOverrideSnapShot.update(
      Number(this.form.value.buyerCpiOverride?.toString().replace(',', '.'))
    );
    this._buyerCpiOverrideSnapShot.take();
  }

  private _validateBuyerCpiT(value: number) {
    return this._buyerCpiOverrideSnapShot.equal(value);
  }

  private _getMargin() {
    const margin = this.form.value.buyerMargin;
    if (!margin) {
      return this.isInsight ? this.insightsBuyerMargin : 0;
    }
    return margin;
  }

  // eslint-disable-next-line complexity
  public calculateSuppCpiOverride(
    skipMarginVerify = true,
    sendCpiData = true,
    doSurveyUpdate = true
  ) {
    const updatedMargin = this._getMargin();
    if (skipMarginVerify) {
      this.verifyMarginToShowWarning();
    }
    const BCPI = Number(
      this.form.value.buyerCpiOverride?.toString().replace(',', '.')
    );

    if (
      !!this.surveyDetails?.aida?.enabled && //
      !!this._eligibleSuppliersPricingDetails?.CPI &&
      BCPI < this._eligibleSuppliersPricingDetails.CPI &&
      this.isLiveSurveyEdit
    ) {
      this._toastr.error(
        notifyMessage.errorMessage.AIDA.LIVE_CPI_OVERRIDE_CHANGE.replace(
          '<CURRENCY_SYMBOL>',
          this.surveyDetails.currency_symbol
        ).replace(
          '<CPI_VALUE>',
          this._eligibleSuppliersPricingDetails.CPI?.toString()
        )
      );

      this.form.controls.buyerCpiOverride.patchValue(
        Number(this.surveyDetails.price_override)
      );
      return;
    }

    this._priceOverrideChanges$.next(BCPI);

    if (this._validateBuyerCpiT(BCPI)) {
      return;
    }

    if (
      updatedMargin &&
      !isNaN(updatedMargin) &&
      !Number.isInteger(updatedMargin)
    ) {
      this.form.controls.buyerMargin.setValue(Number(updatedMargin.toFixed(2)));
    }

    if (BCPI && !isNaN(BCPI) && !Number.isInteger(BCPI)) {
      this.form.controls.buyerCpiOverride.setValue(Number(BCPI.toFixed(2)));
    }
    if (BCPI && !isNaN(BCPI) && !isNaN(updatedMargin)) {
      this._adjustOfferPricePayload(this.surveyPayload);
      this.surveyPayload = Object.assign(this.surveyPayload, {
        price_override: BCPI,
        margin_override: {
          margin: updatedMargin,
          enable: true,
        },
      });

      const scpi = this._calculateScpiValue(BCPI, updatedMargin);
      this.form.controls.supplierCpiOverride.setValue(scpi.toFixed(2));
      if (this.isSurveyCreated) {
        this._setSupplierBCPI(BCPI);
        if (!this.manualScpiDisabled) {
          this._setSupplierSCPI(scpi);
          this._adjustOfferPricePayload(this.surveyDetails);
          this.surveyDetails = Object.assign(this.surveyDetails, {
            price_override: BCPI,
            margin_override: {
              margin: updatedMargin,
              enable: true,
            },
          });
          this._surveyMetaDataService.sendBuyerSurveyData(this.surveyDetails);
        }
      }
      if (
        Number(this._cpiData.buyerData?.surveyId) === this.surveyId &&
        sendCpiData
      ) {
        this._surveyMetaDataService.sendCPIData(this._cpiData);
      }
    }
    if (this.surveyId && BCPI && sendCpiData) {
      // PD-8473 CPI values ​​change to the value before, after remove the quota.
      const action = doSurveyUpdate ? 'update' : '';
      this.autoSaveSurveyInDraft(true, false, action);
    } else if (BCPI && sendCpiData) {
      this.autoSaveSurveyInDraft();
    }
  }

  private _calculateScpiValue(BCPI: number, updatedMargin: number) {
    const scpi = Number((BCPI * (100 - updatedMargin)) / 100);
    if (this._currencyExchangeRate?.CRRate) {
      return this._commonService.localToUSD(
        scpi,
        this._currencyExchangeRate.rate,
        this._currencyExchangeRate.CRRate
      );
    }
    return scpi;
  }

  private _setSupplierSCPI(supplierCpi: number) {
    if (this._cpiData.suppliers?.length) {
      for (const supplier of this._cpiData.suppliers) {
        supplier.scpi = supplierCpi;
      }
    }
  }

  private _setSupplierBCPI(buyerCPI: number) {
    if (this._cpiData.suppliers?.length) {
      for (const supplier of this._cpiData.suppliers) {
        supplier.cpi = buyerCPI;
      }
    }
    const flexibilityPrice = {} as FlexibilityPricesParams;
    flexibilityPrice.survey_price_override = buyerCPI;
    flexibilityPrice.manual_override = true;
    this._cpiData.flexibilityPrice = flexibilityPrice;
  }

  public verifyMarginToShowWarning(): void {
    const isBellowAverage = this.averageSupplierMarginValue > this._getMargin();
    if (isBellowAverage) {
      this.showWarningModal = true;
      this.marginBelowAverage = true;
      return;
    }
    this.marginBelowAverage = false;
    if (this.isInsight && !this.form.value.buyerMargin) {
      this.marginValue = this.insightsBuyerMargin;
      this.form.controls.buyerMargin.setValue(this.marginValue);
    }
    this.marginValue = this.form.value.buyerMargin;
  }

  public userAcceptedMargin() {
    this.marginAccepted = true;
    this.showWarningModal = false;
    this.marginValue = this.form.value.buyerMargin;
  }

  public hideWarningModal() {
    this.showWarningModal = false;
    this.form.controls.buyerMargin.setValue(this.marginValue);
    this.calculateSuppCpiOverride(false);
  }

  public onBuyerCpiConfirmation(confirmation: Confirmation) {
    this.buyerCpiConfirmation = confirmation;
    this.requireBuyerCpiConfirmation = false;
    if (!this.surveyId) {
      this._confirmationHighCpiCalls.push(this.newCpiConfirmation);
    }
  }

  public validateBuyerCpi() {
    const cpi = this.form.value.buyerCpiOverride ?? 0;
    this.newCpiConfirmation = cpi;
    this.requireBuyerCpiConfirmation = cpi > LayoutConstants.MAX_CPI_THRESHOLD;

    this.buyerCpiConfirmation = this.requireBuyerCpiConfirmation
      ? Confirmation.rejected()
      : Confirmation.confirmed();
    this._validateBuyerCpiWhenTemplateApplied();
  }

  public surveyTitleChanged() {
    this.surveyValuesUpdated.emit({
      surveyTitle: this.form.get('surveyTitle')?.value,
    });
  }

  public changeInfields(fieldName: string) {
    if (this.surveyDetails && this.surveyDetails.launch_date) {
      this.surveyValuesUpdated.emit({
        [fieldName]: this.form.get(fieldName)?.value,
      });
    }
  }

  private _getCurrencyExchangeRate(fx: number) {
    this._commonService.getCurrencyExchangeRate(fx).subscribe(
      (response: CurrencyExchangeRateResponse) => {
        this._currencyExchangeRate = response.currencies;
      },
      (error) => {
        this._toastr.error(error.error.msg);
      }
    );
  }

  private _validateBuyerCpiWhenTemplateApplied() {
    // If there is no template selected, no need to validate BUYER CPI in template
    if (
      !this.selectedTemplateResponse ||
      !this.selectedTemplateResponse.template
    ) {
      return;
    }
    // If survey is created out of the selected template, then other BUYER_CPI validation should show
    if (this.isSurveyCreated) {
      return;
    }
    // If there is no target margin enabled for a operator buyer, no need to check for BUYER CPI validation
    if (!this.enableMarginForOperatorBuyer) {
      return;
    }
    const buyerCpiFormControl: FormControl = this.form.get(
      'buyerCpiOverride'
    ) as FormControl;
    const buyerCpi: number = buyerCpiFormControl.value;
    // If Buyer CPI is valid and non-zero, it's correct
    if (buyerCpi && buyerCpi > 0) {
      return;
    }
    // otherwise, throw warning
    buyerCpiFormControl.setErrors({ incorrect: true });
    this._toastr.warning(
      notifyMessage.warningMessage.TEMPLATES.TEMPLATE_DOES_NOT_HAVE_BUYER_CPI
    );
  }

  private _isMcTemplate({
    is_mc_template,
    template,
  }: TemplateResponse): boolean {
    return !!is_mc_template && template && !!template.mc_template_id;
  }

  private _createMcSurveysUsingTemplate(mcTemplateId: string) {
    this._templateService.createMcSurveysUsingTemplate(mcTemplateId).subscribe(
      (surveysResponse) => {
        if (surveysResponse.failed_locales?.length) {
          this._toastr.error(
            `${
              notifyMessage.errorMessage.MULTICOUNTRY_DRAFT.REJECTED_SURVEYS
            } ${new LocaleFullNameMapper(
              surveysResponse.failed_locales,
              this.countries
            ).map()}`
          );
        }

        if (surveysResponse.survey_ids && surveysResponse.survey_ids.length) {
          this._toastr.success(
            notifyMessage.successMessage.MULTICOUNTRY_DRAFT.NEW_SURVEYS_ADDED
          );
          this._router.navigateByUrl(
            `/surveys/${surveysResponse.survey_ids[0]}/edit`
          );
        }
      },
      (err) => {
        this._toastr.error(err.error.ps_api_response_message);
      }
    );
  }

  private _updateBuyerMarginValidation() {
    if (this.isEditMode) {
      // PD-10990 accept whatever value on survey edit
      this.form.controls.buyerMargin.clearValidators();
    }
  }

  public isChildrenQualification(qualificationCode: number): boolean {
    return [
      QUALIFICATION_CODES.CHILDREN,
      QUALIFICATION_CODES.CHILD_AGE_GENDER,
    ].includes(qualificationCode);
  }

  private _setAttributQualification(
    countryCode: string,
    languageCode: string,
    qualifications: Qualification[]
  ) {
    this._updateAttributeQualification(countryCode, qualifications);
    if (
      this._selectedCountries().length > this.attributeQualifications.length
    ) {
      this.attributeQualifications.push({
        countryCode,
        languageCode,
        qualifications,
      });
    }
    if (
      this._selectedCountries().length < this.attributeQualifications.length
    ) {
      this.attributeQualifications = this._removeQualification();
    }
    if (!this._selectedCountries().length) {
      return qualifications;
    }
    return this._filterAttributeQualifications();
  }

  private _removeQualification() {
    const selectedCountries = this._selectedCountries();
    return this.attributeQualifications.filter((attributeQualification) => {
      return selectedCountries.find(
        (selectCountry: CountryInterface) =>
          selectCountry.short_Code === attributeQualification.countryCode
      );
    });
  }

  private _filterAttributeQualifications() {
    const commonQualifications = this._getCommonQualifications();
    return commonQualifications.filter((commonQualification: Qualification) => {
      const saveQualification = !this.attributeQualifications.some(
        (attributeQualification) =>
          attributeQualification.qualifications.some(
            (qualification) =>
              qualification.db_qualification_code ===
                commonQualification.db_qualification_code &&
              !this._compareQualificationOption(
                qualification.data,
                commonQualification.data,
                qualification.is_range
              )
          )
      );
      if (!saveQualification) {
        // remove quota if qualification is being removed.
        this.quotas = this.quotas.filter(
          (quota) =>
            !quota.criteria.some(
              (singleCriteria) =>
                singleCriteria.qualification_code ===
                commonQualification.db_qualification_code
            )
        );
      }
      return saveQualification;
    });
  }

  private _getCommonQualifications() {
    if (this.attributeQualifications.length === 1) {
      return this.attributeQualifications[0].qualifications;
    }
    return this.attributeQualifications
      .map(
        (attrQualification: AttributeQualification) =>
          attrQualification.qualifications
      )
      .reduce((accumulator: Qualification[], currentArray: Qualification[]) => {
        return accumulator.filter((element: Qualification) => {
          return !!currentArray.find((currentObj: Qualification) => {
            return (
              currentObj.db_qualification_code ===
                element.db_qualification_code &&
              [
                this.QUALIFICATIONS_RENDER_TYPE.RANGE,
                this.QUALIFICATIONS_RENDER_TYPE.MULTIPUNCH_DROPDOWN,
              ].includes(currentObj.render_type)
            );
          });
        });
      });
  }

  private _compareQualificationData(
    qualficationData: QualificationData,
    commonQualificationData: QualificationData,
    isRange: boolean
  ) {
    if (isRange) {
      return (
        Number(qualficationData.min) === Number(commonQualificationData.min) &&
        Number(qualficationData.max) === Number(commonQualificationData.max)
      );
    }
    return qualficationData.id === commonQualificationData.id;
  }

  private _compareQualificationOption(
    qualficationsData: QualificationData[],
    commonQualificationsData: QualificationData[],
    isRange: boolean
  ) {
    if (qualficationsData.length !== commonQualificationsData.length) {
      return false;
    }
    for (let i = 0; i < qualficationsData.length; i++) {
      if (
        !this._compareQualificationData(
          qualficationsData[i],
          commonQualificationsData[i],
          isRange
        )
      ) {
        return false;
      }
    }
    return true;
  }

  private _updateAttributeQualification(
    countryCode: string,
    qualifications: Qualification[]
  ) {
    this.attributeQualifications.map(
      (attributeQualifications: AttributeQualification) => {
        if (attributeQualifications.countryCode === countryCode) {
          return (attributeQualifications.qualifications = qualifications);
        }
        return attributeQualifications;
      }
    );
  }

  private _isSetMcAttributeQualification() {
    return (
      !this.isEditMode &&
      !this.surveyId &&
      this.allowMultipleCountriesSelection &&
      !this.isTemplateChanged
    );
  }

  async getAlwaysShowQualCodes(surveyDetails: BuyerSurveyResponse) {
    const currentSurveyQualCodes =
      surveyDetails?.qualifications?.map((q) => q.qualification_code) || [];
    const spaParentSurveyQualCodes = await this._getParentSurveyQualCodesIfSPA(
      surveyDetails
    );
    return Array.from(
      new Set([...currentSurveyQualCodes, ...spaParentSurveyQualCodes])
    );
  }

  private async _getParentSurveyQualCodesIfSPA(
    surveyDetails: BuyerSurveyResponse
  ) {
    if (
      surveyDetails?.channel_type === TRAFFIC_CHANNEL.SPECIFIC_AUDIENCE &&
      surveyDetails?.ps_project_id
    ) {
      const parentSurveyQualifications = await this._buyerApiService
        .getQualifications(surveyDetails?.ps_project_id)
        .toPromise();
      const parentSurveyQualCodes = parentSurveyQualifications?.map(
        (qualification) => qualification.qualification_code
      );
      return parentSurveyQualCodes || [];
    }
    return [];
  }

  public isQualificationInUnicorn(qualification: Qualification) {
    return new SurveyUnicorns(this.unicornQuotas).hasQualification(
      qualification.qualification_code
    );
  }

  public isAdvanceQualificationInUnicorn(
    qualifications: AdvanceTargetingQualification[]
  ) {
    return !!qualifications.some((q) =>
      new SurveyUnicorns(this.unicornQuotas).hasQualification(
        q.qualification_code
      )
    );
  }

  private _handleSurveyUnicornsAndDifficultQuotasPayload(): void {
    this._handleSurveyUnicornsPayload();
    this._handleSurveyDifficultQuotasPayload();
  }

  private _handleSurveyUnicornsPayload(): void {
    this._subscriptions.add(
      this._quotaPayloadService
        .getSurveyUnicorns()
        .subscribe((surveyUnicorns) => {
          this.unicornQuotas = surveyUnicorns;
          this._validateUnicornsForAida();
        })
    );
  }

  private _validateUnicornsForAida() {
    if (
      this.surveyPayload?.aida?.enabled &&
      this.unicornQuotas.length &&
      this.surveyDetails?.ps_survey_status === SURVEY_STATUS.DRAFT
    ) {
      this.surveyPayload.aida = {
        enabled: false,
      };
      return;
    }

    if (this.surveyPayload?.aida?.enabled) {
      this._aidaPayloadService.sendAidaSurveyErrorDetails({
        message: notifyMessage.errorMessage.AIDA.REMOVE_UNICORN_QUOTAS,
        showError: !!this.unicornQuotas.length,
      });
    }
  }

  private _handleSurveyDifficultQuotasPayload(): void {
    this._subscriptions.add(
      this._quotaPayloadService
        .getSurveyDifficultQuotas()
        .subscribe((surveyDifficultQuotas) => {
          this.difficultQuotas = surveyDifficultQuotas;
        })
    );
  }

  private _resetSurveyDetail() {
    this._quotaPayloadService.setNestedQuotas([]);
  }

  private _showQuotaEditNotAllowedErrorForAida(
    quotas: SelectedQuota[] = [],
    qualificationHasNestedQuotas = false
  ): boolean {
    if (!this.isLiveSurveyEdit) {
      return false;
    }
    return new AidaSurveyQuotasEditInspector(
      this.surveyDetails.aida!,
      quotas,
      qualificationHasNestedQuotas
    ).notEditable();
  }

  private _handleAidaErrors() {
    const _subscription = this._aidaPayloadService
      .getAidaError()
      .pipe(skip(1))
      .subscribe((message) => {
        this._toastr.error(message);
      });

    this._subscriptions.add(_subscription);
  }

  private _handleAidaEligibiltyCheck() {
    const eligibilitySubscription = this._priceOverrideChanges$
      .pipe(
        debounceTime(100),
        filter(
          () => !this.isLiveSurveyEdit && !!this.surveyPayload.aida?.enabled // run only in draft and when aida is activated
        )
      )
      .subscribe((priceOverride) => {
        const hasPriceOverrideError =
          this._eligibleSuppliersPricingDetails &&
          this._eligibleSuppliersPricingDetails.CPI > priceOverride;

        if (hasPriceOverrideError) {
          this.surveyPayload.aida = { enabled: false };

          // Trigger AIDA activity log when user changes the price override and gets an error
          this._postActivityLog({
            surveyId: this.surveyId,
            eventSource: 'system',
            logMessage: 'AIDA_AUTO_DEACTIVATED',
            metadata: {
              cpi: `${this.surveyDetails?.currency_symbol}${priceOverride}`,
            },
          });
        }
      });
    this._subscriptions.add(eligibilitySubscription);
  }

  private _buyerNestingConfig() {
    if (this._isLoggedInAsOperator) {
      const verifier = new BuyerConfigSettingsVerifier(
        this._auth.allBuyerConfigs
      );

      return {
        isNestingQuotaGroupingEnabled: verifier.verifyPermission(
          'enableNestingQuotaGrouping',
          this.surveyDetails.buyer_company
        ),
        isNestingMultiQuotaEnabled: verifier.verifyPermission(
          'enableNestingMultiQuota',
          this.surveyDetails.buyer_company
        ),
      };
    }

    return {
      isNestingQuotaGroupingEnabled: this.isNestingQuotaGroupingEnabled,
      isNestingMultiQuotaEnabled: this.isNestingMultiQuotaEnabled,
    };
  }
  ngAfterContentChecked(): void {
    this._cdRef.detectChanges();
  }

  ngOnDestroy() {
    this._quotaPayloadService.resetData();
    this._subscriptions.unsubscribe();
    this._cdRef.detach();
  }

  private _postActivityLog(activityLog: ActivityLogDto): void {
    const activityLogSubscription = this._activityLogsService
      .post(activityLog)
      .subscribe();
    this._subscriptions.add(activityLogSubscription);
  }
}
